// frontend/src/pages/BulkAnalysisPage/InteractiveBatchTable.jsx

import React, {
    useState,
    useRef,
    useEffect,
    useCallback,
    useContext,
    useMemo,
} from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import ReactDOM from "react-dom";
import { useTranslation, Trans } from "react-i18next";
import { useQuery } from '@tanstack/react-query';
import { isEqual } from "lodash";
import Papa from "papaparse";
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Alert,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Select,
    MenuItem,
    Checkbox,
    TextField,
    ListItemText,
    CircularProgress,
    AlertTitle,
    InputAdornment,
    Snackbar,
    FormControlLabel,
    ListItemIcon,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    List,
    ListItem,
    ListItemButton,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import VideocamIcon from '@mui/icons-material/Videocam';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HistoryIcon from '@mui/icons-material/History';
import LinkIcon from '@mui/icons-material/Link';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckIcon from "@mui/icons-material/Check";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SettingsIcon from "@mui/icons-material/Settings";
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AddModeratorIcon from "@mui/icons-material/AddModerator";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SnippetModal from "./SnippetModal";
import useUnsavedChangesWarning from "./useUnsavedChangesWarning";
import DownloadProgressDonut from '../../components/DownloadProgressDonut/DownloadProgressDonut';
import {
    validateUrlExtension,
    validateBlobType,
} from "./mediaUtils";
import { useAuth } from "../../contexts/AuthContext";
import { useUnsavedChanges } from "../../contexts/UnsavedChangesContext";
import { UIContext } from "../../contexts/UIContext";
import ScoreDonut from '../VerificationPage/ScoreDonut';
import { getPlatformOptions, getPlatformDisplayName } from '../../components/PlatformUtils/PlatformUtils.jsx';
import PublicIcon from '@mui/icons-material/Public';


const CURRENCY_SYMBOLS = {
    'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'AUD': 'A$', 'CAD': 'C$', 'CHF': 'Fr', 'CNH': '¥'
};

const formatCurrency = (amount, currencyCode, decimals = 3) => {
    const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;
    const numberPart = new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(amount);
    return `${symbol} ${numberPart}`;
};

const CHART_COLORS = {
    base: '#0088FE',
    explanation: '#00C49F',
    regions: '#FFBB28'
};

const getContentTypeIcon = (contentType) => {
    switch (contentType) {
        case 'text': return <DescriptionIcon fontSize="small" />;
        case 'image': return <ImageIcon fontSize="small" />;
        case 'audio': return <AudiotrackIcon fontSize="small" />;
        case 'video': return <VideocamIcon fontSize="small" />;
        default: return <DescriptionIcon fontSize="small" />;
    }
};

const getScoreColor = (score) => {
    if (score >= 90) return '#F44336';
    if (score >= 75) return '#FF9800';
    if (score >= 50) return '#FFC107';
    if (score >= 25) return '#8BC34A';
    if (score > 10) return '#8BC34A';
    return '#4CAF50';
};

const calculateLocalVetraScore = (aiScore, fnScore) => {
    if (aiScore == null || fnScore == null) return null;
    const p_ai = aiScore / 100.0;
    const p_fn = fnScore / 100.0;
    const prob_or = 1 - ((1 - p_ai) * (1 - p_fn));
    const synergy_bonus = Math.pow(p_ai * p_fn, 0.5) * 0.15;
    return Math.floor(Math.min(prob_or + synergy_bonus, 1.0) * 100);
};

// Analyzes the DB results to find the Highest Score to display as the primary donut
const getAggregatedScoreData = (item, taskConfig) => {
    const rawResults = item.tasks_results || {};
    const selectedTasks = item.tasks || [];
    let virtualResults = { ...rawResults };

    // A. Inject Meta Scores if dependencies are met
    Object.entries(taskConfig || {}).forEach(([key, config]) => {
        if (config.is_meta_task) {
            const deps = config.depends_on || [];
            const allDepsSelected = deps.every(d => selectedTasks.includes(d));
            const allDepsCompleted = deps.every(d => virtualResults[d] != null);
            
            if (allDepsSelected && allDepsCompleted) {
                // Dynamically grab the scores of the dependencies listed in the YAML
                const score1 = virtualResults[deps[0]]?.score;
                const score2 = virtualResults[deps[1]]?.score;
                
                virtualResults[key] = { 
                    score: calculateLocalVetraScore(score1, score2), 
                    is_meta: true 
                };
            }
        }
    });

    // B. Filter to ONLY tasks the user selected
    const relevantResults = {};
    Object.entries(virtualResults).forEach(([key, res]) => {
        const isMetaAndUnlocked = taskConfig[key]?.is_meta_task && taskConfig[key].depends_on.every(d => selectedTasks.includes(d));
        if (selectedTasks.includes(key) || isMetaAndUnlocked) {
            relevantResults[key] = res;
        }
    });

    // C. Find Highest Score
    let maxScore = -1;
    let primaryTaskKey = null;

    Object.entries(relevantResults).forEach(([key, res]) => {
        if (res && res.score != null && res.score > maxScore) {
            maxScore = res.score;
            primaryTaskKey = key;
        }
    });

    // D. Extract unified metadata
    let metadata = null;
    if (relevantResults[primaryTaskKey]?.content_metadata) {
        metadata = relevantResults[primaryTaskKey].content_metadata;
    } else {
        const fallback = Object.values(relevantResults).find(r => r?.content_metadata);
        if (fallback) metadata = fallback.content_metadata;
    }

    return {
        primaryTaskKey: primaryTaskKey || (selectedTasks.length > 0 ? selectedTasks[0] : null),
        displayScore: maxScore === -1 ? null : maxScore,
        allResults: relevantResults,
        metadata
    };
};

// This function will build our rich tooltip content.
const getContentTooltipContent = (item, t, metadata) => {
    
    // Check if it's completed AND if we have at least a preview OR metadata.
    // If we have neither, fall back to the simple text.
    const hasRichData = item.contentPreview || metadata;

    if (item.status !== 'completed' || !hasRichData) {
        return t(`selector.contentType.${item.contentType}`);
    }

    const categoryText = metadata?.category 
        ? t(`contentMetadata.inclusion.${metadata.category}`, { defaultValue: metadata.category }) 
        : "";

    // Build the rich JSX for the tooltip.
    return (
        <div style={{ maxWidth: '400px', fontFamily: 'Roboto, sans-serif', fontSize: '0.875rem' }}>
            {/* 1. Thumbnail Preview (Only render if contentPreview exists) */}
            {item.contentPreview && (
                <div style={{ marginBottom: '12px' }}>
                    <h3 style={{
                        margin: '0 0 0.75rem 0',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: 'rgba(255, 255, 255, 0.85)',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                    }}>
                        {t('verificationPage.contentInfoSection.preview')}
                    </h3>
                    
                    {item.contentType === 'text' ? (
                        <div style={{ padding: '1rem', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                            <p style={{
                                fontStyle: 'italic',
                                color: 'rgba(255, 255, 255, 0.8)',
                                lineHeight: 1.5,
                                fontSize: '0.9rem',
                                margin: 0,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}>
                                "{item.contentPreview}{item.contentPreview.length === 150 ? '...' : ''}"
                            </p>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', borderRadius: '4px', overflow: 'hidden', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                            <img
                                src={item.contentPreview}
                                alt="Content preview"
                                style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* 2. Description & Category (Only render if metadata exists) */}
            {metadata?.summary && (
                <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '4px', fontWeight: '600', paddingTop: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        {t('contentMetadata.descriptionLabel')}
                    </div>
                    <div style={{ fontSize: '0.85rem', lineHeight: '1.4', fontStyle: 'italic', color: 'rgba(255,255,255,0.9)' }}>
                        "{metadata.summary}"
                        {categoryText && (
                            <span style={{ color: 'var(--color-primary)', fontWeight: 700, fontStyle: 'normal', marginLeft: '8px' }}>
                                ({categoryText})
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* 3. Safety Flags Alert */}
            {metadata?.safety_flags && metadata.safety_flags.length > 0 && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '12px',
                    padding: '10px',
                    backgroundColor: 'rgba(139, 0, 0, 0.2)',
                    border: '1px solid rgba(139, 0, 0, 0.5)', 
                    borderRadius: '8px', 
                    color: '#F44336', 
                }}>
                    <ReportProblemOutlinedIcon
                        sx={{ fontSize: '1rem', color: '#F44336', marginTop: '2px', flexShrink: 0 }}
                    />
                    <div style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                        <strong style={{ color: '#F44336' }}>
                            {t('metaScores.safetyAlertPrefix')}
                        </strong>
                        {' '}
                        {metadata.safety_flags.map((flagKey, index) => (
                            <span key={index}>
                                {t(`contentMetadata.exclusion.${flagKey}`, { defaultValue: flagKey })}
                                {index < metadata.safety_flags.length - 1 ? ', ' : ''}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};


// Helper to split filename from its extension
const splitFilename = (filename) => {
    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === 0) {
        // No extension or hidden file like .bashrc
        return { basename: filename, extension: "" };
    }
    return {
        basename: filename.substring(0, lastDotIndex),
        extension: filename.substring(lastDotIndex), // includes the '.'
    };
};

// --- Calculate SHA-256 Client-Side ---
const calculateFileHash = async (file) => {
    if (!file) return null;
    try {
        const arrayBuffer = await file.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        return hashHex;
    } catch (e) {
        console.error("Client-side hashing failed", e);
        return null;
    }
};

// --- URL VALIDATION HELPER ---
const isValidHttpUrl = (string) => {
    if (!string) return true; // Empty is valid
    const pattern = new RegExp(
        '^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$', // fragment locator
        'i'
    );
    return !!pattern.test(string);
};

const stringToHash = (str) => {
    let hash = 0;
    if (str.length === 0) return hash.toString(16);
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash |= 0; // Convert to 32bit integer
    }
    // Return a positive hex string
    return Math.abs(hash).toString(16);
};

const getAllowedFileTypes = (contentType) => {
    // This mapping should reflect your config.yaml
    const typeMapping = {
        image: [".png", ".jpg", ".jpeg", ".gif", ".webp"],
        text: [".txt"],
        audio: [".mp3", ".wav", ".ogg", ".aac", ".flac", ".m4a"],
        video: [".mp4", ".webm", ".mov", ".avi", ".mkv"],
    };
    return typeMapping[contentType]?.join(",") || "*/*"; // Fallback to all files
};

const getPlaceholderTooltipText = (item, t, masterTaskOrder, taskConfig) => {
    const tasks = item.tasks || [];
    const metaTaskEntry = Object.entries(taskConfig || {}).find(([k, v]) => v.is_meta_task);
    const metaTaskKey = metaTaskEntry ? metaTaskEntry[0] : 'digital_integrity_score';
    const deps = metaTaskEntry ? metaTaskEntry[1].depends_on : ['ai_detection', 'fake_news_detection'];
    
    const hasAllDeps = deps.length > 0 && deps.every(d => tasks.includes(d));

    let label = "";
    if (hasAllDeps && metaTaskEntry) {
        label = t(`verificationPage.analysisTasks.${metaTaskKey}`, { defaultValue: taskConfig[metaTaskKey]?.display_name || "Vetra Score" });
    } else if (tasks.length === 1) {
        label = t(`verificationPage.analysisTasks.${tasks[0]}`, { defaultValue: taskConfig[tasks[0]]?.display_name || tasks[0] }) + " Score";
    } else {
        label = t("bulkAnalysisPage.defaults.scoreLabel");
    }

    return t("bulkAnalysisPage.table.tooltips.scoreNotAvailable", {
        scoreLabel: label,
    });
};

const constructVerificationUrl = (item, user) => {
    if (!item.exactHash) return "";

    let url = `/verify?id=${item.exactHash}`;

    // --- Append Attribution ---
    if (user && user.public_id) {
        url += `&ref=${user.public_id}`;
    }

    // Append Context Hash if available. This ensures the link opens the 
    // verification page with the specific context used in the analysis.
    if (item.contextHash) {
        url += `&ctx=${item.contextHash}`;
    }

    // 2. Add Tasks
    if (item.tasks && item.tasks.length > 0) {
        item.tasks.forEach(task => {
            url += `&context=${encodeURIComponent(task)}`;
        });
    }

    // 3. Add Options
    // Important: We must cast to string 'true'/'false' explicitly
    url += `&regions=${String(item.includeRegions)}`;
    url += `&explanation=${String(item.includeExplanation)}`;

    return url;
};

// Helper map to get translated status labels
const statusLabelKeys = {
    pending: "bulkAnalysisPage.table.statusLabels.pending",
    ready: "bulkAnalysisPage.table.statusLabels.ready",
    queued: "bulkAnalysisPage.table.statusLabels.queued",
    processing: "bulkAnalysisPage.table.statusLabels.processing",
    completed: "bulkAnalysisPage.table.statusLabels.completed",
    failed: "bulkAnalysisPage.table.statusLabels.failed",
    resolving: "bulkAnalysisPage.table.statusLabels.resolving",
    uploading: "bulkAnalysisPage.table.statusLabels.uploading",
    url_error: "bulkAnalysisPage.table.statusLabels.url_error",
    upload_failed: "bulkAnalysisPage.table.statusLabels.upload_failed",
    processing_url: "bulkAnalysisPage.table.statusLabels.resolving", 
};

const isSocialUrl = (url) => {
    if (!url) return false;
    return /youtube\.com|youtu\.be|tiktok\.com|instagram\.com|twitter\.com|x\.com|facebook\.com/i.test(url);
};

const InteractiveBatchTable = ({
    initialBatch,
    onBack,
    onOpenResetConfirm,
    setNotification,
    onBatchUpdate,
    confirmedBatchDeletions,
    setConfirmedBatchDeletions,
    setIsSnippetModalOpen,
    setIsDeployModalOpen,
    setIsConfigModalOpen,
    isOverwriteConfirmOpen,
    setIsOverwriteConfirmOpen,
    isExporting,
    onActivateLicense,
    triggerDownloadItemId,
    onDownloadTriggered,
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { fetchWithAuth, user } = useAuth();
    const { isDirty, setIsDirty } = useUnsavedChanges();
    const { openDocViewer } = useContext(UIContext); 
    const [validationErrors, setValidationErrors] = useState([]);
    const { setIsPageBlurred } = useContext(UIContext);
    const [batchName, setBatchName] = useState(initialBatch.batch_name);
    const [isEditingName, setIsEditingName] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isNameSaving, setIsNameSaving] = useState(false);
    const [parseError, setParseError] = useState(null);
    const [uploadValidationErrors, setUploadValidationErrors] = useState([]);
    const [isUploadConfirmOpen, setIsUploadConfirmOpen] = useState(false);
    const [fileToProcess, setFileToProcess] = useState(null);
    const tableContainerRef = useRef(null);
    const csvInputRef = useRef(null);
    const [isManualUnsavedDialogOpen, setIsManualUnsavedDialogOpen] =
        useState(false);
    const [editingItemId, setEditingItemId] = useState(null);
    const [editingValue, setEditingValue] = useState("");
    const [isStarting, setIsStarting] = useState(false);
    const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
    const [emailNotification, setEmailNotification] = useState(
        initialBatch.email_notification_enabled || false,
    );
    const [generatingOutputId, setGeneratingOutputId] = useState(null);
    const [snippetModal, setSnippetModal] = useState({
        isOpen: false,
        snippet: "",
    });
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [rowToDeleteId, setRowToDeleteId] = useState(null);
    const [dontAskAgain, setDontAskAgain] = useState(false);
    const [isRetentionDialogOpen, setIsRetentionDialogOpen] = useState(false);
    const [dontAskRetentionAgain, setDontAskRetentionAgain] = useState(false);
    const [retentionDialogCallback, setRetentionDialogCallback] = useState(null);
    const isAutoProcessingRef = useRef(false);
    const [isConfigHovered, setIsConfigHovered] = useState(false);
    const [isUploadButtonHovered, setIsUploadButtonHovered] = useState(false);
    const [historyModal, setHistoryModal] = useState({ isOpen: false, itemIndex: null, history: [] });
    const [showContext, setShowContext] = useState(false);
    const platformOptions = useMemo(() => getPlatformOptions(t), [t]);
    const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
    const [dontShowConsentAgain, setDontShowConsentAgain] = useState(false);
    const [consentWarningType, setConsentWarningType] = useState('none'); // 'note', 'url', 'both'
    const [noteModal, setNoteModal] = useState({ isOpen: false, item: null, value: '' });
    const [progressById, setProgressById] = useState({}); 
    const noteInputRef = useRef(null);
    const [checkingLicenseId] = useState(null);

    // --- User Preferences ---
    const DEFAULT_PREFS = {
        default_content_type: 'image',
        displayed_content_types: ['image', 'text', 'video', 'audio'],
        default_tasks: ['ai_detection', 'fake_news_detection'],
        displayed_tasks: ['ai_detection', 'fake_news_detection'],
        default_regions: true,
        display_regions: true,
        default_explanation: true,
        display_explanation: true,
        default_qr_label: 'VERIFIED'
    };
    const prefs = user?.analysis_preferences ? { ...DEFAULT_PREFS, ...user.analysis_preferences } : DEFAULT_PREFS;

    // --- Pricing State ---
    const [selectedCurrency, setSelectedCurrency] = useState('USD');
    const [isCostExpanded, setIsCostExpanded] = useState(false);

    // Fetch Pricing & Rates (Instantly pulled from cache)
    const { data: billingConfig } = useQuery({
        queryKey: ['billingConfig'],
        queryFn: async () => {
            const [priceRes, rateRes] = await Promise.all([
                fetchWithAuth('/api/config/billing'),
                fetchWithAuth('/api/user/billing?limit=1')
            ]);
            const prices = priceRes.ok ? await priceRes.json() : null;
            const rates = rateRes.ok ? await rateRes.json() : { currency_rates: { USD: 1 } };
            return { prices, rates: rates.currency_rates || { USD: 1 } };
        },
        staleTime: Infinity,
    });
    const pricingConfig = billingConfig?.prices || null;
    const currencyRates = useMemo(() => billingConfig?.rates || { USD: 1 }, [billingConfig]);

    // Fetch Task Config
    const { data: tasksConfigData } = useQuery({
        queryKey: ['tasksConfig'],
        queryFn: async () => {
            const res = await fetch('/api/config/tasks');
            return res.ok ? res.json() : { tasks: {} };
        },
        staleTime: Infinity,
    });
    const taskConfig = useMemo(() => tasksConfigData?.tasks || {}, [tasksConfigData]);
    const masterTaskOrder = useMemo(() => tasksConfigData?.tasks ? Object.keys(tasksConfigData.tasks) : [], [tasksConfigData]);

    const getTaskModeForItem = (item) => {
        const tasks = item.tasks || [];
        const hasAi = tasks.includes('ai_detection');
        const hasFakeNews = tasks.includes('fake_news_detection');
        if (hasAi && hasFakeNews) return 'both';
        if (hasAi) return 'ai';
        if (hasFakeNews) return 'fake_news';
        return 'both';
    };

    const getNotesPlaceholderForItem = (item) => {
        if (!item) return '';
        const taskMode = getTaskModeForItem(item);
        const key = `contentInput.context.userNotesPlaceholders.${item.contentType}.${taskMode}`;
        const placeholder = t(key, { defaultValue: null });
        return placeholder || t('contentInput.context.userNotesPlaceholders.default');
    };

    const getContextWhyTooltipForItem = (item) => {
        if (!item) return '';
        const taskMode = getTaskModeForItem(item);
        const key = `contentInput.context.contextWhyTooltips.${item.contentType}.${taskMode}`;
        const tooltip = t(key, { defaultValue: null });
        return tooltip || t('contentInput.context.contextWhyTooltips.default', {
            defaultValue: "Providing context helps the AI differentiate between content that is technically manipulated versus content that is deceptively manipulated."
        });
    };

    const handleOpenNoteModal = (item) => {
        setNoteModal({ 
            isOpen: true, 
            item: item, 
            value: item.contextUserNotes || '' 
        });
    };

    const handleSaveNote = () => {
        if (noteModal.item) {
            handleRowUpdate(noteModal.item.id, 'contextUserNotes', noteModal.value);
        }
        setNoteModal({ isOpen: false, item: null, value: '' });
    };

    const handleViewVerificationPage = (item) => {
        if (!item.exactHash) return;
        let url = constructVerificationUrl(item, user);

        // Inject Time Bomb for Shield assets to mimic ad behavior
        if (item.creativeOutput === 'shield') {
            url += `&source=shield&click_ts=${Date.now()}`;
        }
        window.open(url, '_blank');
    };

    const [batchItems, setBatchItems] = useState(() => {
        // This function runs only once on initial render. It maps over the
        // items from the database and assigns a stable, unique ID to each one
        // for frontend use (React keys, deletion logic).
        const initialItems = initialBatch.items || [];
        return initialItems.map((item, index) => ({
            ...item,
            id: `initial-item-${initialBatch.batch_id}-${index}`,
        }));
    });

    // --- COST CALCULATION LOGIC ---
    const calculateItemCost = useCallback((item) => {
        // Default structure to prevent undefined errors
        const defaultResult = {
            units: 0,
            fiat: formatCurrency(0, selectedCurrency, 3),
            breakdown: { base: 0, explanation: 0, regions: 0 }
        };

        if (!pricingConfig) return defaultResult;
        
        const prices = pricingConfig.prices;
        const taskCount = (item.tasks || []).length;
        if (taskCount === 0) return defaultResult;

        // 1. Base Cost
        const baseUnitCost = prices.analysis_new[item.contentType] || 0;
        const totalBaseCost = baseUnitCost * taskCount;

        // 2. Add-ons
        const explanationUnitCost = prices.analysis_addons.explanation || 0;
        const totalExplanationCost = item.includeExplanation ? (explanationUnitCost * taskCount) : 0;

        const regionsUnitCost = prices.analysis_addons.regions || 0;
        const supportsRegions = ['image', 'video', 'text'].includes(item.contentType);
        // Note: Audio currently excludes regions (handled in backend billing too)
        const totalRegionsCost = (item.includeRegions && supportsRegions) ? (regionsUnitCost * taskCount) : 0;

        const totalUnits = totalBaseCost + totalExplanationCost + totalRegionsCost;

        // 3. Currency Conversion
        const rate = currencyRates[selectedCurrency] || 1;
        const currencyRatio = pricingConfig.billing_meta.unit_to_currency_ratio || 0.001;
        const totalFiat = totalUnits * currencyRatio * rate;

        return {
            units: totalUnits,
            fiat: formatCurrency(totalFiat, selectedCurrency, 3),
            breakdown: {
                base: totalBaseCost,
                explanation: totalExplanationCost,
                regions: totalRegionsCost
            }
        };
    }, [pricingConfig, selectedCurrency, currencyRates]);

    // --- TRIAL & USAGE GATEKEEPER CALCS ---
    const isMetered = !!user?.stripe_subscription_item_id;
    const isCancellationPending = !!user?.is_cancellation_pending;

    const parseDateSafe = (dateString, fallbackDaysAdd = 0) => {
        if (!dateString) {
            const d = new Date();
            d.setDate(d.getDate() + fallbackDaysAdd);
            return d;
        }
        let parsed = new Date(dateString);
        if (!isNaN(parsed.getTime())) return parsed;
        if (typeof dateString === 'string') {
            const safeString = dateString.replace(/^(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2}:\d{2})/, '$1T$2');
            parsed = new Date(safeString);
            if (!isNaN(parsed.getTime())) return parsed;
        }
        return new Date();
    };

    const trialExpires = parseDateSafe(user?.trial_expires_at, 14);
    const daysRemaining = Math.max(0, Math.ceil((trialExpires - new Date()) / (1000 * 60 * 60 * 24)));
    const totalGrantedUnits = Number(user?.compute_units_balance || 5000);
    const trialSpent = Number(user?.trial_spent || 0);
    const unitsRemaining = Math.max(0, totalGrantedUnits - trialSpent);

    const hasEverActivatedBilling = !!user?.billing_activated_at;
    const isTrialActive = !user?.is_guest && !isMetered && !hasEverActivatedBilling && daysRemaining > 0 && unitsRemaining > 0;
    const isSubscriptionInactive = !user?.is_guest && !isMetered && hasEverActivatedBilling;
    
    const showBanner = isTrialActive || isCancellationPending || isSubscriptionInactive || (!user?.is_guest && !isMetered && !hasEverActivatedBilling && (daysRemaining <= 0 || unitsRemaining <= 0));
    
    const trialExhausted = !hasEverActivatedBilling && unitsRemaining <= 0;
    const trialExpired = !hasEverActivatedBilling && daysRemaining <= 0;

    const isTrial = !user?.is_guest && !isMetered;

    // --- BATCH TOTAL COST ---
    const batchCostData = useMemo(() => {
        if (!pricingConfig || masterTaskOrder.length === 0) return null;
        
        let grandTotalUnits = 0;
        let totalBase = 0;
        let totalExp = 0;
        let totalReg = 0;
        let totalTaskCount = 0; 
        
        // Dynamic counters for tasks
        const taskCounts = {};
        masterTaskOrder.forEach(k => taskCounts[k] = 0);

        batchItems.forEach(item => {
            const cost = calculateItemCost(item);
            grandTotalUnits += cost.units;
            totalBase += cost.breakdown.base;
            totalExp += cost.breakdown.explanation;
            totalReg += cost.breakdown.regions;

            // Count specific tasks dynamically
            if (item.tasks) {
                totalTaskCount += item.tasks.length;
                item.tasks.forEach(t => {
                    if (taskCounts[t] !== undefined) taskCounts[t]++;
                });
            }
        });

        // Build the dynamic label with counts (e.g., "2 AI & 2 Fake News")
        const labelParts = [];
        masterTaskOrder.forEach(key => {
            if (taskCounts[key] > 0) {
                const shortName = t(`selector.analysisTasks.short.${key}`, { defaultValue: taskConfig[key]?.display_name || key });
                labelParts.push(`${taskCounts[key]} ${shortName}`);
            }
        });
        const taskLabels = labelParts.join(' & ');

        const baseAnalysisName = t('selector.costEstimator.baseAnalysisLabel', { 
            count: totalTaskCount, 
            tasks: taskLabels 
        });

        // Chart Data
        const rate = currencyRates[selectedCurrency] || 1;
        const currencyRatio = pricingConfig.billing_meta.unit_to_currency_ratio || 0.001;
        
        const getFiatStr = (units) => formatCurrency(units * currencyRatio * rate, selectedCurrency, 2);

        const chartData = [
            { name: baseAnalysisName, value: totalBase, color: CHART_COLORS.base, fiat: getFiatStr(totalBase) },
        ];
        if (totalExp > 0) chartData.push({ name: t('selector.costEstimator.explanation'), value: totalExp, color: CHART_COLORS.explanation, fiat: getFiatStr(totalExp) });
        if (totalReg > 0) chartData.push({ name: t('selector.costEstimator.regions'), value: totalReg, color: CHART_COLORS.regions, fiat: getFiatStr(totalReg) });

        const totalFiatDisplay = getFiatStr(grandTotalUnits);

        const exchangeRateText = t('selector.costEstimator.exchangeRateMsg', {
            cost: formatCurrency(1.00 * rate, selectedCurrency, 2)
        });

        // Calculate approx savings
        const cachedVid = pricingConfig.prices.analysis_cached.video;
        const newVid = pricingConfig.prices.analysis_new.video;
        const percentCheaper = Math.round((1 - (cachedVid / newVid)) * 100);

        return {
            totalUnits: grandTotalUnits,
            totalFiatDisplay,
            chartData,
            exchangeRateText,
            percentCheaper
        };

    }, [batchItems, calculateItemCost, pricingConfig, selectedCurrency, currencyRates, t, masterTaskOrder, taskConfig]);
    
    const readyItemsCount = batchItems.filter(
        (item) => item.status === "ready",
    ).length;
    const completedItemsCount = batchItems.filter(
        (item) => item.status === "completed",
    ).length;
    const batchMode = initialBatch.batch_mode || "manual";
    const isDcmReadOnly = batchMode === "dcm";
    const estimatedCostUnits = batchCostData?.totalUnits || 0;
    const exceedsCredit = isTrial && (estimatedCostUnits > unitsRemaining);

    // --- NOW DECLARE THE CONSTANTS THAT USE batchItems ---
    const isProcessing =
        initialBatch.status === "queued" ||
        initialBatch.status === "processing";
    const progress = initialBatch.progress || {
        completed: 0,
        failed: 0,
        total: batchItems.length,
    };
    const finishedCount = progress.completed + progress.failed;

    // --- The rest of your state declarations can follow ---
    const [isRenaming, setIsRenaming] = useState(null);

    // This is a ref to hold the timer ID for debouncing
    const debounceTimeoutRef = useRef(null);
    const availableTasks = useMemo(() => {
        if (!masterTaskOrder || masterTaskOrder.length === 0) return [];
        return masterTaskOrder
            .filter(key => !taskConfig[key]?.is_meta_task)
            .filter(key => prefs.displayed_tasks.includes(key))
            .map(key => ({
                value: key,
                label: t(`selector.analysisTasks.${key}`, { defaultValue: taskConfig[key]?.display_name || key })
            }));
    }, [masterTaskOrder, taskConfig, prefs.displayed_tasks, t]);

    const taskShortLabels = useMemo(() => {
        const labels = {};
        masterTaskOrder.forEach(key => {
            labels[key] = t(`selector.analysisTasks.short.${key}`, { 
                defaultValue: taskConfig[key]?.display_name || key 
            });
        });
        return labels;
    }, [masterTaskOrder, taskConfig, t]);

    const taskKeyMapping = useMemo(() => {
        const mapping = {};
        masterTaskOrder.forEach(key => {
            const shortLabel = t(`selector.analysisTasks.short.${key}`, { defaultValue: taskConfig[key]?.display_name || key });
            const fullLabel = t(`selector.analysisTasks.${key}`, { defaultValue: taskConfig[key]?.display_name || key });
            mapping[shortLabel] = key;
            mapping[fullLabel] = key;
        });
        return mapping;
    }, [masterTaskOrder, taskConfig, t]);

    // Old Constrain: This will be true if the mode is 'manual' OR if it's 'dcm' and contains at least one video.
    // const showQrLabelColumn = batchMode === 'manual' || (batchMode === 'dcm' && batchItems.some(item => item.contentType === 'video'));
    // New Constrain: QR Label column is now hidden in DCM mode since we force Shield/VAST output
    const showQrLabelColumn = batchMode === 'manual' && batchItems.some(item => item.creativeOutput === 'qr');

    const findTasksInString = (inputString) => {
        if (!inputString) return [];
        const lowercasedInput = inputString.toLowerCase();
        const foundTasks = new Set();
        for (const [label, systemKey] of Object.entries(taskKeyMapping)) {
            if (lowercasedInput.includes(label.toLowerCase()))
                foundTasks.add(systemKey);
            const normalizedLabel = label.toLowerCase().replace(/[\s_]+/g, "");
            if (
                lowercasedInput.replace(/[\s_]+/g, "").includes(normalizedLabel)
            )
                foundTasks.add(systemKey);
        }
        return Array.from(foundTasks);
    };

    // --- Render a Cost Tooltip for a specific row ---
    const renderRowCostTooltip = (item) => {
        const cost = calculateItemCost(item);
        
        // Fix: Renamed argument from 't' to 'taskKey' to avoid shadowing the translation function 't'
        const taskLabels = (item.tasks || []).map(taskKey => {
            if (taskKey === 'ai_detection') return t('selector.analysisTasks.short.ai');
            if (taskKey === 'fake_news_detection') return t('selector.analysisTasks.short.fakeNews');
            return taskKey;
        }).join(' & ');

        const baseLabel = t('selector.costEstimator.baseAnalysisLabel', { 
            count: (item.tasks || []).length, 
            tasks: taskLabels 
        });

        return (
            <Box sx={{ p: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 0.5 }}>
                    {t('selector.costEstimator.title')}
                </Typography>
                {cost.breakdown.base > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', mb: 0.5 }}>
                        <span style={{ color: CHART_COLORS.base, marginRight: '8px' }}>{baseLabel}:</span>
                        <span>{cost.breakdown.base}</span>
                    </Box>
                )}
                {cost.breakdown.explanation > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', mb: 0.5 }}>
                        <span style={{ color: CHART_COLORS.explanation }}>{t('selector.costEstimator.explanation')}:</span>
                        <span>{cost.breakdown.explanation}</span>
                    </Box>
                )}
                {cost.breakdown.regions > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', mb: 0.5 }}>
                        <span style={{ color: CHART_COLORS.regions }}>{t('selector.costEstimator.regions')}:</span>
                        <span>{cost.breakdown.regions}</span>
                    </Box>
                )}
                <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', mt: 1, pt: 0.5, display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                    <span>{t('selector.costEstimator.total')}:</span>
                    <span>{cost.units} {t('selector.costEstimator.units')} ({cost.fiat})</span>
                </Box>
            </Box>
        );
    };

    // --- Fetch History Logic ---
    const fetchHistoryForItem = useCallback(async (item, index) => {
        if (!item.exactHash) return;

        // Mark as loading history locally to show spinner without affecting main status
        setBatchItems(prev => prev.map((i, idx) => idx === index ? { ...i, isHistoryLoading: true } : i));

        try {
            // Note: We use the existing endpoint but pass exact_hash directly.
            // (Requires backend to accept 'exact_hash' param, or we send dummy content + type)
            // Assuming we update backend to accept exact_hash for efficiency.
            const formData = new FormData();
            formData.append('type', item.contentType);
            formData.append('exact_hash', item.exactHash); 
            // Fallback: If backend strictly needs content, we can't fetch history easily without re-downloading.
            // For now, we assume the backend handles exact_hash lookup.
            
            const response = await fetchWithAuth('/api/analysis/context-history', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                setBatchItems(prev => prev.map((i, idx) => 
                    idx === index ? { ...i, contextHistory: data.history || [], isHistoryLoading: false } : i
                ));
            } else {
                 setBatchItems(prev => prev.map((i, idx) => idx === index ? { ...i, isHistoryLoading: false } : i));
            }
        } catch (err) {
            console.error("History fetch failed", err);
            setBatchItems(prev => prev.map((i, idx) => idx === index ? { ...i, isHistoryLoading: false } : i));
        }
    }, [fetchWithAuth]);

    // Effect to focus the input when modal opens
    useEffect(() => {
        if (noteModal.isOpen) {
            // Small timeout to allow the modal animation to finish/render
            const timer = setTimeout(() => {
                if (noteInputRef.current) {
                    noteInputRef.current.focus();
                    // Optional: Move cursor to end of text
                    const length = noteInputRef.current.value.length;
                    noteInputRef.current.setSelectionRange(length, length);
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [noteModal.isOpen]);

    // --- Effect to trigger history fetch when item becomes Ready/Completed ---
    useEffect(() => {
        batchItems.forEach((item, index) => {
            // If item is stable (ready/completed), has a hash, but we haven't fetched history yet...
            if ((item.status === 'ready' || item.status === 'completed') && item.exactHash && item.contextHistory === undefined && !item.isHistoryLoading) {
                fetchHistoryForItem(item, index);
            }
        });
    }, [batchItems, fetchHistoryForItem]);

    const handleOpenHistory = (item, index) => {
        setHistoryModal({ isOpen: true, itemIndex: index, history: item.contextHistory || [] });
    };

    const handleApplyHistory = (historyItem) => {
        const { itemIndex } = historyModal;
        if (itemIndex !== null) {
            const itemId = batchItems[itemIndex].id;
            handleRowUpdate(itemId, 'contextSourceUrl', historyItem.source_url || '');
            handleRowUpdate(itemId, 'contextPlatform', (historyItem.platform || '').toLowerCase());
            handleRowUpdate(itemId, 'contextUserNotes', historyItem.user_notes || '');
        }
        setHistoryModal({ isOpen: false, itemIndex: null, history: [] });
    };

    // This effect now ONLY reports the dirty status to the context.
    useEffect(() => {
        const cleanInitialItems = (initialBatch.items || []).map((item) => ({
            sourcePath: item.sourcePath,
            contentType: item.contentType,
            tasks: item.tasks.slice().sort(),
            includeRegions: item.includeRegions,
            includeExplanation: item.includeExplanation,
            creativeOutput: item.creativeOutput,
            // --- Include new fields in dirty check ---
            contextSourceUrl: item.contextSourceUrl,
            contextPlatform: item.contextPlatform,
            contextUserNotes: item.contextUserNotes,
            status: item.status,
        }));
        const cleanCurrentItems = batchItems.map((item) => ({
            sourcePath: item.sourcePath,
            contentType: item.contentType,
            tasks: item.tasks.slice().sort(),
            includeRegions: item.includeRegions,
            includeExplanation: item.includeExplanation,
            creativeOutput: item.creativeOutput,
            // --- Include new fields in dirty check ---
            contextSourceUrl: item.contextSourceUrl,
            contextPlatform: item.contextPlatform,
            contextUserNotes: item.contextUserNotes,
            status: item.status,
        }));

        const dirty = !isEqual(cleanInitialItems, cleanCurrentItems);
        const notificationDirty =
            emailNotification !==
            (initialBatch.email_notification_enabled || false);
        setIsDirty(dirty || notificationDirty);

        return () => {
            setIsDirty(false);
        };
    }, [
        batchItems,
        emailNotification,
        initialBatch.items,
        initialBatch.email_notification_enabled,
        setIsDirty,
    ]);

    // Add an effect to link the manual dialog state to the global blur context
    useEffect(() => {
        const shouldBeBlurred =
            isManualUnsavedDialogOpen ||
            isUploadConfirmOpen ||
            isDeleteConfirmOpen ||
            isRetentionDialogOpen ||
            isConsentModalOpen ||
            noteModal.isOpen ||
            historyModal.isOpen;;
        setIsPageBlurred(shouldBeBlurred);

        // Cleanup function to remove blur if component unmounts with dialog open
        return () => setIsPageBlurred(false);
    }, [
        isManualUnsavedDialogOpen,
        isUploadConfirmOpen,
        isDeleteConfirmOpen,
        isRetentionDialogOpen,
        isConsentModalOpen,
        noteModal.isOpen,
        historyModal.isOpen,
        setIsPageBlurred,
    ]);

    // Add an effect to link the snippet modal's state to the parent
    useEffect(() => {
        setIsSnippetModalOpen(snippetModal.isOpen);

        // Cleanup function to tell the parent it's closed when the component unmounts
        return () => setIsSnippetModalOpen(false);
    }, [snippetModal.isOpen, setIsSnippetModalOpen]);

    // This hook still correctly handles closing the tab/browser
    useUnsavedChangesWarning(isDirty);

    // The "Back to Batches" button handler now uses the context's isDirty flag
    const handleManualBackClick = () => {
        if (isDirty) {
            setIsManualUnsavedDialogOpen(true);
        } else {
            onBack();
        }
    };

    const handleManualLeavePage = () => {
        setIsManualUnsavedDialogOpen(false);
        onBack();
    };

    const handleCopyLink = (fullUrl) => {
        if (!fullUrl) return;
        navigator.clipboard
            .writeText(fullUrl)
            .then(() => {
                setNotification({
                    open: true,
                    message: t("bulkAnalysisPage.notifications.copySuccess"), // You will add this translation
                    severity: "success",
                });
            })
            .catch((err) => {
                console.error("Failed to copy link: ", err);
                setNotification({
                    open: true,
                    message: t("bulkAnalysisPage.notifications.copyError"), // You will add this
                    severity: "error",
                });
            });
    };

    const getPlaceholder = (contentType) => {
        // This will produce a string like "https://... or upload Image file"
        return t("bulkAnalysisPage.table.placeholders.combined", {
            contentType: t(`selector.contentType.${contentType}`),
        });
    };

    const autosaveBatchName = useCallback(
        async (newName) => {
            setIsNameSaving(true);
            try {
                const response = await fetchWithAuth(
                    `/api/bulk/batches/${initialBatch.batch_id}`,
                    {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ batch_name: newName }), // Only send the name
                    },
                );

                // Handle the specific 409 conflict error first and exit the function.
                if (response.status === 409) {
                    const errorData = await response.json();
                    // Use the translated string with the batchName from the backend
                    const message = t(
                        "bulkAnalysisPage.dialogs.create.errorDuplicate",
                        {
                            batchName:
                                errorData.errorDetails?.batchName || newName,
                        },
                    );
                    setNotification({
                        open: true,
                        message: message,
                        severity: "error",
                    });
                    setBatchName(initialBatch.batch_name);
                    return;
                }

                // Handle any other non-successful responses.
                if (!response.ok) {
                    throw new Error(
                        t("bulkAnalysisPage.notifications.nameUpdateFailed"),
                    );
                }

                // If we reach this point, the update was successful.
                setNotification({
                    open: true,
                    message: t(
                        "bulkAnalysisPage.notifications.nameUpdateSuccess",
                    ),
                    severity: "success",
                });
            } catch (error) {
                console.error(error);
                setNotification({
                    open: true,
                    message: t("bulkAnalysisPage.notifications.errorPrefix", { message: error.message }),
                    severity: "error",
                });
                setBatchName(initialBatch.batch_name); // Revert on any other error
            } finally {
                setIsNameSaving(false); // Stop the spinner
            }
        },
        [
            fetchWithAuth,
            initialBatch.batch_id,
            initialBatch.batch_name,
            setNotification,
            t,
        ],
    ); // Add dependencies

    const handleNameChange = (e) => {
        setBatchName(e.target.value.toLowerCase());
    };

    // CREATE A DEDICATED HANDLER FOR CONFIRMING THE NAME EDIT
    const handleNameEditConfirm = useCallback(() => {
        setIsEditingName(false);
        const trimmedName = batchName.trim();
        setBatchName(trimmedName); // Update the state to reflect the trimmed value visually

        // Save logic now lives here exclusively, using the trimmed name.
        if (trimmedName && trimmedName !== initialBatch.batch_name) {
            autosaveBatchName(trimmedName);
        }
    }, [batchName, initialBatch.batch_name, autosaveBatchName]);

    // Cleanup the timeout on component unmount
    useEffect(() => {
        // Copy ref to a local variable inside the effect to satisfy strict mode cleanup
        const currentTimeout = debounceTimeoutRef.current;
        return () => {
            if (currentTimeout) {
                clearTimeout(currentTimeout);
            }
        };
    }, []);

    // This effect synchronizes the internal state whenever the initialBatch prop from the parent changes.
    // This is crucial for reflecting the correct state after a save operation.
    useEffect(() => {
        const newItems = (initialBatch.items || []).map((item, index) => ({
            ...item,
            id: `initial-item-${initialBatch.batch_id}-${index}`, // Re-assign stable IDs
        }));
        setBatchItems(newItems);
        setBatchName(initialBatch.batch_name);
        setEmailNotification(initialBatch.email_notification_enabled || false);
    }, [initialBatch]); // The dependency array ensures this runs *only* when the initialBatch prop changes.

    // Add an effect to link the delete dialog state to the global blur context
    useEffect(() => {
        const shouldBeBlurred =
            isManualUnsavedDialogOpen ||
            isUploadConfirmOpen ||
            isDeleteConfirmOpen;
        setIsPageBlurred(shouldBeBlurred);

        return () => setIsPageBlurred(false);
    }, [
        isManualUnsavedDialogOpen,
        isUploadConfirmOpen,
        isDeleteConfirmOpen,
        setIsPageBlurred,
    ]);

    const handleDownloadStandardTemplate = () => {
        // Dynamically get labels for the first two active standard tasks
        const activeStandardTasks = masterTaskOrder.filter(k => !taskConfig[k]?.is_meta_task);
        const task1Key = activeStandardTasks[0] || 'ai_detection';
        const task2Key = activeStandardTasks[1] || 'fake_news_detection';

        const task1Label = taskShortLabels[task1Key] || task1Key;
        const task2Label = taskShortLabels[task2Key] || task2Key;

        // Use an array of rows and then join them with a newline character.
        // This is much cleaner and easier to maintain than a single long string.
        const csvRows = [
            // 1. The Header Row (unchanged)
            "Content Type,Analysis Task(s),Explain,Regions,Content Input,Creative Output",

            // 2. Example 1: The most common case - Image from a URL with full analysis and Shield output.
            `image,"${task1Label},${task2Label}",true,false,https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/image_innercircle.png,shield`,

            // 3. Example 2: Image from a URL with full analysis and QR output.
            `image,${task1Label},true,true,https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/image_starbucks.png,qr`,

            // 4. Example 3: A video from a URL with AI analysis and Shield output.
            `video,"${task1Label},${task2Label}",true,true,https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/video_dove.mp4,shield`,

            // 5. Example 4: Text analysis. Note that Creative Output is left blank as it's not applicable.
            `text,${task1Label},true,true,https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/text_1.txt,`,

            // 6. Example 5: Text analysis. Note that Creative Output is left blank as it's not applicable.
            `text,${task2Label},true,false,https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/text_2.txt,`,

            // 7. Example 6: Audio analysis from a URL. CRITICAL: Note that 'Regions' is set to 'false' as it's not supported for audio.
            `audio,${task1Label},true,false,https://vetra-ai-public-content.s3.eu-central-1.amazonaws.com/audio_1.mp3,`,
        ];

        const csvContent = csvRows.join("\n");

        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "vetra_ai_bulk_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadDcmTemplate = () => {
        // Define the required header for the CSV file.
        const csvHeader =
            "Placement ID,Placement Name,Iframes/JavaScript Tag,VAST 4.0 Pre-fetch Tag";

        // --- NEW: Define realistic, multiline template tags ---
        // Note the use of backticks (`) for multiline strings and embedded quotes.
        const tag1 = `"<ins class='dcmads' style='display:inline-block;width:728px;height:90px'
    data-dcm-placement='N000.TEMPLATE/B0000.001'
    data-dcm-rendering-mode='iframe'
    data-dcm-https-only
    data-dcm-gdpr-applies='gdpr=\${GDPR}'
    data-dcm-gdpr-consent='gdpr_consent=\${GDPR_CONSENT_755}'
    data-dcm-addtl-consent='addtl_consent=\${ADDTL_CONSENT}'>
    <!-- AUTH_AI_TEMPLATE_PLACEHOLDER_728x90 -->
    <script src='https://www.googletagservices.com/dcm/dcmads.js'></script>
</ins>"`;

        const tag2 = `"<ins class='dcmads' style='display:inline-block;width:300px;height:250px'
    data-dcm-placement='N000.TEMPLATE/B0000.002'
    data-dcm-rendering-mode='iframe'
    data-dcm-https-only
    data-dcm-gdpr-applies='gdpr=\${GDPR}'
    data-dcm-gdpr-consent='gdpr_consent=\${GDPR_CONSENT_755}'
    data-dcm-addtl-consent='addtl_consent=\${ADDTL_CONSENT}'>
    <!-- AUTH_AI_TEMPLATE_PLACEHOLDER_300x250 -->
    <script src='https://www.googletagservices.com/dcm/dcmads.js'></script>
</ins>"`;

        // --- Define the VAST Tag for Video ---
        // eslint-disable-next-line
        const vastTag = "https://ad.doubleclick.net/ddm/pfadx/N339.3289898TEST/B34220385.429164593;sz=0x0;ord=[timestamp];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;dc_tdv=1;dcmt=text/xml;dc_sdk_apis=[APIFRAMEWORKS];dc_omid_p=[OMIDPARTNER];dc_vast=4;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};dc_mpos=[BREAKPOSITION];ltd="; // VETRA_AI_IGNORE_LINE

        // Create rows.
        // Note: Row 1 & 2 end with a comma to leave the VAST column empty.
        const csvRow1 = `12345678,Example Leaderboard Ad,${tag1},`;
        const csvRow2 = `98765432,Example MPU Ad,${tag2},`;

        // Note: Row 3 leaves the JS Tag column empty (double comma) and fills the VAST column.
        const csvRow3 = `11223344,Example Video Ad,,${vastTag}`;

        // Combine the header and rows into the final CSV content.
        const csvContent = [csvHeader, csvRow1, csvRow2, csvRow3].join("\n");

        // The rest of the download logic...
        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "vetra_ai_dcm_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const parseAndSetItems = (file, mode) => {
        if (!file) return;

        setParseError(null);
        setUploadValidationErrors([]);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                // --- CHANGE: Make this function async
                try {
                    const actualHeaders = results.meta.fields || [];

                    if (batchMode === "dcm") {
                        // Look for the correct headers
                        const tagHeaderName = actualHeaders.includes(
                            "Iframes/JavaScript Tag",
                        )
                            ? "Iframes/JavaScript Tag"
                            : "Internal Redirect Tag"; // Fallback to the older name

                        const dcmRequiredHeaders = [
                            "Placement ID",
                            tagHeaderName,
                        ];
                        if (
                            !dcmRequiredHeaders.every((h) =>
                                actualHeaders.includes(h),
                            )
                        ) {
                            setParseError(
                                t("bulkAnalysisPage.errors.dcmInvalidHeaders", {
                                    headers:
                                        "Placement ID and 'Iframes/JavaScript Tag' or 'Internal Redirect Tag'",
                                }),
                            );
                            return;
                        }

                        // --- VALIDATION FOR DUPLICATE Placement IDs (Your existing logic is good) ---
                        const placementIdMap = new Map();
                        results.data.forEach((row, index) => {
                            const placementId = row["Placement ID"];
                            if (!placementId) return;

                            const rowNum = index + 2;
                            if (!placementIdMap.has(placementId)) {
                                placementIdMap.set(placementId, []);
                            }
                            placementIdMap.get(placementId).push(rowNum);
                        });

                        const duplicates = [];
                        for (const [id, rows] of placementIdMap.entries()) {
                            if (rows.length > 1) {
                                duplicates.push({ id, rows });
                            }
                        }

                        if (duplicates.length > 0) {
                            const errorDetails = (
                                <>
                                    <AlertTitle>
                                        {t(
                                            "bulkAnalysisPage.errors.dcmDuplicateIdTitle",
                                        )}
                                    </AlertTitle>
                                    {t(
                                        "bulkAnalysisPage.errors.dcmDuplicateIdBody",
                                    )}
                                    <ul
                                        style={{
                                            marginTop: "10px",
                                            marginBottom: "0",
                                            paddingLeft: "20px",
                                        }}
                                    >
                                        {duplicates.map((dup) => (
                                            <li key={dup.id}>
                                                <strong>{dup.id}:</strong>{" "}
                                                {t(
                                                    "bulkAnalysisPage.errors.dcmDuplicateIdRowInfo",
                                                )}{" "}
                                                {dup.rows.join(", ")}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            );
                            setParseError(errorDetails);
                            return;
                        }

                        // --- PARALLEL PROCESSING WORKFLOW ---

                        // A. Create initial placeholder items with a 'resolving' status
                        const initialItems = results.data
                            .map((row, index) => {
                                const adTag =
                                    row["Iframes/JavaScript Tag"] ||
                                    row["Internal Redirect Tag"];
                                const vastTag =
                                    row["VAST 4.0 Pre-fetch Tag"] ||
                                    row["VAST 3.0 Pre-fetch Tag"] ||
                                    row["VAST 2.0 Pre-fetch Tag"];
                                const placementId = row["Placement ID"];

                                if (!placementId) return null; // Skip rows without a placement ID

                                if (adTag) {
                                    // This is the existing logic for Display Ads
                                    return {
                                        id: `dcm-item-${index}-${Date.now()}`,
                                        status: "resolving", // Start in resolving state
                                        contentType: "image",
                                        sourcePath: "",
                                        originalSourceUrl: adTag,
                                        originalAdTag: adTag,
                                        platformPlacementId: placementId,
                                        tasks: [
                                            "ai_detection",
                                            "fake_news_detection",
                                        ],
                                        includeRegions: true,
                                        includeExplanation: true,
                                        creativeOutput: "shield",
                                        // DCM does not support manual context
                                        contextSourceUrl: "",
                                        contextPlatform: "",
                                        contextUserNotes: "",
                                    };
                                } else if (vastTag) {
                                    // --- THIS IS THE NEW LOGIC FOR VIDEO ADS ---
                                    return {
                                        id: `dcm-item-${index}-${Date.now()}`,
                                        status: "resolving", // Start in resolving state
                                        contentType: "video", // Set the content type to video
                                        sourcePath: vastTag, // The source is the VAST URL
                                        originalSourceUrl: vastTag, // Also store for display
                                        originalAdTag: null, // No JS tag for this one
                                        platformPlacementId: placementId,
                                        tasks: [
                                            "ai_detection",
                                            "fake_news_detection",
                                        ],
                                        includeRegions: true, // Regions for video are supported
                                        includeExplanation: true,
                                        creativeOutput: "shield",
                                        contextSourceUrl: "",
                                        contextPlatform: "",
                                        contextUserNotes: "",
                                    };
                                }

                                return null; // Ignore rows that are neither display nor video
                            })
                            .filter(Boolean);

                        // B. Immediately update the UI to show the "resolving" state
                        setBatchItems(
                            mode === "overwrite"
                                ? initialItems
                                : (prev) => [...prev, ...initialItems],
                        );
                        setNotification({
                            open: true,
                            message: t("bulkAnalysisPage.notifications.importingResolving", { count: initialItems.length }),
                            severity: "info",
                        });

                        // 3. Create an array of promises for all API calls
                        const resolutionPromises = initialItems.map(
                            async (item) => {
                                try {
                                    // --- THIS IS THE NEW DISPATCHER LOGIC ---
                                    if (
                                        item.contentType === "image" &&
                                        item.originalAdTag
                                    ) {
                                        // If it's an image with a JS tag, call the tag renderer
                                        const response = await fetchWithAuth(
                                            "/api/bulk/render-dcm-tag",
                                            {
                                                method: "POST",
                                                headers: {
                                                    "Content-Type":
                                                        "application/json",
                                                },
                                                body: JSON.stringify({
                                                    ad_tag: item.originalAdTag,
                                                }),
                                            },
                                        );
                                        const data = await response.json();
                                        if (!response.ok)
                                            throw new Error(data.error || t("bulkAnalysisPage.errors.renderImageTag"));
                                        return {
                                            id: item.id,
                                            success: true,
                                            url: data.imageUrl,
                                        };
                                    } else if (
                                        item.contentType === "video" &&
                                        item.sourcePath
                                    ) {
                                        // If it's a video with a VAST URL in sourcePath, call the VAST resolver
                                        const response = await fetchWithAuth(
                                            "/api/video/resolve-vast",
                                            {
                                                method: "POST",
                                                headers: {
                                                    "Content-Type":
                                                        "application/json",
                                                },
                                                body: JSON.stringify({
                                                    vast_url: item.sourcePath,
                                                }),
                                            },
                                        );
                                        const data = await response.json();
                                        if (!response.ok)
                                            throw new Error(data.error || t("bulkAnalysisPage.errors.resolveVast"));
                                        return {
                                            id: item.id,
                                            success: true,
                                            url: data.videoUrl,
                                        };
                                    } else {
                                        // If it's an item that doesn't need resolving (e.g., manual entry), pass it through
                                        return {
                                            id: item.id,
                                            success: true,
                                            url: item.sourcePath,
                                        };
                                    }
                                } catch (err) {
                                    console.error(
                                        `Failed to resolve tag for item ${item.id}:`,
                                        err,
                                    );
                                    return {
                                        id: item.id,
                                        success: false,
                                        error: err.message || t("bulkAnalysisPage.errors.resolveInput"),
                                    };
                                }
                            },
                        );

                        // D. Wait for ALL promises to complete (either success or failure)
                        const allResults =
                            await Promise.all(resolutionPromises);

                        // E. Update the batch state in one go after all resolutions are done
                        setBatchItems((currentItems) => {
                            const updatedItemsMap = new Map(
                                currentItems.map((item) => [item.id, item]),
                            );

                            allResults.forEach((result) => {
                                const item = updatedItemsMap.get(result.id);
                                if (item) {
                                    if (result.success) {
                                        item.status = "pending"; // Now ready for the auto-processor to download
                                        item.sourcePath = result.url; // The real URL for processing
                                        // item.originalSourceUrl = result.url; // The URL for display
                                    } else {
                                        item.status = "url error";
                                        item.errorMessage = result.error;
                                    }
                                }
                            });
                            return Array.from(updatedItemsMap.values());
                        });
                    } else {
                        // Manual mode
                        const requiredHeaders = [
                            "Content Type",
                            "Analysis Task(s)",
                            "Regions",
                            "Explain",
                            "Content Input",
                            "Creative Output",
                        ];
                        if (
                            !requiredHeaders.every((h) =>
                                actualHeaders.includes(h),
                            )
                        ) {
                            setParseError(
                                t(
                                    "bulkAnalysisPage.table.errors.missingHeadersError",
                                    { headers: requiredHeaders.join(", ") },
                                ),
                            );
                            return;
                        }

                        // Your existing validation logic for manual template...
                        // This part is unchanged.
                        const newItems = results.data
                            .filter(
                                (row) =>
                                    !Object.values(row).every(
                                        (value) =>
                                            value === null ||
                                            String(value).trim() === "",
                                    ),
                            )
                            .map((row, index) => ({
                                id: `csv-item-${index}-${Date.now()}`,
                                status: "pending",
                                contentType: (row["Content Type"] || prefs.default_content_type)
                                    .trim()
                                    .toLowerCase(),
                                sourcePath: row["Content Input"] || "",
                                tasks: findTasksInString(
                                    row["Analysis Task(s)"],
                                ),
                                // Use default pref if blank
                                includeRegions: row["Regions"]
                                    ? row["Regions"].trim().toLowerCase() === "true"
                                    : prefs.default_regions,
                                // Use default pref if blank
                                includeExplanation: row["Explain"]
                                    ? row["Explain"].trim().toLowerCase() === "true"
                                    : prefs.default_explanation,
                                creativeOutput: (
                                    row["Creative Output"] || "none"
                                )
                                    .trim()
                                    .toLowerCase(),
                                customQrLabel: row["QR Label"] || (
                                    (row["Creative Output"] || "").trim().toLowerCase() === 'qr' 
                                        ? (prefs.default_qr_label !== undefined ? prefs.default_qr_label : t("bulkAnalysisPage.defaults.verifiedQrLabel")) 
                                        : ""
                                ),
                                // --- Initialize Context fields empty for CSV import for now ---
                                // (Can be enhanced to read columns if added to template later)
                                contextSourceUrl: "",
                                contextPlatform: "",
                                contextUserNotes: "",
                            }));

                        setBatchItems(
                            mode === "overwrite"
                                ? newItems
                                : (prev) => [...prev, ...newItems],
                        );
                    }
                } catch (err) {
                    console.error("Error during CSV processing:", err);
                    setParseError(err.message || t("bulkAnalysisPage.errors.processingFile"));
                }
            },
            error: (error) => {
                setParseError(error.message);
            },
        });
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Store the file to be uploaded later during the save operation.
        setFileToProcess(file);

        if (batchMode === "dcm" && batchItems.length > 0) {
            // If we are in DCM mode and there are already items,
            // we skip the Append/Overwrite dialog and go straight to our new overwrite confirmation.
            setIsOverwriteConfirmOpen(true);
        } else if (batchItems.length === 0) {
            // If the table is empty (in any mode), just overwrite.
            parseAndSetItems(file, "overwrite");
        } else {
            // Otherwise (Manual mode with items), show the Append/Overwrite dialog.
            setIsUploadConfirmOpen(true);
        }

        // Always reset the input value to allow re-uploading the same file
        event.target.value = null;
    };

    const handleConfirmOverwrite = () => {
        if (fileToProcess) {
            parseAndSetItems(fileToProcess, "overwrite");
        }
        setIsOverwriteConfirmOpen(false);
    };

    const handleCancelOverwrite = () => {
        setIsOverwriteConfirmOpen(false);
        setFileToProcess(null);
    };

    // --- CREATE HANDLERS FOR THE NEW MODAL ---
    const handleConfirmUpload = (mode) => {
        parseAndSetItems(fileToProcess, mode);
        setIsUploadConfirmOpen(false);
    };

    const handleCancelUpload = () => {
        setIsUploadConfirmOpen(false);
        setFileToProcess(null);
    };

    const handleAddRow = () => {
        const newRow = {
            id: `manual-${Date.now()}`,
            status: "pending",
            contentType: prefs.default_content_type,
            sourcePath: "",
            originalSourceUrl: null,
            tasks: prefs.default_tasks,
            includeRegions: prefs.default_regions,
            includeExplanation: prefs.default_explanation,
            creativeOutput: "none",
            customQrLabel: prefs.default_qr_label !== undefined ? prefs.default_qr_label : t("bulkAnalysisPage.defaults.verifiedQrLabel"),
            errorMessage: null,
            contextSourceUrl: "",
            contextPlatform: "",
            contextUserNotes: "",
        };
        setEditingItemId(null);
        setEditingValue("");
        setBatchItems((prev) => [...prev, newRow]);

        // --- Auto-scroll to bottom-left to show the new row inputs ---
        setTimeout(() => {
            if (tableContainerRef.current) {
                tableContainerRef.current.scrollTo({
                    top: tableContainerRef.current.scrollHeight, // Scroll to bottom
                    left: 0, // Scroll to far left
                    behavior: 'smooth'
                });
            }
        }, 100); // Small delay to allow state update to render the new row

    };

    const handleRemoveRow = (idToRemove) => {
        setBatchItems((prev) => prev.filter((item) => item.id !== idToRemove));
    };

    const handleCloseDeleteConfirm = () => {
        setIsDeleteConfirmOpen(false);
        setRowToDeleteId(null);
    };

    const handleDeleteClick = (rowId) => {
        // If this batch ID is already in our confirmed set, delete immediately.
        if (confirmedBatchDeletions.has(initialBatch.batch_id)) {
            handleRemoveRow(rowId);
        } else {
            // Otherwise, open the confirmation dialog.
            setRowToDeleteId(rowId);
            setDontAskAgain(false); // Reset checkbox state
            setIsDeleteConfirmOpen(true);
        }
    };

    const handleConfirmDelete = () => {
        if (dontAskAgain) {
            // Add the current batch's ID to the confirmed set in the parent component.
            setConfirmedBatchDeletions((prev) =>
                new Set(prev).add(initialBatch.batch_id),
            );
        }
        if (rowToDeleteId) {
            handleRemoveRow(rowToDeleteId);
        }
        handleCloseDeleteConfirm();
    };

    const handleConfirmReset = () => {
        setBatchItems([]);
    };

    const handleRowUpdate = useCallback((id, field, value) => {
        setBatchItems((prevItems) =>
            prevItems.map((item) => {
                if (item.id !== id) return item;
                const updatedItem = { ...item, [field]: value };

                if (field === "contentType") {
                    if (
                        updatedItem.sourcePath &&
                        updatedItem.sourcePath.trim() !== ""
                    ) {
                        updatedItem.status = "pending";
                    }
                    if (value === "audio") {
                        updatedItem.includeRegions = false;
                    } else if (
                        item.contentType === "audio" &&
                        value !== "audio"
                    ) {
                        updatedItem.includeRegions = true;
                    }
                    // When content type changes, reset creative output
                    if (value !== "image") {
                        updatedItem.creativeOutput = "none"; // Set to none if not an image
                    } else {
                        updatedItem.creativeOutput = "none"; // Also default to none when switching TO image
                    }
                }

                if (field === "creativeOutput") {
                    if (value === "qr") {
                        // Use preferences default instead of standard default
                        updatedItem.customQrLabel = prefs.default_qr_label !== undefined 
                            ? prefs.default_qr_label 
                            : t("bulkAnalysisPage.table.placeholders.qrLabel");
                    } else {
                        // This is the existing logic: Clear the label when switching AWAY from 'qr'
                        updatedItem.customQrLabel = "";
                    }
                }

                // --- Reset Status on Config Change ---
                // If the user changes tasks, regions, explanations, or context, the item needs to be re-run
                const configFields = ["tasks", "includeRegions", "includeExplanation", "contextSourceUrl", "contextPlatform", "contextUserNotes"];
                
                if (configFields.includes(field)) {
                    if (['completed', 'failed'].includes(item.status)) {
                        // If it has an S3 path, we don't need to re-upload, it's just 'ready' to analyze
                        if (updatedItem.sourcePath && updatedItem.sourcePath.startsWith("bulk-uploads/")) {
                            updatedItem.status = "ready";
                        } else if (updatedItem.sourcePath) {
                            // If it's a URL that needs fetching, go to pending
                            updatedItem.status = "pending";
                        }
                    }
                }

                return updatedItem;
            }),
        );
    }, [prefs.default_qr_label, t]);

    const fileInputRefs = useRef({}); // Use a ref to hold refs for each file input
    const handleFileUpload = useCallback(
        async (itemId, file) => {
            const currentItem = batchItems.find((item) => item.id === itemId);
            if (!currentItem) return;

            const allowedTypes = getAllowedFileTypes(currentItem.contentType).split(',');
            const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;

            // Check if extension is valid (unless */* is allowed)
            if (!allowedTypes.includes(fileExtension) && allowedTypes[0] !== '*/*') {
                const errorMessage = t('bulkAnalysisPage.errors.invalidFileTypeExtended', {
                    contentType: currentItem.contentType,
                    expected: allowedTypes.join(', '),
                    got: fileExtension
                });
                setNotification({
                    open: true,
                    message: errorMessage,
                    severity: 'error',
                });
                
                // FIX FOR BUG 2: Set status to 'upload failed' instead of 'pending'.
                // Setting it to 'pending' caused the auto-processor to pick it up again immediately,
                // creating the infinite loop.
                handleRowUpdate(itemId, "status", "upload failed"); 
                handleRowUpdate(itemId, "errorMessage", errorMessage);

                // Clear the file input for this row to allow re-selection
                if (fileInputRefs.current[itemId]?.current) {
                    fileInputRefs.current[itemId].current.value = null;
                }
                return; // Stop the upload process
            }

            handleRowUpdate(itemId, "status", "uploading");

            try {

                // --- Calculate Hash Immediately ---
                // This allows context history to load before the batch is analyzed
                const clientSideHash = await calculateFileHash(file);
                if (clientSideHash) {
                    handleRowUpdate(itemId, "exactHash", clientSideHash);
                }

                let s3Key; // This will hold the final S3 key, regardless of which method succeeds.

                // --- STEP 1: Attempt the standard, direct-to-S3 upload first ---
                try {
                    console.log("Attempting direct-to-S3 upload...");

                    const urlResponse = await fetchWithAuth(
                        `/api/bulk/batches/${initialBatch.batch_id}/generate-upload-url`,
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                filename: file.name,
                                contentType: currentItem.contentType,
                                fileType: file.type,
                            }),
                        },
                    );

                    if (!urlResponse.ok) {
                        throw new Error(t("bulkAnalysisPage.errors.uploadUrl"));
                    }

                    const { uploadUrl, s3Key: keyFromPresigned } =
                        await urlResponse.json();

                    // Attempt the direct PUT request to S3
                    const s3Response = await fetch(uploadUrl, {
                        method: "PUT",
                        body: file,
                        headers: { "Content-Type": file.type },
                    });

                    if (!s3Response.ok) {
                        throw new Error(t("bulkAnalysisPage.errors.s3UploadStatus", { status: s3Response.status }));
                    }

                    console.log("Direct-to-S3 upload successful!");
                    s3Key = keyFromPresigned;
                } catch (error) {
                    // --- STEP 2: CATCH THE FAILURE AND DECIDE WHETHER TO FALL BACK ---
                    console.warn("Direct upload failed:", error);

                    if (
                        error instanceof TypeError &&
                        error.message.includes("Failed to fetch")
                    ) {
                        // --- STEP 3: EXECUTE THE PROXY FALLBACK ---
                        console.log(
                            "Network error detected. Falling back to proxy upload...",
                        );

                        const formData = new FormData();
                        formData.append("file", file);
                        formData.append("contentType", currentItem.contentType);

                        const proxyResponse = await fetchWithAuth(
                            `/api/bulk/batches/${initialBatch.batch_id}/upload-proxy`,
                            {
                                method: "POST",
                                body: formData,
                            },
                        );

                        const data = await proxyResponse.json(); // Get the JSON response body
                        if (!proxyResponse.ok) {
                            // If the fallback itself fails, throw a new, specific error.
                            throw new Error(data.error || t("bulkAnalysisPage.errors.proxyFallback"));
                        }

                        console.log("Proxy fallback upload successful!");
                        s3Key = data.s3Key; // Assign the s3Key from the proxy response
                    } else {
                        // If the error was not a network block (e.g., a 403 from S3),
                        // we re-throw it to be caught by the outer catch block.
                        throw error;
                    }
                }

                // --- STEP 4: UPDATE UI ON SUCCESS (from either method) ---
                handleRowUpdate(itemId, "sourcePath", s3Key);
                handleRowUpdate(itemId, "status", "ready");

            } catch (finalError) {
                // This outer catch block handles any unrecoverable error.
                console.error("Upload process failed:", finalError);
                handleRowUpdate(itemId, "status", "upload failed");
                handleRowUpdate(itemId, "errorMessage", finalError.message);
            }
        },
        [fetchWithAuth, batchItems, handleRowUpdate, initialBatch.batch_id, t, setNotification],
    );

    // Helper to remove the 6-char hash for display purposes
    const getCleanFilename = (path) => {
        if (!path) return "";
        const filename = path.split("/").pop();
        // Regex looks for: hyphen + 6 hex chars + extension at the end of string
        // Replaces "image-a1b2c3.png" with "image.png"
        return filename.replace(/-[a-f0-9]{6}(\.[a-z0-9]+)$/i, '$1');
    };

    const handleUrlBlur = useCallback(
        async (item) => {
            // Get the trimmed value from the item's source path.
            const value = (item.sourcePath || "").trim();
            const itemId = item.id;

            // 1. Check if we should exit early (Optimized)
            const isS3Path = value.startsWith("bulk-uploads/");
            
            // If it's an S3 path (Already uploaded/Ready) OR matches what we previously processed, DO NOTHING.
            // This prevents the status from flipping back to "Pending" when you just click in/out.
            if (isS3Path || (item.originalSourceUrl && value === item.originalSourceUrl)) {
                return; 
            }

            // If the item has an originalCsvSource and it hasn't changed, do nothing.
            if (item.originalCsvSource && value === item.originalCsvSource) {
                return; // Exit the function early
            }

            // Find the row index for user-facing error messages.
            const itemIndex = batchItems.findIndex((i) => i.id === itemId);
            const rowIndex = itemIndex + 1;

            // Clear any previous error message for this row at the very beginning.
            handleRowUpdate(itemId, "errorMessage", null);
            // We can also optimistically set status to 'pending' to clear old error states
            handleRowUpdate(itemId, "status", "pending");

            // Basic validation for the input value.
            const isUrl = value.toLowerCase().startsWith("http");

            // If the input is not a URL, not an S3 path, and not empty, it's an invalid format.
            if (!isUrl && !isS3Path && value.length > 0) {
                handleRowUpdate(itemId, "status", "url error");
                handleRowUpdate(
                    itemId,
                    "errorMessage",
                    t("bulkAnalysisPage.errors.invalidUrlFormat"),
                );
                return;
            }

            // A simple check for incomplete URLs.
            if (isUrl && value.length < 8) {
                handleRowUpdate(itemId, "status", "url error");
                handleRowUpdate(
                    itemId,
                    "errorMessage",
                    t("bulkAnalysisPage.errors.incompleteUrl"),
                );
                return;
            }

            // If the input is not a URL (e.g., it's empty or an S3 path), there's nothing to process here.
            if (!isUrl) {
                return;
            }

            // --- NOW it is safe to change status to Pending ---
            // We only do this if we passed all checks and are actually going to process a NEW url.
            handleRowUpdate(itemId, "errorMessage", null);
            handleRowUpdate(itemId, "status", "pending");

            // In Bulk Analysis, we do not support social media resolving (yt-dlp) for performance/complexity reasons.
            // This now blocks ALL content types, not just non-video/audio.
            if (isSocialUrl(value)) {
                const errorMessage = t('bulkAnalysisPage.errors.socialLinksNotSupported');
                handleRowUpdate(itemId, "status", "url error");
                handleRowUpdate(itemId, "errorMessage", errorMessage);
                return; // STOP all further processing for this item.
            }

            // Set the initial status to indicate processing has begun.
            handleRowUpdate(itemId, "status", "processing url");
            setNotification({ open: false });

            try {
                // Pre-Fetch Validation using Shared Utils
                validateUrlExtension(item.contentType, value, t);
                // VAST Tag Detection and Dispatcher Logic
                const isVastTag =
                    value.includes("/pfadx/") || value.endsWith(".xml");

                if (isVastTag && item.contentType !== "video") {
                    throw new Error(t("contentInput.errors.vastForVideoOnly"));
                }

                let finalMediaUrl;

                if (isVastTag) {
                    // This branch handles VAST URLs.
                    // It calls our new resolver endpoint to get the final .mp4 URL.

                    // Set a specific status message for the user.
                    setNotification({
                        open: true,
                        message: `Row ${rowIndex}: Resolving VAST tag...`,
                        severity: "info",
                    });

                    const resolveResponse = await fetchWithAuth(
                        "/api/video/resolve-vast",
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ vast_url: value }),
                        },
                    );

                    const resolveData = await resolveResponse.json();
                    if (!resolveResponse.ok) {
                        throw new Error(resolveData.error || t("bulkAnalysisPage.errors.resolveVast"));
                    }

                    finalMediaUrl = resolveData.videoUrl;
                } else {
                    // This branch handles all other URLs (direct image/video links).
                    finalMediaUrl = value;
                }

                if (!finalMediaUrl) {
                    throw new Error(t("bulkAnalysisPage.errors.resolveMediaUrl"));
                }

                // --- UNIFIED DOWNLOAD LOGIC ---
                // Before we do anything else, update the row to store this resolved URL.
                // This is the public URL we need for the final VAST MediaFile.
                handleRowUpdate(item.id, "originalSourceUrl", finalMediaUrl);
                // Both VAST and direct URL paths will end up here with a `finalMediaUrl`.
                const response = await fetchWithAuth("/api/proxy/file", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        url: finalMediaUrl,
                        contentType: item.contentType,
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || t("bulkAnalysisPage.errors.fetchFromUrl", { status: response.status }));
                }

                // PRESERVE ORIGINAL VAST TAG IN DCM MODE
                // 1. Manual Mode: Always update originalSourceUrl to what was processed.
                // 2. DCM Image: Update originalSourceUrl to the EXTRACTED clean URL (value). 
                //    (We don't want the raw <ins> tag here, as the Shield Snippet needs a valid img src).
                // 3. DCM Video: DO NOT update. We must keep the original VAST Tag for the Wrapper.
                if (batchMode !== "dcm" || item.contentType === 'image') {
                    handleRowUpdate(item.id, "originalSourceUrl", value);
                }

                const blob = await response.blob();
                
                // We validate the blob type *before* creating the file object if possible, 
                // but your util `validateBlobType` throws errors, which is good.
                validateBlobType(item.contentType, blob.type, t);
                
                const disposition = response.headers.get("Content-Disposition");
                let filename;

                // Try to get the filename from the Content-Disposition header.
                if (disposition && disposition.includes("filename=")) {
                    const filenameMatch = disposition.match(/filename="([^"]+)"/);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1];
                    }
                }

                // If the header didn't provide a filename, create one from the URL.
                if (!filename) {
                    try {
                        const urlPath = new URL(finalMediaUrl).pathname;
                        // Get the last segment of the path
                        let baseNameFromPath = urlPath.substring(urlPath.lastIndexOf("/") + 1);
                        
                        // If path extraction failed, fallback to default
                        if (!baseNameFromPath || baseNameFromPath.length < 2) {
                            baseNameFromPath = t("bulkAnalysisPage.defaults.downloadedFile");
                        }

                        // Use helper to split name and extension
                        const { basename, extension } = splitFilename(baseNameFromPath);

                        // Generate a SHORT unique hash (6 chars) to prevent S3 collisions
                        const urlHash = stringToHash(value).substring(0, 6);

                        // Construct clean filename: originalName-shortHash.ext
                        // This prevents weird truncation like ".p" or ".pn"
                        filename = `${basename}-${urlHash}${extension}`;
                    } catch (e) {
                        filename = `downloaded-${stringToHash(value).substring(0, 6)}.tmp`;
                    }
                }

                // DCM URLs often yield filenames without extensions (e.g., 'image-id'). 
                // This causes the `handleFileUpload` validation to fail (got extension ".").
                // We force an extension based on the content type if one is missing.
                if (filename && !filename.includes('.')) {
                    if (item.contentType === 'image') filename += '.jpg';
                    else if (item.contentType === 'video') filename += '.mp4';
                    else if (item.contentType === 'audio') filename += '.mp3';
                    else if (item.contentType === 'text') filename += '.txt';
                }

                // Create a new File object with the determined filename.
                const fetchedFile = new File([blob], filename, {
                    type: blob.type,
                });

                // Await the file upload process to complete. This uploads the file to S3.
                await handleFileUpload(itemId, fetchedFile);

                // Only overwrite if the current selection is 'none'.
                // If it was set to 'qr' via CSV or manual selection, leave it alone.
                if (item.creativeOutput === "none") {
                    if (item.contentType === "image" || item.contentType === "video") {
                        handleRowUpdate(itemId, "creativeOutput", "shield");
                    }
                }

            } catch (err) {
                // This catch block handles any error from the try block (resolver, proxy, or upload).
                console.error("url processing failed:", err);
                handleRowUpdate(itemId, "status", "url error");
                handleRowUpdate(itemId, "errorMessage", err.message);
                setNotification({
                    open: true,
                    message: `Row ${rowIndex}: ${err.message}`,
                    severity: "error",
                });
            }
        },
        [
            batchItems,
            fetchWithAuth,
            handleFileUpload,
            handleRowUpdate,
            setNotification,
            t,
            batchMode
        ],
    );


    // --- Auto-translate "QR Label" on language switch ---
    const prevLangRef = useRef(i18n.language);

    useEffect(() => {
        const currentLang = i18n.language;
        const prevLang = prevLangRef.current;

        // Only proceed if the language has actually changed
        if (currentLang !== prevLang) {
            // 1. Fetch exactly what the default string was in the PREVIOUS language
            const oldDefaultLabel = i18n.t("bulkAnalysisPage.defaults.verifiedQrLabel", { lng: prevLang });
            
            // 2. Fetch what the default string is in the CURRENT language
            const newDefaultLabel = i18n.t("bulkAnalysisPage.defaults.verifiedQrLabel", { lng: currentLang });

            // 3. Update state
            setBatchItems(prevItems => 
                prevItems.map(item => {
                    // If the current label matches the OLD default (e.g., "VERIFIED"), 
                    // update it to the NEW default (e.g., "VERIFICATO").
                    // If the user typed a custom label (e.g., "MY BRAND"), it won't match, so it stays.
                    if (item.customQrLabel === oldDefaultLabel) {
                        return { ...item, customQrLabel: newDefaultLabel };
                    }
                    return item;
                })
            );

            // 4. Update ref for the next switch
            prevLangRef.current = currentLang;
        }
    }, [i18n.language, i18n, setBatchItems]);


    useEffect(() => {
        // We removed the strict 'batchMode !== dcm' check here.
        // Instead, we filter specific items below to decide what to auto-process.

        const autoProcessUrls = async () => {
            if (isAutoProcessingRef.current) {
                return;
            }

            // Filter items that need auto-processing
            const itemsToProcess = batchItems.filter((item) => {
                const hasPendingUrl = item.status === "pending" &&
                    (item.sourcePath || "").trim().toLowerCase().startsWith("http");

                if (!hasPendingUrl) return false;

                // LOGIC:
                // 1. DCM Mode: Always auto-process.
                // 2. Manual Mode: Only auto-process items from CSV imports (id starts with 'csv-item-').
                //    We skip 'manual-' items here so we don't trigger validation while the user is still typing.
                //    Manual items are handled by the onBlur event in the TextField.
                if (batchMode === "dcm") return true;
                if (item.id.startsWith("csv-item-")) return true;

                return false;
            });

            if (itemsToProcess.length === 0) {
                return;
            }

            isAutoProcessingRef.current = true;

            try {
                // 1. Create an array of promises by calling handleUrlBlur for each item.
                //    We don't await them individually here.
                const processingPromises = itemsToProcess.map((item) =>
                    handleUrlBlur(item),
                );

                // 2. Promise.allSettled will wait for ALL promises to either resolve or reject.
                await Promise.allSettled(processingPromises);

                console.log("Automated URL processing batch complete.");
            } catch (error) {
                console.error(
                    "The main auto-processing loop failed unexpectedly:",
                    error,
                );
                setNotification({
                    open: true,
                    message: t("bulkAnalysisPage.errors.autoProcessingCritical"),
                    severity: "error",
                });
            } finally {
                // Release the lock so the effect can run again if new items are added.
                isAutoProcessingRef.current = false;
            }
        };

        const timerId = setTimeout(autoProcessUrls, 100);
        return () => clearTimeout(timerId);
    }, [batchItems, batchMode, handleUrlBlur, setNotification, t]);

    const handleSaveBatch = async () => {
        setShowUnsavedWarning(false);
        setIsSaving(true);
        setNotification({ open: false, message: "", severity: "success" });
        try {
            let s3KeyForSourceCsv = initialBatch.source_csv_path;

            if (batchMode === "dcm" && fileToProcess) {
                setNotification({
                    open: true,
                    message: t("bulkAnalysisPage.notifications.storingSheet"),
                    severity: "info",
                });

                const urlResponse = await fetchWithAuth(
                    `/api/bulk/batches/${initialBatch.batch_id}/generate-upload-url`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            filename: fileToProcess.name,
                            contentType: "source_csv",
                            fileType: fileToProcess.type,
                        }),
                    },
                );
                if (!urlResponse.ok)
                    throw new Error(t("bulkAnalysisPage.errors.presignedUrlCsv"));

                const { uploadUrl, s3Key } = await urlResponse.json();

                try {
                    console.log(
                        "Attempting direct-to-S3 upload for source CSV...",
                    );
                    const s3Response = await fetch(uploadUrl, {
                        method: "PUT",
                        body: fileToProcess,
                        headers: { "Content-Type": fileToProcess.type },
                    });
                    if (!s3Response.ok)
                        throw new Error(t("bulkAnalysisPage.errors.s3UploadStatus", { status: s3Response.status }));
                    console.log("Direct-to-S3 upload successful!");
                    s3KeyForSourceCsv = s3Key;
                } catch (error) {
                    console.warn("Direct S3 upload failed:", error);
                    // Check if it's a network error (indicative of a firewall block)
                    if (
                        error instanceof TypeError &&
                        error.message.includes("Failed to fetch")
                    ) {
                        console.log(
                            "Network error detected. Falling back to server-side proxy upload...",
                        );

                        const formData = new FormData();
                        formData.append("file", fileToProcess);
                        // Add the new field for the backend
                        formData.append("uploadType", "source_csv");

                        const proxyResponse = await fetchWithAuth(
                            `/api/bulk/batches/${initialBatch.batch_id}/upload-proxy`,
                            {
                                method: "POST",
                                body: formData,
                            },
                        );
                        const data = await proxyResponse.json();
                        if (!proxyResponse.ok)
                            throw new Error(data.error || t("bulkAnalysisPage.errors.proxyFallback"));

                        console.log("Proxy fallback upload successful!");
                        s3KeyForSourceCsv = data.s3Key; // Use the key returned by our backend
                    } else {
                        // If it wasn't a network error, re-throw it to be caught by the main handler
                        throw error;
                    }
                }
            }

            const payload = {
                batch_name: batchName,
                emailNotificationEnabled: emailNotification,
                items: batchItems.map(({ id, ...rest }) => rest),
                source_csv_path: s3KeyForSourceCsv,
            };

            const response = await fetchWithAuth(
                `/api/bulk/batches/${initialBatch.batch_id}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                },
            );

            const responseData = await response.json(); // Get response body regardless of status

            if (!response.ok) {
                if (response.status === 409) {
                    throw new Error(
                        responseData.error ||
                            t("bulkAnalysisPage.notifications.batchNameExists"),
                    );
                }
                throw new Error(
                    responseData.error ||
                        t("bulkAnalysisPage.notifications.saveError"),
                );
            }

            // SUCCESS!
            setNotification({
                open: true,
                message: t("bulkAnalysisPage.notifications.saveSuccess"),
                severity: "success",
            });

            // Use the authoritative response from the server as the new "source of truth".
            // Do not manually construct the object on the client side.
            onBatchUpdate(responseData);

            // Mark the form as clean since it now matches the server state.
            setIsDirty(false);
            setIsEditingName(false);
            setFileToProcess(null);
        } catch (error) {
            console.error(error);
            setNotification({
                open: true,
                message: t("bulkAnalysisPage.notifications.errorPrefix", { message: error.message }),
                severity: "error",
            });
        } finally {
            setIsSaving(false);
            setIsEditingName(false);
        }
    };

    // 1. The actual function that triggers the API call (moved out of handleStartAnalysis)
    const executeBatchStart = async () => {
        setIsStarting(true);
        try {
            const response = await fetchWithAuth(`/api/bulk/batches/${initialBatch.batch_id}/start`, {
                method: 'POST',
            });

            const confirmedUpdatedBatch = await response.json();

            if (!response.ok) {
                throw new Error(confirmedUpdatedBatch.error || t("bulkAnalysisPage.errors.startAnalysis"));
            }

            setNotification({
                open: true,
                message: t('bulkAnalysisPage.notifications.startSuccess', { count: confirmedUpdatedBatch.items.length }),
                severity: 'success'
            });

            onBatchUpdate(confirmedUpdatedBatch);

        } catch (err) {
            console.error("Error starting analysis:", err);
            setNotification({ open: true, message: t("bulkAnalysisPage.notifications.errorPrefix", { message: err.message }), severity: 'error' });
            onBatchUpdate(initialBatch);
        } finally {
            setIsStarting(false);
        }
    };

    // 2. Handle "I Agree"
    const handleConsentAgree = () => {
        if (dontShowConsentAgain) {
            sessionStorage.setItem('auth_ai_public_consent', 'true');
        }
        setIsConsentModalOpen(false);
        executeBatchStart();
    };

    // 3. Handle "Delete and Proceed"
// 3. Handle "Delete and Proceed"
    const handleConsentDeleteAndProceed = async (deleteType) => {
        setIsConsentModalOpen(false);
        setIsSaving(true); // Show spinner while validating/saving

        try {
            // A. Create a cleaned copy of items (Local State Update simulation)
            const cleanedItems = batchItems.map(item => {
                const newItem = { ...item };
                if (deleteType === 'url' || deleteType === 'both') newItem.contextSourceUrl = '';
                if (deleteType === 'note' || deleteType === 'both') newItem.contextUserNotes = '';
                return newItem;
            });

            // --- NEW: RE-RUN VALIDATION ON CLEANED ITEMS ---
            const errors = [];
            const seenCombinations = new Set();

            cleanedItems.forEach((item, index) => {
                const rowNum = index + 1;

                if (item.status === 'URL Error' || item.status === 'Upload Failed') {
                    errors.push(t('bulkAnalysisPage.validation.errorUnresolved', { row: rowNum, status: item.status }));
                    return;
                }

                const sourcePath = (item.sourcePath || '').trim();
                
                // Generate key based on the NEW cleaned state
                const combinationKey = [
                    sourcePath.toLowerCase(),
                    item.creativeOutput,
                    item.customQrLabel || '',
                    (item.contextSourceUrl || '').trim().toLowerCase(),
                    (item.contextPlatform || '').trim().toLowerCase(),
                    (item.contextUserNotes || '').trim().toLowerCase()
                ].join('|');

                if (!sourcePath) {
                    errors.push(t('bulkAnalysisPage.validation.errorEmptySource', { row: rowNum }));
                } else {
                    if (batchMode === "manual") {
                        if (seenCombinations.has(combinationKey)) {
                            const displaySource = sourcePath.startsWith('bulk-uploads/') 
                                ? sourcePath.split('/').slice(-2).join('/') 
                                : sourcePath;

                            errors.push(
                                t("bulkAnalysisPage.validation.errorDuplicateSource", { row: rowNum, source: displaySource }),
                            );
                        }
                    }
                    seenCombinations.add(combinationKey);
                }

                if (!item.tasks || item.tasks.length === 0) {
                    errors.push(t('bulkAnalysisPage.validation.errorEmptyTasks', { row: rowNum }));
                }

                const isUrl = sourcePath.toLowerCase().startsWith('http');
                const isS3Path = sourcePath.toLowerCase().startsWith('bulk-uploads/');
                const looksLikeFile = /\.(jpg|jpeg|png|gif|webp|mp3|wav|ogg|aac|flac|m4a|mp4|webm|mov|avi|mkv|txt)$/i.test(sourcePath);

                if (!isUrl && !isS3Path && looksLikeFile) {
                    errors.push(t('bulkAnalysisPage.validation.errorLocalFile', { row: rowNum, source: sourcePath }));
                }
            });

            // --- CHECK VALIDATION RESULTS ---
            if (errors.length > 0) {
                // 1. Update the UI to reflect the cleaned state (so user sees the note is gone)
                setBatchItems(cleanedItems);
                // 2. Show the validation errors
                setValidationErrors(errors);
                // 3. Stop the saving spinner
                setIsSaving(false);
                // 4. ABORT: Do not save, do not start analysis.
                return; 
            }

            // --- B. Validation Passed: Save cleaned items to DB ---
            const payload = {
                // We only send items here to update content, preserving other metadata
                items: cleanedItems.map(({ id, ...rest }) => rest)
            };

            const saveResponse = await fetchWithAuth(`/api/bulk/batches/${initialBatch.batch_id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!saveResponse.ok) throw new Error(t("bulkAnalysisPage.errors.clearContext"));
            
            // C. Update Local State with Server Response
            const updatedBatchData = await saveResponse.json();
            const newItemsWithIds = (updatedBatchData.items || []).map((item, index) => ({
                ...item,
                id: `initial-item-${initialBatch.batch_id}-${index}` 
            }));
            setBatchItems(newItemsWithIds);
            setIsDirty(false); // Mark clean

            // D. Proceed to Start Analysis
            await executeBatchStart();

        } catch (err) {
            console.error("Error processing request:", err);
            setNotification({ open: true, message: t("bulkAnalysisPage.notifications.errorPrefix", { message: err.message }), severity: 'error' });
            setIsSaving(false);
        }
    };

    // This will be the new handler for the "Start Analysis" button
    const handleStartAnalysis = async () => {
        // 1. Check for unsaved changes first
        if (isDirty) {
            setShowUnsavedWarning(true);
            return;
        }

        // 2. Perform new validations
        const errors = [];
        const seenCombinations = new Set();

        batchItems.forEach((item, index) => {
            const rowNum = index + 1;

            // Validate against the definitive status of the row, not just the temporary error message.
            if (
                item.status === "URL Error" ||
                item.status === "Upload Failed"
            ) {
                errors.push(
                    t("bulkAnalysisPage.validation.errorUnresolved", {
                        row: rowNum,
                        status: item.status,
                    }),
                );
                // Continue to the next item in the loop to find all errors.
                return;
            }

            const sourcePath = item.sourcePath.trim();
            // We now include ALL context fields in the uniqueness check.
            // This ensures that the same file with different context is treated as distinct.
            const combinationKey = [
                sourcePath.toLowerCase(),
                item.creativeOutput,
                item.customQrLabel || '',
                (item.contextSourceUrl || '').trim().toLowerCase(),
                (item.contextPlatform || '').trim().toLowerCase(),
                (item.contextUserNotes || '').trim().toLowerCase()
            ].join('|');

            // Check for empty source path
            if (!sourcePath) {
                errors.push(
                    t("bulkAnalysisPage.validation.errorEmptySource", {
                        row: rowNum,
                    }),
                );
            } else {
                // Only perform the duplicate check if the batch is in manual mode.
                if (batchMode === "manual") {
                    if (seenCombinations.has(combinationKey)) {
                        // Clean up the path for display: ".../inputs/text/file.txt" -> "text/file.txt"
                        const displaySource = sourcePath.startsWith('bulk-uploads/') 
                            ? sourcePath.split('/').slice(-2).join('/') 
                            : sourcePath;

                        errors.push(
                            t(
                                "bulkAnalysisPage.validation.errorDuplicateSource",
                                { row: rowNum, source: displaySource },
                            ),
                        );
                    }
                }
                // Always add the combination to the set to check against (for manual mode).
                seenCombinations.add(combinationKey);
            }

            // Check if the tasks array is empty or doesn't exist.
            if (!item.tasks || item.tasks.length === 0) {
                errors.push(
                    t("bulkAnalysisPage.validation.errorEmptyTasks", {
                        row: rowNum,
                    }),
                );
            }

            // Check for unresolved local file paths, now correctly ignoring S3 paths.
            const isUrl = sourcePath.toLowerCase().startsWith("http");
            const isS3Path = sourcePath
                .toLowerCase()
                .startsWith("bulk-uploads/");
            const looksLikeFile =
                /\.(jpg|jpeg|png|gif|webp|mp3|wav|ogg|aac|flac|m4a|mp4|webm|mov|avi|mkv|txt)$/i.test(
                    sourcePath,
                );

            // The condition is now: Is it NOT a URL, AND NOT an S3 path, BUT it looks like a file?
            // This correctly identifies only true local files.
            if (!isUrl && !isS3Path && looksLikeFile) {
                errors.push(
                    t("bulkAnalysisPage.validation.errorLocalFile", {
                        row: rowNum,
                        source: sourcePath,
                    }),
                );
            }
        });

        setValidationErrors(errors);

        // 3. If validation passes, proceed
        if (errors.length === 0) {

            const hasAnyNote = batchItems.some(i => i.contextUserNotes && i.contextUserNotes.trim().length > 0);
            const hasAnyUrl = batchItems.some(i => i.contextSourceUrl && i.contextSourceUrl.trim().length > 0);

            if (hasAnyNote || hasAnyUrl) {
                // Determine Type for Modal Text
                if (hasAnyNote && hasAnyUrl) setConsentWarningType('both');
                else if (hasAnyNote) setConsentWarningType('note');
                else setConsentWarningType('url');

                // Check Session Storage
                const sessionConsent = sessionStorage.getItem('auth_ai_public_consent');
                if (sessionConsent !== 'true') {
                    setIsConsentModalOpen(true);
                    return; // STOP HERE, wait for modal interaction
                }
            }

            setIsStarting(true);

            try {
                // The API call now happens *after* the UI has already updated.
                const response = await fetchWithAuth(
                    `/api/bulk/batches/${initialBatch.batch_id}/start`,
                    {
                        method: "POST",
                    },
                );

                // 3. Get the full, authoritative batch object from the response.
                const confirmedUpdatedBatch = await response.json();

                if (!response.ok) {
                    // Use the error message from the JSON body if it exists.
                    throw new Error(confirmedUpdatedBatch.error || t("bulkAnalysisPage.errors.startAnalysis"));
                }

                // C. Show the success notification immediately.
                setNotification({
                    open: true,
                    message: t("bulkAnalysisPage.notifications.startSuccess", {
                        count: confirmedUpdatedBatch.items.length,
                    }), // Example of a more dynamic message
                    severity: "success",
                });

                // 5. Sync the parent component with the CONFIRMED state from the backend.
                // This ensures that when polling begins, it starts from a perfectly consistent state.
                onBatchUpdate(confirmedUpdatedBatch);
            } catch (err) {
                console.error("Error starting analysis:", err);
                setNotification({
                    open: true,
                    message: t("bulkAnalysisPage.notifications.errorPrefix", { message: err.message }),
                    severity: "error",
                });
                // If the API call fails, revert the UI back to its original state
                onBatchUpdate(initialBatch);
            } finally {
                setIsStarting(false);
            }
        }
    };

    const getStatusBorderColor = (item) => {
        // Define your theme colors or use hex codes directly
        const errorColor = "#f44336"; // Red
        const successColor = "#66bb6a"; // Green
        const warningColor = "#ffa726"; // Orange
        const processingColor = "#50728C"; // Light Blue for processing
        const defaultColor = "rgba(255, 255, 255, 0.23)";

        if (
            item.errorMessage ||
            item.status === "url error" ||
            item.status === "upload failed"
        ) {
            return errorColor;
        }
        if (item.status === "ready" || item.status === "completed") {
            return successColor;
        }
        if (["processing url", "processing", "queued", "resolving"].includes(item.status)) {
            return processingColor;
        }
        if (["pending", "uploading"].includes(item.status)) {
            return warningColor;
        }
        return defaultColor;
    };

    const getStatusBorderWidth = (item) => {
        // For any of our special states, we want a thicker border to make it stand out.
        if (item.errorMessage || item.status !== "pending" || item.sourcePath) {
            // This covers Ready, Error, processing, uploading, and any non-empty pending field.
            return "2px";
        }
        // For a new, empty, pending row, use the default thin border.
        return "1px";
    };

    const handleStartEditing = (item) => {
        setEditingItemId(item.id);
        // Use the clean filename to get the basename without the hash
        const cleanName = getCleanFilename(item.sourcePath); 
        const { basename } = splitFilename(cleanName);
        setEditingValue(basename);
    };

    const handleCancelEditing = () => {
        setEditingItemId(null);
        setEditingValue("");
    };

    const handleConfirmRename = async (item) => {
        setIsRenaming(item.id);
        try {
            const response = await fetchWithAuth(
                `/api/bulk/batches/${initialBatch.batch_id}/items/${item.item_id}/rename`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ newBasename: editingValue }),
                },
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || t("bulkAnalysisPage.errors.renameFile"));
            }

            // Update the local state to reflect the change immediately
            handleRowUpdate(item.id, "sourcePath", data.newSourcePath);
            setNotification({
                open: true,
                message: t("bulkAnalysisPage.notifications.fileRenamed"),
                severity: "success",
            });
        } catch (error) {
            console.error("Rename failed:", error);
            setNotification({
                open: true,
                message: t("bulkAnalysisPage.notifications.errorPrefix", { message: error.message }),
                severity: "error",
            });
        } finally {
            setIsRenaming(null);
            handleCancelEditing();
        }
    };

    // Handler for copying the URL to clipboard
    const handleCopyUrl = (urlToCopy) => {
        if (!urlToCopy) return;
        navigator.clipboard
            .writeText(urlToCopy)
            .then(() => {
                setNotification({
                    open: true,
                    message: t("bulkAnalysisPage.notifications.urlCopied"),
                    severity: "success",
                });
            })
            .catch((err) => {
                console.error("Failed to copy URL: ", err);
                setNotification({
                    open: true,
                    message: t("bulkAnalysisPage.notifications.urlCopyFailed"),
                    severity: "error",
                });
            });
    };

    const handleGenerateOutput = useCallback(async (item, skipLicenseCheck = false) => {
        if (generatingOutputId || checkingLicenseId) return;

        const isLicenseRequired = item.creativeOutput === 'qr' || item.creativeOutput === 'invisible';

        // --- PHASE 1: LICENSE INTERCEPTION ---
        if (isLicenseRequired && item.exactHash && !skipLicenseCheck) {
            // ALWAYS open the modal first.
            // We pass a 'null' status initially to indicate "Loading/Unknown".
            // The parent component (BulkAnalysisPage) will handle the fetching.
            onActivateLicense(item, null); 
            return; 
        }
        
        // --- PHASE 2: FILE GENERATION & DOWNLOAD (SHIELD OR LICENSED ASSET) ---
        // Start Progress Donut
        setGeneratingOutputId(item.id);
        setNotification({ open: false });
        setProgressById(prev => ({ ...prev, [item.id]: 0 }));
        const isVideo = item.contentType === 'video';
        let progressInterval = null;

        // Only run the simulated percentage timer if it's a video
        if (isVideo) {
            setProgressById(prev => ({ ...prev, [item.id]: 0 }));
            progressInterval = setInterval(() => {
                setProgressById(prev => {
                    const old = prev[item.id] || 0;
                    if (old >= 99) return prev; 
                    
                    const minInc = 0.5;
                    const maxInc = 1.5;
                    const diff = Math.random() * (maxInc - minInc) + minInc;
                    return { ...prev, [item.id]: Math.min(old + diff, 99) };
                });
            }, 500);
        }

        try {

            setNotification({
                open: true,
                message: t("bulkAnalysisPage.notifications.creativeDownloading", { defaultValue: "Your verified creative is downloading." }),
                severity: "info",
            });

            // Step 1: Always call 'generate-output' first. This ensures the output file
            // exists and handles the 'shield' (snippet) case.
            const generateResponse = await fetchWithAuth(
                `/api/bulk/batches/${initialBatch.batch_id}/items/${item.item_id}/generate-output`,
                {
                    method: "POST",
                },
            );

            // --- ASYNC INTERCEPT LOGIC ---
            if (generateResponse.status === 202) {
                const data = await generateResponse.json();
                if (progressInterval) clearInterval(progressInterval);
                if (isVideo) setProgressById(prev => ({ ...prev, [item.id]: 100 }));
                setNotification({
                    open: true,
                    message: t("adCreativeTools.notifications.emailed", { defaultValue: data.message }),
                    severity: "success",
                });
                
                // Delay clearing the success 100% circle so the user can read the message
                setTimeout(() => {
                    setGeneratingOutputId(null);
                    setProgressById(prev => {
                        const newState = { ...prev };
                        delete newState[item.id];
                        return newState;
                    });
                }, 8000); 
                
                return; // Stop here, wait for the email!
            }
            // ----------------------------------------

            const generateData = await generateResponse.json();
            if (!generateResponse.ok) {
                throw new Error(generateData.error || t("bulkAnalysisPage.errors.prepareOutput"));
            }

            // Handle the 'shield' case as before.
            if (item.creativeOutput === "shield") {
                const isVast = item.contentType === "video"; // Determine if it's VAST
                setSnippetModal({
                    isOpen: true,
                    snippet: generateData.snippet,
                    type: isVast ? "vast" : "html", // Pass the type
                    title: isVast
                        ? t("bulkAnalysisPage.snippetModal.vastTitle")
                        : t("adCreativeTools.snippetArea.title"), // Pass a dynamic title
                });
                // Stop here for snippets
                if (progressInterval) clearInterval(progressInterval);
                setGeneratingOutputId(null);
                return;
            }

            // 2. Download File (Binary Proxy)

            // Step 2: Use fetchWithAuth to call our download proxy endpoint.
            const downloadResponse = await fetchWithAuth(
                `/api/bulk/batches/${initialBatch.batch_id}/items/${item.item_id}/download-output`,
            );

            if (!downloadResponse.ok) {
                // Try to get a JSON error message from the backend if the download fails.
                let errorMsg = t("bulkAnalysisPage.errors.downloadServer");
                try {
                    const errorData = await downloadResponse.json();
                    errorMsg = errorData.error || errorMsg;
                } catch (e) {
                    // Ignore if the response isn't JSON.
                }
                throw new Error(errorMsg);
            }

            // Step 3: Get the file data as a Blob. A blob is a representation of raw file data.
            const blob = await downloadResponse.blob();

            // ---> NOW THE DOWNLOAD IS ACTUALLY IN MEMORY, WE CAN STOP THE TIMER AND SHOW 100% <---
            if (progressInterval) clearInterval(progressInterval);
            if (isVideo) setProgressById(prev => ({ ...prev, [item.id]: 100 }));

            // Step 4: Create a temporary URL that points to this blob data in the browser's memory.
            const localUrl = window.URL.createObjectURL(blob);

            // Step 5: Create a temporary link element to trigger the browser's download dialog.
            const link = document.createElement("a");
            link.href = localUrl;

            // Construct the correct download filename.
            const originalFilename =
                item.sourcePath.split("/").pop() || "creative.unknown";
            let { basename, extension } = splitFilename(originalFilename);

            // Attempt to remove the hash (hyphen followed by hex string) added during upload
            // This converts "Video-7a053ae7" back to "Video"
            const hashRegex = /-[a-f0-9]+$/i;
            if (hashRegex.test(basename)) {
                basename = basename.replace(hashRegex, "");
            }

            const suffix = item.creativeOutput === 'invisible' ? '-secured' : '-verified';
            let finalExtension = extension || ".png";
            if (item.creativeOutput === 'invisible' && item.contentType === 'image') {
                finalExtension = '.png'; // Force PNG for invisible image watermarks to preserve frequencies
            } else if (item.contentType === 'video') {
                finalExtension = '.mp4';
            }

            link.setAttribute("download", `${basename}${suffix}${finalExtension}`);

            // Trigger the download and then clean up the temporary link and URL.
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(localUrl); // Free up browser memory

            setNotification({
                open: true,
                message: t(
                    "bulkAnalysisPage.notifications.downloadComplete",
                ),
                severity: "success",
            });
        } catch (error) {
            if (progressInterval) clearInterval(progressInterval);
            setProgressById(prev => ({ ...prev, [item.id]: 0 }));
            console.error("Output generation/download failed:", error);
            setNotification({
                open: true,
                message: t("bulkAnalysisPage.notifications.errorPrefix", { message: error.message }),
                severity: "error",
            });
        } finally {
            setGeneratingOutputId(null);
             // Delay clearing
             setTimeout(() => {
                setGeneratingOutputId(null);
                setProgressById(prev => {
                    const newState = { ...prev };
                    delete newState[item.id];
                    return newState;
                });
             }, 500);
        }
    }, [fetchWithAuth, initialBatch.batch_id, t, generatingOutputId, checkingLicenseId, onActivateLicense, setNotification, setSnippetModal]);

    // --- Listen for download trigger from parent ---
    useEffect(() => {
        if (triggerDownloadItemId) {
            const item = batchItems.find(i => i.id === triggerDownloadItemId);
            if (item) {
                // Call the internal download logic directly
                // We pass 'true' to skip the license check since we just came from the modal
                handleGenerateOutput(item, true); 
            }
            onDownloadTriggered(); // Reset the trigger
        }
    }, [triggerDownloadItemId, batchItems, onDownloadTriggered, handleGenerateOutput]);


    const isAnyItemUploading = batchItems.some(
        (item) =>
            item.status === "uploading" || item.status === "processing url",
    );

    // This is a new variable to determine the tooltip text
    let saveButtonTooltip = "";
    if (isAnyItemUploading) {
        saveButtonTooltip = t(
            "bulkAnalysisPage.tableHeader.saveDisabledTooltip",
        );
    } else if (!isDirty) {
        // We will add this new translation key in the next step
        saveButtonTooltip = t("bulkAnalysisPage.tableHeader.noChangesTooltip");
    }

    return (
        <Box className="batch-management-panel glass-card">
            <Snackbar
                open={showUnsavedWarning}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                sx={{
                    // Use 'fixed' to position relative to the viewport
                    position: "fixed",
                    // Adjust top position to be below the main header
                    top: "280px", // You might need to adjust this value slightly
                    // Ensure it spans a good portion of the screen width
                    width: "calc(100% - 48px)", // Full width minus some padding
                    maxWidth: "600px", // But don't let it get too wide
                    zIndex: 1400,
                }}
            >
                <Alert
                    severity="warning"
                    onClose={() => setShowUnsavedWarning(false)}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {t("bulkAnalysisPage.notifications.saveBeforeAnalyze")}
                </Alert>
            </Snackbar>
            <Tooltip title={t("bulkAnalysisPage.tableHeader.backButton")}>
                <IconButton
                    className="batch-back-btn"
                    onClick={handleManualBackClick}
                    aria-label="back to batches"
                >
                    <ArrowBackIcon />
                </IconButton>
            </Tooltip>
            <Tooltip
                title={t("bulkAnalysisPage.table.actions.resetBatchTooltip")}
            >
                <IconButton
                    className="batch-reset-btn"
                    onClick={() => onOpenResetConfirm(handleConfirmReset)}
                    aria-label="clear all items from batch"
                    disabled={isProcessing || batchItems.length === 0}
                >
                    <ReplayIcon />
                </IconButton>
            </Tooltip>

            <Box className="batch-controls">
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, minWidth: 0 }}>
                    <Tooltip
                        title={t(
                            batchMode === "dcm"
                                ? "bulkAnalysisPage.table.tooltips.dcmModeSelected"
                                : "bulkAnalysisPage.table.tooltips.manualModeSelected",
                        )}
                        arrow
                        placement="right"
                        PopperProps={{ className: "global-dark-tooltip" }}
                        onMouseEnter={() => setIsConfigHovered(true)}
                        onMouseLeave={() => setIsConfigHovered(false)}
                    >
                        <Box className="batch-mode-indicator" sx={{ flexShrink: 0 }}>
                            {batchMode === "dcm" ? (
                                <img
                                    src="/static/previews/platform/dcm.png"
                                    alt="DCM Mode"
                                    className="batch-mode-logo"
                                />
                            ) : (
                                <img
                                    src="/static/previews/platform/vetra_ai.png"
                                    alt="Manual Mode"
                                    className="batch-mode-logo"
                                />
                            )}
                        </Box>
                    </Tooltip>
                    <IconButton
                        onClick={() => {
                            if (isEditingName) {
                                handleNameEditConfirm();
                            } else {
                                setIsEditingName(true);
                            }
                        }}
                        disabled={isNameSaving}
                        sx={{
                            color: "rgba(255, 255, 255, 0.7)",
                            flexShrink: 0,
                            "&:hover": {
                                color: "rgba(255, 255, 255, 1)",
                                backgroundColor: "rgba(255, 255, 255, 0.08)",
                            },
                        }}
                    >
                        {isNameSaving ? (
                            <CircularProgress
                                size={24}
                                sx={{ color: "white" }}
                            />
                        ) : isEditingName ? (
                            <SaveIcon />
                        ) : (
                            <EditIcon />
                        )}
                    </IconButton>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                        {isEditingName ? (
                            <TextField
                                value={batchName}
                                onChange={handleNameChange}
                                onBlur={handleNameEditConfirm}
                                onKeyDown={(e) =>
                                    e.key === "Enter" && handleNameEditConfirm()
                                }
                                variant="standard"
                                autoFocus
                                inputProps={{
                                    spellCheck: "false",
                                    size: Math.max(batchName.length - 1, 1),
                                }}
                                sx={{
                                    "& .MuiInput-input": {
                                        color: "white",
                                        fontSize: "1.25rem",
                                        paddingBottom: "2px",
                                    },
                                    "& .MuiInput-underline:before": {
                                        borderColor: "rgba(255, 255, 255, 0.4)",
                                    },
                                    "& .MuiInput-underline:after": {
                                        borderColor: "white",
                                    },
                                }}
                            />
                        ) : (
                            <Tooltip 
                                title={batchName} 
                                arrow 
                                placement="bottom-start"
                                PopperProps={{ className: "global-dark-tooltip" }}
                            >
                                <Typography 
                                    variant="h6" 
                                    sx={{ 
                                        whiteSpace: "nowrap", 
                                        overflow: "hidden", 
                                        textOverflow: "ellipsis",
                                        cursor: "default" /* Helps indicate it's hoverable */
                                    }}
                                >
                                    {batchName}
                                </Typography>
                            </Tooltip>
                        )}
                        {initialBatch.description && !isEditingName && (
                            <Tooltip title={initialBatch.description} arrow PopperProps={{ className: "global-dark-tooltip" }}>
                                <InfoOutlinedIcon
                                    sx={{
                                        color: "rgba(255, 255, 255, 0.6)",
                                        cursor: "help",
                                        flexShrink: 0
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                </Box>
                <Box className="batch-actions" sx={{ alignItems: "center", flexShrink: 0 }}>
                    <Tooltip
                        title={t(
                            "bulkAnalysisPage.table.tooltips.configureBatch",
                        )}
                        arrow
                        placement="top"
                        PopperProps={{ className: "global-dark-tooltip" }}
                        open={isConfigHovered}
                        onMouseEnter={() => setIsConfigHovered(true)}
                        onMouseLeave={() => setIsConfigHovered(false)}
                    >
                        {/* The <span> wrapper has been removed. IconButton is now the direct child. */}
                        <IconButton
                            onClick={() => setIsConfigModalOpen(true)}
                            disabled={isProcessing}
                            sx={{
                                "& .MuiSvgIcon-root": {
                                    color: isConfigHovered
                                        ? "white"
                                        : "rgba(255, 255, 255, 0.7)",
                                    transition: "color 0.2s ease-in-out",
                                },
                                "&.Mui-disabled .MuiSvgIcon-root": {
                                    color: "rgba(255, 255, 255, 0.3)",
                                },
                            }}
                        >
                            <SettingsIcon sx={{ fontSize: "2rem" }} />
                        </IconButton>
                    </Tooltip>

                    {/* --- Workspace Preferences Icon --- */}
                    <Tooltip
                        title={t('selector.customizePreferencesTooltip')}
                        arrow
                        placement="top"
                        PopperProps={{ className: "global-dark-tooltip" }}
                    >
                        <IconButton
                            onClick={() => navigate('/app/account', { state: { scrollToPrefs: true } })}
                            disabled={isProcessing}
                            sx={{
                                color: "rgba(255, 255, 255, 0.7)",
                                transition: "color 0.2s ease-in-out",
                                "&:hover": {
                                    color: "white",
                                },
                                "&.Mui-disabled": {
                                    color: "rgba(255, 255, 255, 0.3)",
                                }
                            }}
                        >
                            <ManageAccountsIcon sx={{ fontSize: "2rem" }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip 
                        title={
                            batchMode === 'dcm' 
                            ? t('bulkAnalysisPage.table.tooltips.contextDisabledDcm') 
                            : (showContext 
                                ? t('bulkAnalysisPage.table.tooltips.hideContext') 
                                : t('bulkAnalysisPage.table.tooltips.showContext')
                            )
                        }
                        arrow
                        placement="top"
                        PopperProps={{ className: 'global-dark-tooltip' }}
                    >
                        {/* Wrapper span controls opacity for disabled state while keeping Tooltip active */}
                        <span style={{ opacity: (batchMode === 'dcm' || isProcessing) ? 0.5 : 1, display: 'inline-flex', alignItems: 'center' }}>
                            <Box sx={{ opacity: (batchMode === 'dcm' || isProcessing) ? 0.5 : 1 }}>
                                <IconButton 
                                    onClick={() => setShowContext(!showContext)} 
                                    disabled={batchMode === 'dcm' || isProcessing}
                                    size="small"
                                >
                                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                        {/* Main Icon */}
                                        <AddModeratorIcon 
                                            sx={{ 
                                                fontSize: '2rem', 
                                                color: showContext ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.4)',
                                                transition: 'color 0.3s ease-in-out'
                                            }} 
                                        />
                                        
                                        {/* Status Overlay Icons */}
                                        {showContext ? (
                                            <CheckCircleIcon 
                                                sx={{ 
                                                    position: 'absolute', 
                                                    top: '-4px', 
                                                    right: '-4px', 
                                                    fontSize: '1rem', 
                                                    color: 'success.main', 
                                                    backgroundColor: 'var(--primary-color)', 
                                                    borderRadius: '50%' 
                                                }} 
                                            />
                                        ) : (
                                            <CancelIcon 
                                                sx={{ 
                                                    position: 'absolute', 
                                                    top: '-4px', 
                                                    right: '-4px', 
                                                    fontSize: '1rem', 
                                                    color: 'error.main', 
                                                    backgroundColor: 'var(--primary-color)', 
                                                    borderRadius: '50%' 
                                                }} 
                                            />
                                        )}
                                    </Box>
                                </IconButton>
                            </Box>
                        </span>
                    </Tooltip>

                    <Tooltip
                        title={t(
                            emailNotification
                                ? "bulkAnalysisPage.tableHeader.emailNotificationTooltip.disable"
                                : "bulkAnalysisPage.tableHeader.emailNotificationTooltip.enable",
                        )}
                        arrow
                        placement="top"
                        PopperProps={{ className: "global-dark-tooltip" }}
                    >
                        <span
                            style={{
                                opacity: (isProcessing || isSaving) ? 0.5 : 1,
                                display: "inline-flex",
                                alignItems: "center",
                                cursor: (isProcessing || isSaving) ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <Box sx={{ opacity: (isProcessing || isSaving) ? 0.5 : 1 }}>
                                <IconButton
                                    onClick={() =>
                                        setEmailNotification((prev) => !prev)
                                    }
                                    disabled={isProcessing || isSaving}
                                    aria-label="toggle email notifications"
                                    size="small"
                                >
                                    <Box
                                        sx={{
                                            position: "relative",
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {/* 1. The Base Mail Icon */}
                                        <MailOutlineIcon
                                            sx={{
                                                fontSize: "2rem", // Slightly larger to accommodate the status icon
                                                color: emailNotification
                                                    ? "rgba(255, 255, 255, 0.9)"
                                                    : "rgba(255, 255, 255, 0.4)",
                                                transition:
                                                    "color 0.3s ease-in-out",
                                            }}
                                        />

                                        {/* 2. The Status Icon Overlay */}
                                        {emailNotification ? (
                                            <CheckCircleIcon
                                                sx={{
                                                    position: "absolute",
                                                    top: "-4px",
                                                    right: "-4px",
                                                    fontSize: "1rem",
                                                    color: "success.main", // Green
                                                    backgroundColor:
                                                        "var(--primary-color)",
                                                    borderRadius: "50%",
                                                }}
                                            />
                                        ) : (
                                            <CancelIcon
                                                sx={{
                                                    position: "absolute",
                                                    top: "-4px",
                                                    right: "-4px",
                                                    fontSize: "1rem",
                                                    color: "error.main", // Red
                                                    backgroundColor:
                                                        "var(--primary-color)",
                                                    borderRadius: "50%",
                                                }}
                                            />
                                        )}
                                    </Box>
                                </IconButton>
                            </Box>
                        </span>
                    </Tooltip>
                    <Tooltip
                        title={saveButtonTooltip}
                        arrow
                        placement="top"
                        PopperProps={{ className: "global-dark-tooltip" }}
                    >
                        <span>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={
                                    isSaving ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        <SaveIcon />
                                    )
                                }
                                onClick={handleSaveBatch}
                                // Add !isDirty to the disabled condition
                                disabled={
                                    !isDirty ||
                                    isSaving ||
                                    isProcessing ||
                                    isAnyItemUploading
                                }
                                className={`batch-save-btn ${showUnsavedWarning ? "highlight-warning" : ""}`}
                            >
                                {isSaving
                                    ? t(
                                          "bulkAnalysisPage.tableHeader.savingButton",
                                      )
                                    : t(
                                          "bulkAnalysisPage.tableHeader.saveButton",
                                      )}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={
                            batchMode === "dcm"
                                ? handleDownloadDcmTemplate
                                : handleDownloadStandardTemplate
                        }
                        disabled={isProcessing}
                    >
                        {t("bulkAnalysisPage.table.downloadTemplateButton")}
                    </Button>
                    <Tooltip
                        // Conditionally show the tooltip only when the button is disabled for this specific reason
                        title={
                            batchMode === "dcm" && batchItems.length > 0
                                ? t(
                                      "bulkAnalysisPage.table.tooltips.overwriteDcmSheet",
                                  )
                                : t(
                                      "bulkAnalysisPage.table.tooltips.uploadDcmSheet",
                                  )
                        }
                        arrow
                        placement="top"
                        PopperProps={{ className: "global-dark-tooltip" }}
                    >
                        {/* A span is required for the tooltip to work on a disabled button */}
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={<UploadFileIcon />}
                                onClick={() => csvInputRef.current.click()}
                                disabled={isProcessing}
                                onMouseEnter={() => {
                                    if (batchMode === "dcm")
                                        setIsUploadButtonHovered(true);
                                }}
                                onMouseLeave={() => {
                                    if (batchMode === "dcm")
                                        setIsUploadButtonHovered(false);
                                }}
                                sx={{
                                    // Define a transition for smooth color changes
                                    transition:
                                        "background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out",

                                    // Conditionally apply highlight styles ONLY when in DCM mode and the shared state is true
                                    ...(batchMode === "dcm" &&
                                        isUploadButtonHovered &&
                                        !isProcessing && {
                                            borderColor:
                                                "var(--secondary-color)",
                                            backgroundColor:
                                                "rgba(79, 195, 247, 0.1)",
                                            color: "var(--secondary-color)",
                                        }),

                                    // This '&:hover' selector ensures the direct hover effect still works perfectly
                                    "&:hover": {
                                        borderColor: "var(--secondary-color)",
                                        backgroundColor:
                                            "rgba(79, 195, 247, 0.1)",
                                        color: "var(--secondary-color)",
                                    },
                                }}
                            >
                                {batchMode === "dcm"
                                    ? t(
                                          "bulkAnalysisPage.buttons.uploadDcmSheet",
                                      )
                                    : t(
                                          "bulkAnalysisPage.table.uploadCsvButton",
                                      )}
                            </Button>
                        </span>
                    </Tooltip>
                    <input
                        type="file"
                        accept=".csv"
                        ref={csvInputRef}
                        onChange={handleFileSelect}
                        style={{ display: "none" }}
                    />
                </Box>
            </Box>

            {parseError && (
                <Alert
                    severity="error"
                    onClose={() => setParseError(null)}
                    sx={{ my: 2 }}
                >
                    {parseError}
                </Alert>
            )}
            {uploadValidationErrors.length > 0 && (
                <Alert
                    severity="error"
                    onClose={() => setUploadValidationErrors([])}
                    sx={{ my: 2, maxHeight: "200px", overflowY: "auto" }}
                >
                    <AlertTitle>
                        {t("bulkAnalysisPage.csvValidation.title")}
                    </AlertTitle>
                    {t("bulkAnalysisPage.csvValidation.description")}
                    <ul style={{ margin: "8px 0 0", paddingLeft: "20px" }}>
                        {uploadValidationErrors.map((err, index) => (
                            <li key={index}>
                                <strong>
                                    {t(
                                        "bulkAnalysisPage.csvValidation.rowPrefix",
                                        { row: err.row },
                                    )}
                                    :
                                </strong>{" "}
                                {err.message}
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            {/* --- GLOBAL COST ESTIMATOR ACCORDION --- */}
            {batchCostData && (
                <Accordion 
                    className="cost-estimator-accordion" 
                    expanded={isCostExpanded} 
                    onChange={(e, expanded) => setIsCostExpanded(expanded)}
                    sx={{ 
                        border: '1px solid rgba(255, 255, 255, 0.12)', 
                        borderRadius: '8px !important',
                        // --- FIX: Force remove default margins ---
                        margin: '0 !important', 

                        '&.Mui-expanded': {
                            margin: '0 !important',
                            marginBottom: '1rem !important',
                            minHeight: 'auto !important' // Prevents height jump
                        },
                        '&:before': {
                            display: 'none' // Removes the default separator line
                        }
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                        aria-controls="cost-content"
                        id="cost-header"
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography className="explanation-main-text">
                                    {t('selector.costEstimator.title')} (Batch Total)
                                </Typography>
                                <Tooltip 
                                    title={t('selector.costEstimator.helpTooltip')} 
                                    arrow 
                                    placement="top"
                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                >
                                    <IconButton 
                                        size="small" 
                                        className="cost-help-btn"
                                        onClick={(e) => {
                                            e.stopPropagation(); 
                                            openDocViewer('business/billing_and_usage_units.md');
                                        }}
                                    >
                                        <HelpOutlineIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            
                            <Typography className="explanation-subtext cost-breakdown-subtext">
                                {t('selector.costEstimator.subtitle', { 
                                    units: batchCostData.totalUnits, 
                                    cost: batchCostData.totalFiatDisplay 
                                })}
                            </Typography>
                        </Box>

                        {/* Currency Dropdown */}
                        <Box 
                            onClick={(e) => e.stopPropagation()}
                            className="cost-estimator-currency-row"
                        >
                            <Tooltip 
                                title={t('billingPage.exchangeRateTooltip')} 
                                arrow 
                                placement="left"
                                PopperProps={{ className: 'global-dark-tooltip' }}
                            >
                                <InfoIcon className="currency-info-icon" fontSize="small" />
                            </Tooltip>
                            <Select
                                value={selectedCurrency}
                                onChange={(e) => setSelectedCurrency(e.target.value)}
                                variant="standard"
                                disableUnderline
                                size="small"
                                className="currency-selector-small"
                                MenuProps={{ className: 'currency-menu' }}
                            >
                                {Object.keys(currencyRates).map(curr => (
                                    <MenuItem key={curr} value={curr}>
                                        {curr} ({CURRENCY_SYMBOLS[curr] || curr})
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                            {/* Chart Side */}
                            <Box sx={{ width: '120px', height: '120px' }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={batchCostData.chartData}
                                            cx="50%" cy="50%"
                                            innerRadius={30} outerRadius={50}
                                            paddingAngle={5}
                                            dataKey="value"
                                            stroke="none"
                                        >
                                            {batchCostData.chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'var(--primary-color)', 
                                                border: '1px solid rgba(255, 255, 255, 0.2)', 
                                                borderRadius: '4px', 
                                                fontSize: '12px',
                                                padding: '8px 12px'
                                            }}
                                            itemStyle={{ color: '#fff', padding: 0 }}
                                            formatter={(value, name, props) => {
                                                const percent = ((value / batchCostData.totalUnits) * 100).toFixed(0);
                                                return [
                                                    <div key="tooltip-content" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                        <span style={{ fontWeight: 600, color: '#fff' }}>{name}</span>
                                                        <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                                                            {percent}% - {value} {t('selector.costEstimator.units')} <span style={{ color: 'rgba(255,255,255,0.7)' }}>({props.payload.fiat})</span>
                                                        </span>
                                                    </div>, 
                                                    '' 
                                                ];
                                            }}
                                            separator="" 
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                            
                            {/* Legend Side */}
                            <Box sx={{ flex: 1, ml: 3 }}>
                                {batchCostData.chartData.map((item, index) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, mr: 1.5 }} />
                                            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)' }}>
                                                {item.name}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#fff' }}>
                                            {item.value} <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 'normal' }}>({item.fiat})</span>
                                        </Typography>
                                    </Box>
                                ))}
                                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'white' }}>
                                            {t('selector.costEstimator.total')}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#fff' }}>
                                            {batchCostData.totalUnits} {t('selector.costEstimator.units')} <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 'normal', fontSize: '0.85rem' }}>({batchCostData.totalFiatDisplay})</span>
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textAlign: 'right', mt: 0.5 }}>
                                        {batchCostData.exchangeRateText}
                                    </Typography>
                                </Box>
                            </Box>
                    </Box>
                        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', textAlign: 'center' }}>
                                {t('selector.costEstimator.cacheNote', { percent: batchCostData.percentCheaper })}
                            </Typography>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            {/* --- TRIAL & CANCELLATION MODE BANNER --- */}
            {(isTrial || isCancellationPending || isSubscriptionInactive) && showBanner && (
                <Box sx={{ width: '100%' }}>
                    <Alert 
                        severity={(trialExhausted || trialExpired || exceedsCredit || isCancellationPending || isSubscriptionInactive) ? "error" : "info"}
                        action={
                            <Button 
                                component={RouterLink} 
                                to="/app/billing" 
                                color="inherit" 
                                size="small"
                                variant="outlined"
                                startIcon={<RocketLaunchIcon />}
                                sx={{ fontWeight: 'bold' }}
                            >
                                {(isCancellationPending || isSubscriptionInactive)
                                    ? t('appContent.trialBanner.reactivateBtn', 'Reactivate Billing') 
                                    : t('appContent.trialBanner.upgradeBtn', 'Activate Billing')}
                            </Button>
                        }
                        sx={{ 
                            alignItems: 'center',
                            backgroundColor: (trialExhausted || trialExpired || exceedsCredit || isCancellationPending || isSubscriptionInactive) ? 'rgba(211, 47, 47, 0.1)' : 'rgba(9, 132, 227, 0.1)',
                            color: 'white',
                            border: '1px solid',
                            borderColor: (trialExhausted || trialExpired || exceedsCredit || isCancellationPending || isSubscriptionInactive) ? 'rgba(211, 47, 47, 0.5)' : 'rgba(9, 132, 227, 0.5)',
                            '& .MuiAlert-icon': {
                                color: (trialExhausted || trialExpired || exceedsCredit || isCancellationPending || isSubscriptionInactive) ? '#ef5350' : '#74b9ff'
                            },
                            '& .MuiAlert-message': {
                                width: '100%',
                                maxWidth: '70%',
                                fontSize: '0.9rem',
                                lineHeight: 1.4,
                                padding: '4px 0'
                            },
                        }}
                    >
                        {isCancellationPending ? (
                            <strong>{t('appContent.trialBanner.cancellationPending')}</strong>
                        ) : isSubscriptionInactive ? (
                            <strong>{t('appContent.trialBanner.subscriptionInactive', 'Subscription Inactive: Please add a valid payment method to resume access.')}</strong>
                        ) : trialExpired ? (
                            <strong>{t('appContent.trialBanner.expired')}</strong>
                        ) : trialExhausted ? (
                            <strong>{t('appContent.trialBanner.exhausted')}</strong>
                       ) : exceedsCredit ? (
                            <strong>{t('appContent.trialBanner.exceeds', { 
                                estimated: Math.round(estimatedCostUnits).toLocaleString(), 
                                remaining: Math.round(unitsRemaining).toLocaleString() 
                            })}</strong>
                        ) : (
                            <span>
                                {t('appContent.trialBanner.active', { 
                                    units: Math.round(unitsRemaining).toLocaleString(), 
                                    days: daysRemaining,
                                    defaultValue: `Trial Mode: ${Math.round(unitsRemaining).toLocaleString()} Units and ${daysRemaining} Days remaining.`
                                })}
                            </span>
                        )}
                    </Alert>
                </Box>
            )}

            <Box className="batch-table-outer-wrapper">
                <TableContainer 
                    ref={tableContainerRef} 
                    className="inner-dark-card batch-table-container"
                >
                    <Table
                        stickyHeader
                        aria-label="batch items table"
                        className="compact-table"
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "0%",
                                        minWidth: "1px",
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 10,
                                        backgroundColor: "var(--primary-color)",
                                        borderRight: "1px solid rgba(255, 255, 255, 0.12)",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        {t("bulkAnalysisPage.table.headers.verification")}
                                        <Tooltip
                                            title={t("bulkAnalysisPage.table.headerTooltips.verification")}
                                            arrow
                                            PopperProps={{ className: "global-dark-tooltip" }}
                                        >
                                            <InfoOutlinedIcon
                                                sx={{
                                                    fontSize: "1rem",
                                                    color: "rgba(255, 255, 255, 0.5)",
                                                    cursor: "help",
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        {t("bulkAnalysisPage.table.headers.contentType")}
                                        <Tooltip
                                            title={t("bulkAnalysisPage.table.headerTooltips.contentType")}
                                            arrow
                                            PopperProps={{ className: "global-dark-tooltip" }}
                                        >
                                            <InfoOutlinedIcon
                                                sx={{
                                                    fontSize: "1rem",
                                                    color: "rgba(255, 255, 255, 0.5)",
                                                    cursor: "help",
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        {t(
                                            "bulkAnalysisPage.table.headers.analysisTasks",
                                        )}
                                        <Tooltip
                                            title={t(
                                                "bulkAnalysisPage.table.headerTooltips.analysisTasks",
                                            )}
                                            arrow
                                            PopperProps={{
                                                className:
                                                    "global-dark-tooltip",
                                            }}
                                        >
                                            <InfoOutlinedIcon
                                                sx={{
                                                    fontSize: "1rem",
                                                    color: "rgba(255, 255, 255, 0.5)",
                                                    cursor: "help",
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                {/* Wrap Explanation Header */}
                                {prefs.display_explanation && (
                                    <TableCell sx={{ align: "center", minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                        <Box
                                            sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}
                                        >
                                            {t("bulkAnalysisPage.table.headers.explanation")}
                                            <Tooltip
                                                title={t("selector.options.includeExplanation.tooltip")}
                                                arrow PopperProps={{ className: "global-dark-tooltip" }}
                                            >
                                                <InfoOutlinedIcon sx={{ fontSize: "1rem", color: "rgba(255, 255, 255, 0.5)", cursor: "help" }} />
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                )}

                                {/* Wrap Regions Header */}
                                {prefs.display_regions && (
                                    <TableCell sx={{ align: "center", minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                        <Box
                                            sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}
                                        >
                                            {t("bulkAnalysisPage.table.headers.regions")}
                                            <Tooltip
                                                title={t("selector.options.inspectableRegions.tooltip_by_type.image")}
                                                arrow PopperProps={{ className: "global-dark-tooltip" }}
                                            >
                                                <InfoOutlinedIcon sx={{ fontSize: "1rem", color: "rgba(255, 255, 255, 0.5)", cursor: "help" }} />
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                )}
                                <TableCell sx={{ minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        {t(
                                            "bulkAnalysisPage.table.headers.contentInput",
                                        )}
                                        <Tooltip
                                            title={t(
                                                "bulkAnalysisPage.table.headerTooltips.contentInput",
                                            )}
                                            arrow
                                            PopperProps={{
                                                className:
                                                    "global-dark-tooltip",
                                            }}
                                        >
                                            <InfoOutlinedIcon
                                                sx={{
                                                    fontSize: "1rem",
                                                    color: "rgba(255, 255, 255, 0.5)",
                                                    cursor: "help",
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                {showContext && (
                                    <>
                                       <TableCell sx={{ minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <AddModeratorIcon sx={{ fontSize: '1rem',  opacity: 0.5 }} />
                                                {t('bulkAnalysisPage.table.headers.contextHistory')}
                                                <Tooltip
                                                    title={t('contentInput.context.historyButtonInfoTooltip')}
                                                    arrow
                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                >
                                                    <InfoOutlinedIcon
                                                        sx={{
                                                            fontSize: '1rem',
                                                            color: 'rgba(255, 255, 255, 0.5)',
                                                            cursor: 'help',
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <AddModeratorIcon sx={{ fontSize: '1rem',  opacity: 0.5 }} />
                                                {t('contentInput.context.sourceUrlLabel')}
                                                <Tooltip
                                                    title={t('contentInput.context.sourceUrlPlaceholder')} // Reusing placeholder text as explanation
                                                    arrow
                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                >
                                                    <InfoOutlinedIcon
                                                        sx={{
                                                            fontSize: '1rem',
                                                            color: 'rgba(255, 255, 255, 0.5)',
                                                            cursor: 'help',
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <AddModeratorIcon sx={{ fontSize: '1rem',  opacity: 0.5 }} />
                                                {t('contentInput.context.platformLabel')}
                                                <Tooltip
                                                    title="The social media platform or website where this content was found."
                                                    arrow
                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                >
                                                    <InfoOutlinedIcon
                                                        sx={{
                                                            fontSize: '1rem',
                                                            color: 'rgba(255, 255, 255, 0.5)',
                                                            cursor: 'help',
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <AddModeratorIcon sx={{ fontSize: '1rem',  opacity: 0.5 }} />
                                                {t('contentInput.context.userNotesLabel')}
                                                <Tooltip
                                                    title="Provide specific claims (e.g., 'Claimed to be unedited', 'Filmed in London') to help the AI verify them against forensic evidence."
                                                    arrow
                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                >
                                                    <InfoOutlinedIcon
                                                        sx={{
                                                            fontSize: '1rem',
                                                            color: 'rgba(255, 255, 255, 0.5)',
                                                            cursor: 'help',
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </>
                                )}
                                <TableCell sx={{ minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        {t(
                                            "bulkAnalysisPage.table.headers.status",
                                        )}
                                        <Tooltip
                                            title={t(
                                                "bulkAnalysisPage.table.headerTooltips.status",
                                            )}
                                            arrow
                                            PopperProps={{
                                                className:
                                                    "global-dark-tooltip",
                                            }}
                                        >
                                            <InfoOutlinedIcon
                                                sx={{
                                                    fontSize: "1rem",
                                                    color: "rgba(255, 255, 255, 0.5)",
                                                    cursor: "help",
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        {t(
                                            "bulkAnalysisPage.table.headers.creative",
                                        )}
                                        <Tooltip
                                            title={t(
                                                "bulkAnalysisPage.table.headerTooltips.creativeOutput",
                                            )}
                                            arrow
                                            PopperProps={{
                                                className:
                                                    "global-dark-tooltip",
                                            }}
                                        >
                                            <InfoOutlinedIcon
                                                sx={{
                                                    fontSize: "1rem",
                                                    color: "rgba(255, 255, 255, 0.5)",
                                                    cursor: "help",
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                {showQrLabelColumn && (
                                    <TableCell sx={{ minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                            }}
                                        >
                                            {t(
                                                "bulkAnalysisPage.table.headers.qrLabel",
                                            )}
                                            <Tooltip
                                                title={t(
                                                    "bulkAnalysisPage.table.headerTooltips.qrLabel",
                                                )}
                                                arrow
                                                PopperProps={{
                                                    className:
                                                        "global-dark-tooltip",
                                                }}
                                            >
                                                <InfoOutlinedIcon
                                                    sx={{
                                                        fontSize: "1rem",
                                                        color: "rgba(255, 255, 255, 0.5)",
                                                        cursor: "help",
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                )}
                                <TableCell sx={{ minWidth: "1px", width: "0%", whiteSpace: "nowrap" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        {t(
                                            "bulkAnalysisPage.table.headers.output",
                                        )}
                                        <Tooltip
                                            title={t(
                                                "bulkAnalysisPage.table.headerTooltips.output",
                                            )}
                                            arrow
                                            PopperProps={{
                                                className:
                                                    "global-dark-tooltip",
                                            }}
                                        >
                                            <InfoOutlinedIcon
                                                sx={{
                                                    fontSize: "1rem",
                                                    color: "rgba(255, 255, 255, 0.5)",
                                                    cursor: "help",
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {batchItems.length > 0 ? (
                                batchItems.map((item, index) => {
                                    // Add a fallback to prevent crashes if sourcePath is ever null or undefined
                                    const isRowReadOnly =
                                        isSaving || isStarting || isProcessing ||
                                        ![
                                            "pending",
                                            "ready",
                                            "failed",
                                            "url error",
                                            "upload failed",
                                        ].includes(item.status);
                                    const isUrlInput = (item.sourcePath || "")
                                        .trim()
                                        .toLowerCase()
                                        .startsWith("http");
                                    const originatedFromUrl =
                                        !!item.originalSourceUrl;
                                    const isS3Path = (item.sourcePath || "")
                                        .trim()
                                        .toLowerCase()
                                        .startsWith("bulk-uploads/");
                                    const acceptString = getAllowedFileTypes(
                                        item.contentType,
                                    );
                                    const hasTaskError =
                                        !item.tasks || item.tasks.length === 0;
                                    const isImageContent =
                                        item.contentType === "image";
                                    const isVideoContent =
                                        item.contentType === "video";
                                    const isRegionsDisabled =
                                        item.contentType === "audio";
                                    const isDcmMode = batchMode === "dcm";

                                    let creativeDisabledTooltipText = "";
                                    if (isDcmMode) {
                                        creativeDisabledTooltipText = t(
                                            "bulkAnalysisPage.table.tooltips.dcmModeCreativeLocked",
                                        );
                                    } else if (
                                        !(isImageContent || isVideoContent)
                                    ) {
                                        creativeDisabledTooltipText = t(
                                            "bulkAnalysisPage.table.tooltips.creativeOutputDisabled",
                                        );
                                    }

                                    // Only show the local file warning if NOT in DCM mode
                                    const shieldDisabledTooltipText =
                                        !originatedFromUrl && !isDcmMode
                                            ? t(
                                                  "bulkAnalysisPage.table.tooltips.shieldDisabledForLocalFile",
                                              )
                                            : "";

                                    const handleEditValueChange = (e) => {
                                        const newBasename = e.target.value;
                                        setEditingValue(newBasename);
                                    };
                                    // Create a ref for this row's file input if it doesn't exist
                                    if (!fileInputRefs.current[item.id]) {
                                        fileInputRefs.current[item.id] =
                                            React.createRef();
                                    }
                                    const canGenerateOutput =
                                        (item.contentType === "image" ||
                                            item.contentType === "video") &&
                                        item.status === "completed" &&
                                        item.creativeOutput !== "none";
                                    const isVerified =
                                        item.status === "completed" &&
                                        item.exactHash;
                                    // const statusKey =
                                    //     item.status?.replace(/\s+/g, "_") ||
                                    //     "pending";
                                    const statusKey = (item.status || 'pending').toLowerCase().replace(/\s+/g, "_");
                                    // const statusKey = item.status || "pending";
                                    const statusLabel = t(
                                        statusLabelKeys[statusKey] ||
                                            "bulkAnalysisPage.table.statusLabels.pending",
                                    );
                                    
                                    // Define the variables here, in the correct scope for the entire row.
                                    const { primaryTaskKey, displayScore, allResults, metadata } = getAggregatedScoreData(item, taskConfig);
                                    const hasSafetyFlags = item.status === 'completed' && metadata?.safety_flags?.length > 0;
                                    
                                    return (
                                        <TableRow key={item.id}>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    position: "sticky",
                                                    left: 0,
                                                    zIndex: 5,
                                                    backgroundColor:
                                                        "var(--primary-color)",
                                                    borderRight:
                                                        "1px solid rgba(255, 255, 255, 0.12)",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "space-between",
                                                        gap: "4px",
                                                    }}
                                                >
                                                    {/* 1. Delete Icon */}
                                                    <Tooltip
                                                        title={
                                                            isDcmReadOnly
                                                                ? t(
                                                                      "bulkAnalysisPage.table.tooltips.readOnlyInDcmMode",
                                                                  )
                                                                : t(
                                                                      "bulkAnalysisPage.table.actions.remove",
                                                                  )
                                                        }
                                                        arrow
                                                        PopperProps={{
                                                            className:
                                                                "global-dark-tooltip",
                                                        }}
                                                    >
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    handleDeleteClick(
                                                                        item.id,
                                                                    )
                                                                }
                                                                disabled={
                                                                    isDcmReadOnly
                                                                }
                                                            >
                                                                <DeleteOutlineIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>

                                                    <Tooltip
                                                        title={renderRowCostTooltip(item)}
                                                        arrow
                                                        placement="right"
                                                        PopperProps={{ 
                                                            className: "global-dark-tooltip",
                                                            sx: { '& .MuiTooltip-tooltip': { minWidth: '200px', p: 0, backgroundColor: '#2D3748 !important' } }
                                                        }}
                                                    >
                                                        <IconButton 
                                                            size="small" 
                                                            sx={{ 
                                                                width: '34px',
                                                                height: '34px',
                                                                color: 'rgba(255, 255, 255, 0.4)', 
                                                                '&:hover': { color: '#81C784', backgroundColor: 'rgba(129, 199, 132, 0.1)' } 
                                                            }}
                                                        >
                                                            <MonetizationOnOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {/* 2. Content Type Icon */}
                                                    <Tooltip
                                                        title={getContentTooltipContent(item, t, metadata)}
                                                        arrow
                                                        placement="bottom"
                                                        PopperProps={{ 
                                                            className: "global-dark-tooltip",
                                                            sx: {
                                                                '& .MuiTooltip-tooltip': {
                                                                    maxWidth: '420px',
                                                                    padding: '12px'
                                                                }
                                                            }
                                                        }}
                                                    >
                                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                        <Box 
                                                            sx={{ 
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '36px',
                                                                height: '36px',
                                                                borderRadius: '50%',
                                                                // --- MODIFICATION START: Remove conditional styling ---
                                                                backgroundColor: item.status === 'completed' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                                                                border: 'none', // Always no border
                                                                // --- MODIFICATION END ---
                                                                color: item.status === 'completed' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.3)',
                                                                cursor: 'help',
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                        >
                                                            {getContentTypeIcon(item.contentType)}
                                                        </Box>
                                                    
                                                        {hasSafetyFlags && (
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                bottom: -4,
                                                                right: -4,
                                                                backgroundColor: 'transparent',
                                                                borderRadius: '50%',
                                                                padding: '1px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                                            }}>
                                                                <ReportProblemOutlinedIcon sx={{ 
                                                                    fontSize: '1.1rem', 
                                                                    color: '#F44336'
                                                                }} />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Tooltip>

                                                    {/* 3. Vetra Score Circle (conditionally rendered) */}
                                                    {item.status === "completed" && displayScore != null ? (
                                                        <Tooltip
                                                            title={
                                                                <Box sx={{ p: 1, minWidth: '200px' }}>
                                                                    <Typography variant="subtitle2" sx={{ mb: 1, borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 0.5, fontWeight: 'bold' }}>
                                                                        {t('resultDisplay.analysisTasks.all_scores', { defaultValue: 'All Analysis Scores' })}
                                                                    </Typography>
                                                                    {/* Map over the strictly ordered array to display the scores neatly */}
                                                                    {masterTaskOrder.map(taskKey => {
                                                                        if (allResults[taskKey] && allResults[taskKey].score != null) {
                                                                            const name = t(`verificationPage.analysisTasks.${taskKey}`, { defaultValue: taskConfig[taskKey]?.display_name || taskKey });
                                                                            const isPrimary = taskKey === primaryTaskKey;
                                                                            
                                                                            // --- NEW: Grab the specific color for this score ---
                                                                            const scoreColor = getScoreColor(allResults[taskKey].score);

                                                                            return (
                                                                                <Box key={taskKey} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                                                                                    <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>{name}</span>
                                                                                    {/* --- NEW: Apply the color to the span --- */}
                                                                                    <span style={{ fontSize: '0.85rem', fontWeight: isPrimary ? 'bold' : 'normal', color: scoreColor }}>
                                                                                        {allResults[taskKey].score}%
                                                                                    </span>
                                                                                </Box>
                                                                            );
                                                                        }
                                                                        return null;
                                                                    })}
                                                                </Box>
                                                            }
                                                            arrow
                                                            placement="bottom"
                                                            PopperProps={{ className: 'global-dark-tooltip' }}
                                                        >
                                                            <Box sx={{ display: 'inline-block' }}>
                                                                <ScoreDonut
                                                                    riskScore={displayScore}
                                                                    scoreType={primaryTaskKey} 
                                                                    subScores={allResults}     
                                                                    size={36}
                                                                    compact={true}
                                                                    showPercentSymbol={true}
                                                                />
                                                            </Box>
                                                        </Tooltip>
                                                    ) : item.status !== "completed" ? (
                                                        // --- STATE 2: Not yet completed ---
                                                        <Tooltip
                                                            title={getPlaceholderTooltipText(item, t, masterTaskOrder, taskConfig)}
                                                            arrow
                                                            PopperProps={{
                                                                className:
                                                                    "global-dark-tooltip",
                                                            }}
                                                        >
                                                            <div className="auth-score-circle placeholder">
                                                                ?
                                                            </div>
                                                        </Tooltip>
                                                    ) : (
                                                        // --- STATE 3: Completed but NO score (fallback) ---
                                                        <div className="auth-score-placeholder" />
                                                    )}

                                                    {/* 4. Shield Icon */}
                                                    <Tooltip
                                                        title={
                                                            isVerified
                                                                ? t("bulkAnalysisPage.table.tooltips.viewVerification")
                                                                : t("bulkAnalysisPage.table.tooltips.verificationNotAvailable")
                                                        }
                                                        arrow
                                                        PopperProps={{ className: "global-dark-tooltip" }}
                                                    >
                                                        <Box 
                                                            onClick={() => isVerified && handleViewVerificationPage(item)}
                                                            sx={{ 
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '36px',
                                                                height: '36px',
                                                                borderRadius: '50%',
                                                                backgroundColor: isVerified 
                                                                    ? 'rgba(255, 255, 255, 0.1)' 
                                                                    : 'rgba(255, 255, 255, 0.05)',
                                                                color: isVerified 
                                                                    ? 'rgba(255, 255, 255, 0.8)' 
                                                                    : 'rgba(255, 255, 255, 0.3)',
                                                                cursor: isVerified ? 'pointer' : 'not-allowed',
                                                                transition: 'all 0.2s ease',
                                                                '&:hover': isVerified ? {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    color: 'rgba(255, 255, 255, 1)',
                                                                    transform: 'scale(1.1)'
                                                                } : {}
                                                            }}
                                                        >
                                                            <img
                                                                src="/static/vetraai_logo_grey.png"
                                                                alt="Verification Shield"
                                                                style={{ 
                                                                    width: '55%', 
                                                                    height: '55%', 
                                                                    opacity: isVerified ? 0.9 : 0.4 
                                                                }}
                                                            />
                                                        </Box>
                                                    </Tooltip>

                                                    {/* 5. Copy Icon */}
                                                    <Tooltip
                                                        title={
                                                            isVerified
                                                                ? t("bulkAnalysisPage.table.tooltips.copyVerificationLink")
                                                                : t("bulkAnalysisPage.table.tooltips.verificationNotAvailable")
                                                        }
                                                        arrow
                                                        PopperProps={{ className: "global-dark-tooltip" }}
                                                    >
                                                        <Box 
                                                            onClick={() => {
                                                                if (isVerified) {
                                                                    let url = `${window.location.origin}${constructVerificationUrl(item, user)}`;
                                                                    
                                                                    // Inject Time Bomb for Shield assets so the copied link works immediately (mimicking ad behavior)
                                                                    if (item.creativeOutput === 'shield') {
                                                                        url += `&source=shield&click_ts=${Date.now()}`;
                                                                    }
                                                                    
                                                                    handleCopyLink(url);
                                                                }
                                                            }}
                                                            sx={{ 
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '36px',
                                                                height: '36px',
                                                                borderRadius: '50%',
                                                                backgroundColor: isVerified 
                                                                    ? 'rgba(255, 255, 255, 0.1)' 
                                                                    : 'rgba(255, 255, 255, 0.05)',
                                                                color: isVerified 
                                                                    ? 'rgba(255, 255, 255, 0.8)' 
                                                                    : 'rgba(255, 255, 255, 0.3)',
                                                                cursor: isVerified ? 'pointer' : 'not-allowed',
                                                                transition: 'all 0.2s ease',
                                                                '&:hover': isVerified ? {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    color: 'rgba(255, 255, 255, 1)',
                                                                    transform: 'scale(1.1)'
                                                                } : {}
                                                            }}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </Box>
                                                    </Tooltip>

                                                    {/* 6. Row Number */}
                                                    <Typography
                                                        variant="body2"
                                                        component="span"
                                                        sx={{
                                                            fontWeight: 500,
                                                            minWidth: "20px",
                                                            textAlign: "right",
                                                        }}
                                                    >
                                                        {index + 1}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell
                                                sx={{ width: "1%", whiteSpace: "nowrap" }}
                                            >
                                                <Tooltip
                                                    title={
                                                        // First, check if the item is locked because it has content.
                                                        // This applies to 'ready', 'upload failed', 'url error' etc. but NOT 'pending'.
                                                        item.status !==
                                                            "pending" &&
                                                        !isRowReadOnly
                                                            ? t(
                                                                  "bulkAnalysisPage.table.tooltips.contentTypeLocked",
                                                              )
                                                            : // Next, check if the entire row is locked because the batch is running.
                                                              isRowReadOnly
                                                              ? t(
                                                                    "bulkAnalysisPage.table.tooltips.fieldLocked",
                                                                    {
                                                                        fieldName:
                                                                            t(
                                                                                "bulkAnalysisPage.table.headers.contentType",
                                                                            ),
                                                                    },
                                                                )
                                                              : // Otherwise, show no tooltip.
                                                                ""
                                                    }
                                                    arrow
                                                    PopperProps={{
                                                        className:
                                                            "global-dark-tooltip",
                                                    }}
                                                >
                                                    <span>
                                                        {" "}
                                                        {/* This span is required for the tooltip to work on a disabled element */}
                                                        <Select
                                                            value={
                                                                item.contentType
                                                            }
                                                            onChange={(e) =>
                                                                handleRowUpdate(
                                                                    item.id,
                                                                    "contentType",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="bulk-table-select"
                                                            disabled={
                                                                isRowReadOnly ||
                                                                item.status !==
                                                                    "pending"
                                                            }
                                                            sx={{
                                                                "&.Mui-disabled":
                                                                    {
                                                                        // Target the component itself when disabled
                                                                        backgroundColor:
                                                                            "rgba(255, 255, 255, 0.08)", // A slightly darker, more blended background
                                                                        color: "rgba(255, 255, 255, 0.5)", // Fade the text
                                                                        "-webkit-text-fill-color":
                                                                            "rgba(255, 255, 255, 0.5)", // For webkit browsers
                                                                        "& .MuiSelect-icon":
                                                                            {
                                                                                color: "rgba(255, 255, 255, 0.3)", // Fade the arrow icon as well
                                                                            },
                                                                    },
                                                            }}
                                                        >
                                                            {prefs.displayed_content_types.includes('image') && <MenuItem value="image">{t("selector.contentType.image")}</MenuItem>}
                                                            {prefs.displayed_content_types.includes('text') && <MenuItem value="text">{t("selector.contentType.text")}</MenuItem>}
                                                            {prefs.displayed_content_types.includes('video') && <MenuItem value="video">{t("selector.contentType.video")}</MenuItem>}
                                                            {prefs.displayed_content_types.includes('audio') && <MenuItem value="audio">{t("selector.contentType.audio")}</MenuItem>}
                                                        </Select>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell
                                                sx={{ width: "1%", whiteSpace: "nowrap" }}
                                            >
                                                <Tooltip
                                                    title={
                                                        isRowReadOnly
                                                            ? t(
                                                                  "bulkAnalysisPage.table.tooltips.fieldLocked",
                                                                  {
                                                                      fieldName:
                                                                          t(
                                                                              "bulkAnalysisPage.table.headers.analysisTasks",
                                                                          ),
                                                                  },
                                                              )
                                                            : ""
                                                    }
                                                    arrow
                                                    PopperProps={{
                                                        className:
                                                            "global-dark-tooltip",
                                                    }}
                                                >
                                                    <span>
                                                    <Select
                                                        multiple
                                                        value={item.tasks}
                                                        onChange={(e) => handleRowUpdate(item.id, "tasks", e.target.value)}
                                                        className="bulk-table-select"
                                                        error={hasTaskError}
                                                        displayEmpty
                                                        disabled={isRowReadOnly}
                                                        renderValue={(selected) => {
                                                            if (selected.length === 0) {
                                                                return (
                                                                    <Typography variant="body2" component="span" sx={{ color: "rgba(255, 255, 255, 0.5)", "& .Mui-disabled": { cursor: "not-allowed" } }}>
                                                                        {t("bulkAnalysisPage.validation.errorEmptyTasksPlaceholder")}
                                                                    </Typography>
                                                                );
                                                            }

                                                            // 1. Sort precisely based on the availableTasks order (which is synced to YAML)
                                                            const displayText = [...selected]
                                                                .sort((a, b) => {
                                                                    const indexA = availableTasks.findIndex(t => t.value === a);
                                                                    const indexB = availableTasks.findIndex(t => t.value === b);
                                                                    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
                                                                })
                                                                .map((value) => taskShortLabels[value] || value)
                                                                .join(", ");

                                                            return (
                                                                <Typography
                                                                    variant="body2"
                                                                    component="span"
                                                                    sx={{
                                                                        display: 'block',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        maxWidth: '220px' // This triggers the "..." when text gets too long
                                                                    }}
                                                                >
                                                                    {displayText}
                                                                </Typography>
                                                            );
                                                        }}
                                                        endAdornment={
                                                            hasTaskError ? (
                                                                <InputAdornment position="end" sx={{ pr: "20px" }}>
                                                                    <Tooltip title={t("bulkAnalysisPage.validation.errorEmptyTasksTooltip")} arrow>
                                                                        <ErrorOutlineIcon color="error" />
                                                                    </Tooltip>
                                                                </InputAdornment>
                                                            ) : null
                                                        }
                                                        sx={{
                                                            maxWidth: '260px', // Prevents the column from stretching
                                                            "&.Mui-error:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f44336" },
                                                        }}
                                                    >
                                                        {availableTasks.map((task) => (
                                                            <MenuItem key={task.value} value={task.value}>
                                                                <Checkbox checked={item.tasks.indexOf(task.value) > -1} />
                                                                <ListItemText primary={task.label} />
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            {prefs.display_explanation && (
                                                <TableCell align="center">
                                                    <Tooltip
                                                        title={isRowReadOnly ? t("bulkAnalysisPage.table.tooltips.fieldLocked", { fieldName: t("bulkAnalysisPage.table.headers.explanation") }) : ""}
                                                        arrow PopperProps={{ className: "global-dark-tooltip" }}
                                                    >
                                                        <span className={isRowReadOnly ? "disabled-checkbox-wrapper" : ""}>
                                                            <Checkbox
                                                                size="small"
                                                                checked={item.includeExplanation}
                                                                onChange={(e) => handleRowUpdate(item.id, "includeExplanation", e.target.checked)}
                                                                className="bulk-table-checkbox"
                                                                disabled={isRowReadOnly}
                                                                sx={{ "&.Mui-disabled": { cursor: "not-allowed" } }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            )}

                                            {/* Wrap Regions Checkbox Cell */}
                                            {prefs.display_regions && (
                                                <TableCell align="center">
                                                    <Tooltip
                                                        title={
                                                            isRowReadOnly ? t("bulkAnalysisPage.table.tooltips.fieldLocked", { fieldName: t("bulkAnalysisPage.table.headers.regions") })
                                                                : isRegionsDisabled ? t("bulkAnalysisPage.table.tooltips.regionsDisabledForAudio") : ""
                                                        }
                                                        arrow PopperProps={{ className: "global-dark-tooltip" }}
                                                    >
                                                        <span className={isRegionsDisabled || isRowReadOnly ? "disabled-checkbox-wrapper" : ""}>
                                                            <Checkbox
                                                                size="small"
                                                                checked={item.includeRegions}
                                                                onChange={(e) => handleRowUpdate(item.id, "includeRegions", e.target.checked)}
                                                                className="bulk-table-checkbox"
                                                                disabled={isRegionsDisabled || isRowReadOnly}
                                                                sx={{ "&.Mui-disabled": { cursor: "not-allowed" } }}
                                                            />
                                                        </span>
                                                        {isRegionsDisabled && (
                                                            <Tooltip title={t("bulkAnalysisPage.table.tooltips.regionsDisabledForAudio")} arrow PopperProps={{ className: "global-dark-tooltip" }}></Tooltip>
                                                        )}
                                                    </Tooltip>
                                                </TableCell>
                                            )}

                                            <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>
                                                {/* 1. CALCULATE DYNAMIC WIDTH HERE */}
                                                {(() => {
                                                    // A. Get the exact text
                                                    const placeholderText = item.status === "resolving"
                                                        ? t("bulkAnalysisPage.table.placeholders.resolving")
                                                        : getPlaceholder(item.contentType);

                                                    // B. Define pixel constants
                                                    const CHAR_WIDTH_PX = 9; // Approx width of a character in Roboto 16px
                                                    const ICON_BUFFER_PX = 15; // Space for Cloud Icon + Error Icon + Padding
                                                    const MIN_WIDTH_PX = 50; // Minimum allowed width

                                                    // C. Calculate
                                                    const calculatedWidth = (placeholderText.length * CHAR_WIDTH_PX) + ICON_BUFFER_PX;
                                                    
                                                    // D. Determine final width (never go below 250px)
                                                    const finalMinWidth = `${Math.max(MIN_WIDTH_PX, calculatedWidth)}px`;

                                                    return (
                                                        <Tooltip
                                                            title={
                                                                isRowReadOnly
                                                                    ? t(
                                                                        "bulkAnalysisPage.table.tooltips.fieldLocked",
                                                                        {
                                                                            fieldName:
                                                                                t(
                                                                                    "bulkAnalysisPage.table.headers.contentInput",
                                                                                ),
                                                                        },
                                                                    )
                                                                    : ""
                                                            }
                                                            arrow
                                                            PopperProps={{
                                                                className:
                                                                    "global-dark-tooltip",
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', width: '100%' }}>
                                                                <TextField
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    value={
                                                                        editingItemId ===
                                                                        item.id
                                                                            ? editingValue
                                                                            : isS3Path
                                                                            ? batchMode ===
                                                                                "dcm"
                                                                                ? item.originalSourceUrl
                                                                                : getCleanFilename(item.sourcePath) // Show clean filename for S3 paths
                                                                            : item.originalSourceUrl ||
                                                                                item.sourcePath // Show URL otherwise
                                                                    }
                                                                    onChange={
                                                                        editingItemId ===
                                                                        item.id
                                                                            ? handleEditValueChange
                                                                            : (e) =>
                                                                                handleRowUpdate(
                                                                                    item.id,
                                                                                    "sourcePath",
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                )
                                                                    }
                                                                    onBlur={() => {
                                                                        if (
                                                                            editingItemId !==
                                                                            item.id
                                                                        )
                                                                            handleUrlBlur(
                                                                                item,
                                                                            );
                                                                    }}
                                                                    className="bulk-table-textfield"
                                                                    placeholder={
                                                                        item.status ===
                                                                        "resolving"
                                                                            ? t(
                                                                                "bulkAnalysisPage.table.placeholders.resolving",
                                                                            )
                                                                            : getPlaceholder(
                                                                                item.contentType,
                                                                            )
                                                                    }
                                                                    disabled={[
                                                                        "resolving",
                                                                        "processing url",
                                                                        "uploading",
                                                                    ].includes(
                                                                        item.status,
                                                                    )}
                                                                    error={
                                                                        !!item.errorMessage
                                                                    }
                                                                    InputProps={{
                                                                        readOnly:
                                                                            batchMode ===
                                                                            "dcm"
                                                                                ? true // Always read-only in DCM mode
                                                                                : editingItemId !==
                                                                                    item.id &&
                                                                                isS3Path,
                                                                        startAdornment:
                                                                            (
                                                                                // (The startAdornment logic for spinner/upload icon is correct and unchanged)
                                                                                <>
                                                                                    {[
                                                                                        "resolving",
                                                                                        "processing url",
                                                                                        "uploading",
                                                                                    ].includes(
                                                                                        item.status,
                                                                                    ) ? (
                                                                                        <InputAdornment position="start">
                                                                                            <Box
                                                                                                sx={{
                                                                                                    padding:
                                                                                                        "4px",
                                                                                                    display:
                                                                                                        "flex",
                                                                                                    marginLeft:
                                                                                                        "-8px",
                                                                                                }}
                                                                                            >
                                                                                                <CircularProgress
                                                                                                    size={
                                                                                                        24
                                                                                                    }
                                                                                                    sx={{
                                                                                                        color: "white",
                                                                                                    }}
                                                                                                />
                                                                                            </Box>
                                                                                        </InputAdornment>
                                                                                    ) : !isUrlInput &&
                                                                                    !isS3Path ? (
                                                                                        <InputAdornment
                                                                                            position="start"
                                                                                            sx={{
                                                                                                marginRight: 0,
                                                                                            }}
                                                                                        >
                                                                                            <Tooltip
                                                                                                title={t(
                                                                                                    "bulkAnalysisPage.table.tooltips.uploadLocalFile",
                                                                                                )}
                                                                                            >
                                                                                                <IconButton
                                                                                                    color="secondary"
                                                                                                    onClick={() => {
                                                                                                        // If the user has already checked "Don't ask again" for this batch, open the file dialog directly.
                                                                                                        if (
                                                                                                            dontAskRetentionAgain
                                                                                                        ) {
                                                                                                            fileInputRefs.current[
                                                                                                                item
                                                                                                                    .id
                                                                                                            ].current.click();
                                                                                                        } else {
                                                                                                            // Otherwise, prepare and open the confirmation dialog.
                                                                                                            // We store the action to be performed in the callback state.
                                                                                                            setRetentionDialogCallback(
                                                                                                                () =>
                                                                                                                    () =>
                                                                                                                        fileInputRefs.current[
                                                                                                                            item
                                                                                                                                .id
                                                                                                                        ].current.click(),
                                                                                                            );
                                                                                                            setIsRetentionDialogOpen(
                                                                                                                true,
                                                                                                            );
                                                                                                        }
                                                                                                    }}
                                                                                                    edge="start"
                                                                                                >
                                                                                                    <CloudUploadIcon />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                        </InputAdornment>
                                                                                    ) : null}
                                                                                </>
                                                                            ),
                                                                        endAdornment: (
                                                                            <InputAdornment position="end">
                                                                                {item.errorMessage && (
                                                                                    <Tooltip
                                                                                        title={
                                                                                            item.errorMessage
                                                                                        }
                                                                                        arrow
                                                                                        PopperProps={{ className: "global-dark-tooltip" }}
                                                                                    >
                                                                                        <ErrorOutlineIcon color="error" />
                                                                                    </Tooltip>
                                                                                )}
                                                                                {batchMode ===
                                                                                "dcm"
                                                                                    ? // --- RENDER FOR DCM MODE ---
                                                                                    // Show copy icon only if the URL has been successfully extracted.
                                                                                    item.originalSourceUrl && (
                                                                                        <Tooltip
                                                                                            title={t("bulkAnalysisPage.table.tooltips.copyExtractedUrl")}
                                                                                            arrow
                                                                                        >
                                                                                            <IconButton
                                                                                                onClick={() =>
                                                                                                    handleCopyUrl(
                                                                                                        item.originalSourceUrl,
                                                                                                    )
                                                                                                }
                                                                                                size="small"
                                                                                                className="copy-url-btn"
                                                                                            >
                                                                                                <ContentCopyIcon />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    )
                                                                                    : // --- RENDER FOR MANUAL MODE (Your existing, corrected logic) ---
                                                                                    isS3Path &&
                                                                                    item.item_id &&
                                                                                    (isRenaming ===
                                                                                    item.id ? (
                                                                                        <CircularProgress
                                                                                            size={
                                                                                                20
                                                                                            }
                                                                                        />
                                                                                    ) : editingItemId ===
                                                                                        item.id ? (
                                                                                        <>
                                                                                            {/* Save/Cancel buttons */}
                                                                                            <Tooltip
                                                                                                title={t(
                                                                                                    "bulkAnalysisPage.table.actions.saveRenameTooltip",
                                                                                                )}
                                                                                            >
                                                                                                <IconButton
                                                                                                    className="rename-save-btn"
                                                                                                    onClick={() =>
                                                                                                        handleConfirmRename(
                                                                                                            item,
                                                                                                        )
                                                                                                    }
                                                                                                    size="small"
                                                                                                    sx={{
                                                                                                        marginRight:
                                                                                                            "4px",
                                                                                                    }}
                                                                                                >
                                                                                                    <CheckIcon />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                            <Tooltip
                                                                                                title={t(
                                                                                                    "bulkAnalysisPage.table.actions.cancelRenameTooltip",
                                                                                                )}
                                                                                            >
                                                                                                <IconButton
                                                                                                    onClick={
                                                                                                        handleCancelEditing
                                                                                                    }
                                                                                                    size="small"
                                                                                                >
                                                                                                    <CloseIcon />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                        </>
                                                                                    ) : (
                                                                                        // Edit button
                                                                                        <Tooltip
                                                                                            title={t(
                                                                                                "bulkAnalysisPage.table.actions.renameFileTooltip",
                                                                                            )}
                                                                                        >
                                                                                            <IconButton
                                                                                                onClick={() =>
                                                                                                    handleStartEditing(
                                                                                                        item,
                                                                                                    )
                                                                                                }
                                                                                                size="small"
                                                                                                className="edit-rename-button"
                                                                                                disabled={
                                                                                                    isRowReadOnly
                                                                                                }
                                                                                            >
                                                                                                <EditIcon />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    ))}
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                    sx={{
                                                                        minWidth: finalMinWidth,
                                                                        whiteSpace: 'normal', 
                                                                        flexGrow: 1, 
                                                                        '& .MuiInputBase-inputMultiline::placeholder': { whiteSpace: 'normal' }, 
                                                                        '& .MuiOutlinedInput-inputMultiline': { whiteSpace: 'normal', wordBreak: 'break-word' },
                                                                        // Conditionally apply the "read-only" look for DCM mode
                                                                        ...(batchMode ===
                                                                            "dcm" && {
                                                                            "& .MuiOutlinedInput-root":
                                                                                {
                                                                                    backgroundColor:
                                                                                        "rgba(255, 255, 255, 0.05)",
                                                                                    pointerEvents:
                                                                                        "none",
                                                                                },
                                                                            "& .MuiInputAdornment-root":
                                                                                {
                                                                                    pointerEvents:
                                                                                        "auto",
                                                                                },
                                                                            "& .MuiOutlinedInput-notchedOutline":
                                                                                {
                                                                                    // In DCM mode, the border is ALWAYS dashed and neutral grey
                                                                                    borderStyle:
                                                                                        "dashed",
                                                                                    borderColor:
                                                                                        "rgba(255, 255, 255, 0.23)", // Neutral color
                                                                                    borderWidth:
                                                                                        "1px",
                                                                                },
                                                                            // Ensure the border doesn't change color on hover or focus in DCM mode
                                                                            "&:hover .MuiOutlinedInput-notchedOutline":
                                                                                {
                                                                                    borderColor:
                                                                                        "rgba(255, 255, 255, 0.23)",
                                                                                },
                                                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                                                {
                                                                                    borderColor:
                                                                                        "rgba(255, 255, 255, 0.23)",
                                                                                },
                                                                        }),
                                                                        // Apply dynamic border colors ONLY for manual mode
                                                                        ...(batchMode !== "dcm" && {
                                                                            "& .MuiOutlinedInput-notchedOutline": {
                                                                                borderColor: `${getStatusBorderColor(item)} !important`,
                                                                                borderWidth: `${getStatusBorderWidth(item)} !important`,
                                                                            },
                                                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                                                borderColor: `${getStatusBorderColor(item)} !important`,
                                                                            },
                                                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                                                borderColor: `${getStatusBorderColor(item)} !important`,
                                                                            },
                                                                        }),
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Tooltip>
                                                    );
                                                })()}
                                                <input
                                                    type="file"
                                                    style={{ display: "none" }}
                                                    ref={
                                                        fileInputRefs.current[
                                                            item.id
                                                        ]
                                                    }
                                                    onChange={(e) => {
                                                        // 1. Perform the upload
                                                        handleFileUpload(item.id, e.target.files[0]);
                                                        
                                                        // 2. ONLY set default to QR if it is currently 'None'.
                                                        // This ensures we don't overwrite if the user already selected something,
                                                        // and keeps this logic specific to manual file uploads (not URLs).
                                                        if (item.creativeOutput === 'none' && (item.contentType === 'image' || item.contentType === 'video')) {
                                                            handleRowUpdate(item.id, "creativeOutput", "qr");
                                                        }
                                                    }}
                                                    accept={acceptString}
                                                />
                                            </TableCell>

                                            {showContext && (
                                                <>
                                                    <TableCell align="center" sx={{ paddingLeft: '10px' }}>
                                                        {/* Show spinner if: 
                                                            1. Row is processing/uploading (input changing) 
                                                            2. Specific history fetch is in progress 
                                                        */}
                                                        {['resolving', 'processing url', 'uploading'].includes(item.status) || item.isHistoryLoading ? (
                                                            <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.5)' }} />
                                                        ) : (
                                                            <Tooltip title={item.contextHistory?.length ? t("bulkAnalysisPage.table.context.historyButton") : t("bulkAnalysisPage.table.context.noHistoryTooltip")}>
                                                                <span>
                                                                    <IconButton 
                                                                        size="small" 
                                                                        onClick={() => handleOpenHistory(item, index)}
                                                                        disabled={!item.contextHistory || item.contextHistory.length === 0 || isRowReadOnly}
                                                                        disableRipple
                                                                        sx={{ 
                                                                            // Default state color
                                                                            color: item.contextHistory?.length ? 'var(--secondary-color)' : 'rgba(255,255,255,0.2)',
                                                                            transition: 'color 0.2s',
                                                                            padding: '4px', // Minimal padding
                                                                            '&:hover': { 
                                                                                backgroundColor: 'transparent !important', // Remove background circle
                                                                                color: 'white !important', // Highlight text/icon in white
                                                                                transform: 'scale(1.1) !important' // Slight zoom for feedback
                                                                            }
                                                                        }}
                                                                    >
                                                                        <HistoryIcon fontSize="small" />
                                                                        {item.contextHistory?.length > 0 && (
                                                                            <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 'bold' }}>
                                                                                ({item.contextHistory.length})
                                                                            </Typography>
                                                                        )}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>
                                                        {/* 1. Logic for Validation & Constants */}
                                                         {(() => {
                                                            const sourceUrl = item.contextSourceUrl || '';
                                                            const isUrlInvalid = sourceUrl.length > 0 && !isValidHttpUrl(sourceUrl);
                                                            const MAX_URL_LENGTH = 500;
                                                            
                                                            // A. Get Placeholder
                                                            const urlPlaceholder = t("bulkAnalysisPage.table.context.addUrlPlaceholder");

                                                            // B. Calculate Dynamic Width
                                                            const CHAR_WIDTH_PX = 9; 
                                                            const ICON_BUFFER_PX = 80;
                                                            const MIN_WIDTH_PX = 50;
                                                            const calculatedWidth = (urlPlaceholder.length * CHAR_WIDTH_PX) + ICON_BUFFER_PX;
                                                            const finalMinWidth = `${Math.max(MIN_WIDTH_PX, calculatedWidth)}px`;

                                                            return (
                                                                <Tooltip
                                                                    // ... tooltip props (unchanged) ...
                                                                    title={
                                                                        sourceUrl ? (
                                                                            <Box>
                                                                                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{sourceUrl}</Typography>
                                                                                <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.2)', my: 1 }} />
                                                                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', textAlign: 'right' }}>
                                                                                    {t('bulkAnalysisPage.table.context.charCount', { current: sourceUrl.length, max: MAX_URL_LENGTH })}
                                                                                </Typography>
                                                                            </Box>
                                                                        ) : ""
                                                                    }
                                                                    arrow
                                                                    placement="top"
                                                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                                                >
                                                                    <span>
                                                                        <TextField
                                                                            fullWidth
                                                                            variant="outlined"
                                                                            className="bulk-table-textfield"
                                                                            value={sourceUrl}
                                                                            onChange={(e) => {
                                                                                const val = e.target.value;
                                                                                if (val.length <= MAX_URL_LENGTH) {
                                                                                    handleRowUpdate(item.id, 'contextSourceUrl', val);
                                                                                }
                                                                            }}
                                                                            disabled={isRowReadOnly}
                                                                            placeholder={t("bulkAnalysisPage.table.context.addUrlPlaceholder")} 
                                                                            error={isUrlInvalid}
                                                                            InputProps={{
                                                                                startAdornment: !sourceUrl ? (
                                                                                    <InputAdornment position="start" sx={{ marginRight: 0 }}>
                                                                                        <LinkIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.4)' }} />
                                                                                    </InputAdornment>
                                                                                ) : null,
                                                                                endAdornment: isUrlInvalid ? (
                                                                                    <InputAdornment position="end">
                                                                                        <Tooltip title={t('contentInput.context.sourceUrlError') || "Invalid URL format"} arrow>
                                                                                            <ErrorOutlineIcon color="error" fontSize="small" />
                                                                                        </Tooltip>
                                                                                    </InputAdornment>
                                                                                ) : null
                                                                            }}
                                                                            sx={{
                                                                                minWidth: finalMinWidth,
                                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                                    borderColor: isUrlInvalid ? '#f44336 !important' : undefined
                                                                                },
                                                                                '& .MuiInputBase-input::placeholder': {
                                                                                    color: 'rgba(255, 255, 255, 0.4)',
                                                                                    opacity: 1
                                                                                },
                                                                                '& .MuiOutlinedInput-input': {
                                                                                    paddingLeft: !sourceUrl ? '4px' : undefined,
                                                                                    whiteSpace: 'nowrap',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis'
                                                                                }
                                                                            }}
                                                                        />
                                                                    </span>
                                                                </Tooltip>
                                                            );
                                                        })()}
                                                    </TableCell>
                                                    <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>
                                                        {(() => {
                                                            // A. Get text to measure
                                                            let textToMeasure = "";
                                                            if (!item.contextPlatform) {
                                                                textToMeasure = t('contentInput.context.platformPlaceholder');
                                                            } else {
                                                                const option = platformOptions.find(opt => opt.value === item.contextPlatform);
                                                                textToMeasure = option?.label || item.contextPlatform;
                                                            }

                                                            // B. Constants
                                                            const CHAR_WIDTH_PX = 9;
                                                            const ICON_BUFFER_PX = 60;
                                                            const MIN_WIDTH_PX = 50;

                                                            // C. Calculate
                                                            const calculatedWidth = (textToMeasure.length * CHAR_WIDTH_PX) + ICON_BUFFER_PX;
                                                            const finalMinWidth = `${Math.max(MIN_WIDTH_PX, calculatedWidth)}px`;

                                                            return (
                                                                <Select
                                                                    fullWidth
                                                                    value={(item.contextPlatform || '').toLowerCase()}
                                                                    onChange={(e) => handleRowUpdate(item.id, 'contextPlatform', e.target.value)}
                                                                    className="bulk-table-select"
                                                                    disabled={isRowReadOnly}
                                                                    displayEmpty
                                                                    renderValue={(selected) => {
                                                                        if (!selected) {
                                                                            // Placeholder State
                                                                            return (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255, 255, 255, 0.4)' }}>
                                                                                    <PublicIcon fontSize="small" sx={{ opacity: 0.7 }} /> 
                                                                                    <Typography 
                                                                                        variant="body2" 
                                                                                        sx={{ 
                                                                                            color: 'inherit',
                                                                                            lineHeight: 1,
                                                                                            mt: '2px' 
                                                                                        }}
                                                                                    >
                                                                                        {t('contentInput.context.platformPlaceholder')}
                                                                                    </Typography>
                                                                                </Box>
                                                                            );
                                                                        }
                                                                        // Selected State
                                                                        const option = platformOptions.find(opt => opt.value === selected.toLowerCase());
                                                                        return (
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                {option?.icon} 
                                                                                <Typography 
                                                                                    variant="body2" 
                                                                                    sx={{ 
                                                                                        // Apply same fix for selected items so they don't jump
                                                                                        lineHeight: 1,
                                                                                        mt: '2px' 
                                                                                    }}
                                                                                >
                                                                                    {option?.label || selected}
                                                                                </Typography>
                                                                            </Box>
                                                                        );
                                                                    }}
                                                                    // Style the dropdown menu to be WHITE with DARK text
                                                                    MenuProps={{
                                                                        // 1. Anchor configuration to push menu down
                                                                        anchorOrigin: {
                                                                            vertical: "bottom",
                                                                            horizontal: "left"
                                                                        },
                                                                        transformOrigin: {
                                                                            vertical: "top",
                                                                            horizontal: "left"
                                                                        },
                                                                        // 2. Add margin-top to create gap
                                                                        sx: {
                                                                            marginTop: "0px" 
                                                                        },
                                                                        PaperProps: {
                                                                            sx: {
                                                                                maxHeight: 250,
                                                                                backgroundColor: 'white !important',
                                                                                color: 'rgba(0, 0, 0, 0.26) !important',
                                                                                '& .MuiMenuItem-root': {
                                                                                    color: 'rgba(0, 0, 0, 0.87) !important',
                                                                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04) !important' },
                                                                                    '&.Mui-selected': { backgroundColor: 'rgba(25, 118, 210, 0.08) !important' },
                                                                                    '&.Mui-selected:hover': { backgroundColor: 'rgba(25, 118, 210, 0.12) !important' }
                                                                                }
                                                                            }
                                                                        }
                                                                    }}
                                                                    sx={{ minWidth: finalMinWidth }}
                                                                >
                                                                    {platformOptions.map((option) => {
                                                                        const isPlaceholder = option.value === '';
                                                                        // Use standard MUI hint color (approx 38% opacity black)
                                                                        const placeholderColor = 'rgba(0, 0, 0, 0.38)'; 

                                                                        return (
                                                                            <MenuItem 
                                                                                key={option.value} 
                                                                                value={option.value}
                                                                                sx={{
                                                                                    // 1. Force Flexbox Centering
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    
                                                                                    // 2. Existing font style logic
                                                                                    fontStyle: isPlaceholder ? 'italic' : 'normal',
                                                                                }}
                                                                            >
                                                                                <ListItemIcon sx={{ 
                                                                                    minWidth: '32px', 
                                                                                    // 3. Ensure Icon is vertically centered
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    color: isPlaceholder ? `${placeholderColor} !important` : 'rgba(0, 0, 0, 0.54) !important' 
                                                                                }}>
                                                                                    {option.icon}
                                                                                </ListItemIcon>
                                                                                
                                                                                <ListItemText 
                                                                                    primary={option.label} 
                                                                                    sx={{ 
                                                                                        // 4. Remove default margins that might push text down
                                                                                        margin: 0,
                                                                                        '& .MuiTypography-root': {
                                                                                            color: isPlaceholder ? `${placeholderColor} !important` : 'rgba(0, 0, 0, 0.87) !important',
                                                                                            // 5. Ensure line-height doesn't create offset
                                                                                            lineHeight: 'normal' 
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </MenuItem>
                                                                        );
                                                                    })}
                                                                </Select>
                                                            );
                                                        })()}
                                                    </TableCell>
                                                    <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>
                                                        {(() => {
                                                            // 1. Get placeholder text
                                                            const notesPlaceholder = t("bulkAnalysisPage.table.context.addNotesPlaceholder");
                                                            
                                                            // 2. Define constants
                                                            const CHAR_WIDTH_PX = 9; 
                                                            const ICON_BUFFER_PX = 30;
                                                            const MIN_WIDTH_PX = 160;  // Original minimum width

                                                            // 3. Calculate width
                                                            const calculatedWidth = (notesPlaceholder.length * CHAR_WIDTH_PX) + ICON_BUFFER_PX;
                                                            const finalMinWidth = `${Math.max(MIN_WIDTH_PX, calculatedWidth)}px`;

                                                            return (
                                                                <div 
                                                                    onClick={() => !isRowReadOnly && handleOpenNoteModal(item)}
                                                                    style={{ cursor: isRowReadOnly ? 'default' : 'pointer' }}
                                                                >
                                                                    <TextField
                                                                        fullWidth
                                                                        variant="outlined"
                                                                        className="bulk-table-textfield"
                                                                        value={item.contextUserNotes || ''}
                                                                        disabled={isRowReadOnly}
                                                                        placeholder={t("bulkAnalysisPage.table.context.addNotesPlaceholder")}
                                                                        InputProps={{
                                                                            readOnly: true,
                                                                            startAdornment: !item.contextUserNotes ? (
                                                                                <InputAdornment position="start" sx={{ marginRight: 0 }}>
                                                                                    <EditIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.4)' }} />
                                                                                </InputAdornment>
                                                                            ) : null
                                                                        }}
                                                                        sx={{
                                                                             minWidth: finalMinWidth,
                                                                            //  pointerEvents: 'none',
                                                                            '& .MuiInputBase-input::placeholder': {
                                                                                color: 'rgba(255, 255, 255, 0.4)',
                                                                                opacity: 1
                                                                            },
                                                                            '& .MuiOutlinedInput-input': {
                                                                                whiteSpace: 'nowrap',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                paddingLeft: !item.contextUserNotes ? '4px' : undefined
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                            );
                                                        })()}
                                                    </TableCell>
                                                </>
                                            )}

                                            <TableCell
                                                sx={{ paddingLeft: "15px", width: "1%", whiteSpace: "nowrap" }}
                                            >
                                                <span
                                                    className={`status-cell status-${item.status.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                                                >
                                                    {statusLabel}
                                                </span>
                                            </TableCell>

                                            <TableCell
                                                sx={{ width: "1%", whiteSpace: "nowrap" }}
                                            >
                                                <Tooltip
                                                    title={
                                                        creativeDisabledTooltipText
                                                    }
                                                    arrow
                                                    PopperProps={{
                                                        className:
                                                            "global-dark-tooltip",
                                                    }}
                                                >
                                                    <span>
                                                        <Select
                                                            value={
                                                                isDcmMode
                                                                    ? "shield"
                                                                    : item.creativeOutput
                                                            }
                                                            onChange={(e) =>
                                                                handleRowUpdate(
                                                                    item.id,
                                                                    "creativeOutput",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="bulk-table-select"
                                                            disabled={
                                                                isDcmMode ||
                                                                !(
                                                                    isImageContent ||
                                                                    isVideoContent
                                                                ) ||
                                                                isRowReadOnly
                                                            }
                                                            sx={{
                                                                minWidth: "185px",
                                                                "&.Mui-disabled":
                                                                    {
                                                                        // Target the component itself when disabled
                                                                        backgroundColor:
                                                                            "rgba(255, 255, 255, 0.08)", // A slightly darker, more blended background
                                                                        color: "rgba(255, 255, 255, 0.5)", // Fade the text
                                                                        "-webkit-text-fill-color":
                                                                            "rgba(255, 255, 255, 0.5)", // For webkit browsers
                                                                        "& .MuiSelect-icon":
                                                                            {
                                                                                color: "rgba(255, 255, 255, 0.3)", // Fade the arrow icon as well
                                                                            },
                                                                    },
                                                            }}
                                                        >
                                                            <MenuItem value="none">
                                                                {t(
                                                                    "bulkAnalysisPage.table.creativeOptions.none",
                                                                )}
                                                            </MenuItem>
                                                            <MenuItem value="qr">
                                                                {t(
                                                                    "bulkAnalysisPage.table.creativeOptions.qr",
                                                                )}
                                                            </MenuItem>
                                                            <MenuItem value="invisible">
                                                                {t(
                                                                    "bulkAnalysisPage.table.creativeOptions.invisible",
                                                                )}
                                                            </MenuItem>

                                                            {originatedFromUrl ||
                                                            isDcmMode ? (
                                                                <MenuItem value="shield">
                                                                    {t(
                                                                        "bulkAnalysisPage.table.creativeOptions.shield",
                                                                    )}
                                                                </MenuItem>
                                                            ) : (
                                                                <Tooltip
                                                                    title={
                                                                        shieldDisabledTooltipText
                                                                    }
                                                                    arrow
                                                                    PopperProps={{
                                                                        className:
                                                                            "global-dark-tooltip",
                                                                    }}
                                                                >
                                                                    <span>
                                                                        {/* Ensure item isn't disabled internally if we are forcing it via DCM mode */}
                                                                        <MenuItem
                                                                            value="shield"
                                                                            disabled={
                                                                                !isDcmMode
                                                                            }
                                                                        >
                                                                            {t(
                                                                                "bulkAnalysisPage.table.creativeOptions.shield",
                                                                            )}
                                                                        </MenuItem>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                        </Select>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            {showQrLabelColumn && (
                                                <TableCell sx={{ width: "1%", whiteSpace: "nowrap" }}>
                                                    {(() => {
                                                        // 1. Calculate Dynamic Width
                                                        const currentValue = item.customQrLabel || "";
                                                        const placeholder = t("bulkAnalysisPage.table.placeholders.qrLabel");
                                                        
                                                        // Use the length of the value, or the placeholder (whichever is longer)
                                                        // so the box doesn't shrink below the placeholder size.
                                                        const textLength = Math.max(currentValue.length, placeholder.length);
                                                        
                                                        // Constants for pixel calculation
                                                        const CHAR_WIDTH_PX = 9; // Approx width per char
                                                        const PADDING_PX = 15;    // Input padding + borders
                                                        const MIN_WIDTH_PX = 80; // Absolute minimum

                                                        const calculatedWidth = (textLength * CHAR_WIDTH_PX) + PADDING_PX;
                                                        const finalMinWidth = `${Math.max(MIN_WIDTH_PX, calculatedWidth)}px`;

                                                        return (
                                                            item.creativeOutput === "qr" && (
                                                                <Tooltip
                                                                    title={t('bulkAnalysisPage.table.context.charCount', { 
                                                                        current: (item.customQrLabel || "").length, 
                                                                        max: 15 
                                                                    })}
                                                                    arrow
                                                                    placement="top"
                                                                    PopperProps={{ className: "global-dark-tooltip" }}
                                                                >
                                                                    <span>
                                                                        <TextField
                                                                            variant="outlined"
                                                                            value={item.customQrLabel || ""}
                                                                            onChange={(e) =>
                                                                                handleRowUpdate(
                                                                                    item.id,
                                                                                    "customQrLabel",
                                                                                    e.target.value,
                                                                                )
                                                                            }
                                                                            className="bulk-table-textfield"
                                                                            // placeholder={placeholder}
                                                                            disabled={isRowReadOnly}
                                                                            inputProps={{
                                                                                maxLength: 15,
                                                                            }}
                                                                            sx={{
                                                                                // Apply the calculated dynamic width
                                                                                minWidth: finalMinWidth,
                                                                                // Add a transition for smooth resizing
                                                                                transition: 'min-width 0.1s ease-out', 
                                                                                '& .MuiOutlinedInput-input': {
                                                                                    padding: '8.5px 12px'
                                                                                }
                                                                            }}
                                                                        />
                                                                    </span>
                                                                </Tooltip>
                                                            )
                                                        );
                                                    })()}
                                                </TableCell>
                                            )}
                                            <TableCell align="center">
                                                {checkingLicenseId === item.id ? (
                                                    <Tooltip title="Checking license status...">
                                                        <CircularProgress size={24} color="secondary" />
                                                    </Tooltip>
                                                ) : 
                                                generatingOutputId === item.id ? (
                                                    <Tooltip 
                                                        title={t('bulkAnalysisPage.table.tooltips.generating')}
                                                        PopperProps={{ className: "global-dark-tooltip" }}
                                                        arrow
                                                        placement="bottom"
                                                    >
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            {item.contentType === 'video' ? (
                                                                <DownloadProgressDonut 
                                                                    progress={progressById[item.id] || 0} 
                                                                    size={60} 
                                                                    thickness={3} 
                                                                    fontSize="0.45rem"
                                                                    color="white" 
                                                                />
                                                            ) : (
                                                                <CircularProgress 
                                                                    size={28} 
                                                                    sx={{ color: 'white' }} 
                                                                />
                                                            )}
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip
                                                        title={
                                                            canGenerateOutput
                                                                ? t(
                                                                        "bulkAnalysisPage.table.tooltips.generateOutput",
                                                                    )
                                                                : t(
                                                                        "bulkAnalysisPage.table.tooltips.outputNotAvailable",
                                                                    )
                                                        }
                                                        arrow
                                                        PopperProps={{
                                                            modifiers: [
                                                                {
                                                                    name: "offset",
                                                                    options: {
                                                                        offset: [
                                                                            0,
                                                                            12,
                                                                        ], // [skidding, distance] - pushes the tooltip 12px down
                                                                    },
                                                                },
                                                            ],
                                                            className: "global-dark-tooltip"
                                                        }}
                                                    >
                                                        <span>
                                                            {" "}
                                                            {/* Span is required for tooltip on disabled button */}
                                                            <IconButton
                                                                onClick={() =>
                                                                    handleGenerateOutput(
                                                                        item,
                                                                    )
                                                                }
                                                                disabled={
                                                                    !canGenerateOutput
                                                                }
                                                                className="output-btn-table"
                                                            >
                                                                <FileDownloadIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell
                                        // UPDATE: Dynamic colSpan calculation
                                        colSpan={
                                            7 +
                                            (showContext ? 4 : 0) +
                                            (showQrLabelColumn ? 1 : 0) +
                                            (prefs.display_explanation ? 1 : 0) +
                                            (prefs.display_regions ? 1 : 0)
                                        }
                                        align="left"
                                        sx={{ py: 4, border: "none" }}
                                    >
                                        <Typography sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
                                            {t("bulkAnalysisPage.table.empty")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Box className="add-row-footer">
                <Tooltip
                    title={
                        batchMode !== "manual"
                            ? t(
                                  "bulkAnalysisPage.table.tooltips.addRowDisabled",
                              )
                            : ""
                    }
                    arrow
                    placement="bottom"
                    PopperProps={{ className: "global-dark-tooltip" }}
                    onMouseEnter={() => {
                        if (batchMode === "dcm") setIsUploadButtonHovered(true);
                    }}
                    onMouseLeave={() => {
                        if (batchMode === "dcm")
                            setIsUploadButtonHovered(false);
                    }}
                >
                    <span>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAddRow}
                            disabled={isProcessing || batchMode !== "manual"}
                        >
                            {t("bulkAnalysisPage.table.addRowButton")}
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {validationErrors.length > 0 && (
                <Alert
                    severity="error"
                    onClose={() => setValidationErrors([])}
                    sx={{ my: 2, maxHeight: "200px", overflowY: "auto" }}
                >
                    <AlertTitle>
                        {t("bulkAnalysisPage.validation.title")}
                    </AlertTitle>
                    {t("bulkAnalysisPage.validation.description")}
                    <ul style={{ margin: "8px 0 0", paddingLeft: "20px" }}>
                        {validationErrors.map((err, index) => (
                            <li key={index}>{err}</li>
                        ))}
                    </ul>
                </Alert>
            )}

            <Box
                className="start-analysis-footer"
                sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
                <Box /> {/* This empty Box acts as a spacer */}
                <Box sx={{ display: "flex", gap: 2 }}>
                    {/* It now appears based on completed items, independent of the analysis button */}
                    {batchMode === "dcm" &&
                        completedItemsCount > 0 &&
                        !isProcessing && (
                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={
                                    isExporting ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        <FileDownloadIcon />
                                    )
                                }
                                onClick={() => setIsDeployModalOpen(true)}
                                disabled={batchMode !== "dcm" || isExporting}
                            >
                                <Tooltip
                                    title={
                                        batchMode !== "dcm"
                                            ? t(
                                                  "bulkAnalysisPage.table.tooltips.exportDisabled",
                                              )
                                            : ""
                                    }
                                >
                                    <span>
                                        {isExporting
                                            ? t(
                                                  "bulkAnalysisPage.buttons.exporting",
                                              )
                                            : t(
                                                  "bulkAnalysisPage.deployModal.exportAndDeploy",
                                              )}
                                    </span>
                                </Tooltip>
                            </Button>
                        )}

                    <Tooltip
                        title={
                            isDirty
                                ? t("bulkAnalysisPage.notifications.saveBeforeAnalyze")
                                : isCancellationPending
                                ? t('appContent.trialBanner.cancellationPending')
                                : (isTrial && trialExpired)
                                ? t('contentInput.tooltips.trialExpired')
                                : (isTrial && trialExhausted)
                                ? t('contentInput.tooltips.trialExhausted')
                                : (isTrial && exceedsCredit) // already has isTrial internally, but good for consistency
                                ? t('contentInput.tooltips.trialInsufficient')
                                : ""
                        }
                        arrow
                        placement="top"
                    >
                        <span>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="large"
                                startIcon={
                                    isStarting || isProcessing ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        <PlayCircleOutlineIcon />
                                    )
                                }
                                // The button is disabled if:
                                // 1. A save operation is in progress.
                                // 2. An analysis is already starting or processing.
                                // 3. There are no items ready to be analyzed.
                                // 4. There are unsaved changes (isDirty)
                                // 5. Trial limits reached
                                disabled={
                                    isSaving ||
                                    isStarting ||
                                    isProcessing ||
                                    readyItemsCount === 0 ||
                                    isDirty ||
                                    isCancellationPending ||
                                    (isTrial && (trialExhausted || trialExpired || exceedsCredit))
                                 }
                                onClick={handleStartAnalysis}
                            >
                                {isStarting
                                    ? t("bulkAnalysisPage.buttons.analyzing")
                                    : isProcessing
                                      ? t(
                                            "bulkAnalysisPage.buttons.analyzingProgress",
                                            {
                                                finished: finishedCount,
                                                total: progress.total,
                                            },
                                        )
                                      : t("bulkAnalysisPage.startAnalysisButton", {
                                            count: readyItemsCount,
                                        })}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            <SnippetModal
                isOpen={snippetModal.isOpen}
                onClose={() =>
                    setSnippetModal({
                        isOpen: false,
                        snippet: "",
                        type: "html",
                        title: "",
                    })
                }
                snippet={snippetModal.snippet}
                type={snippetModal.type}
                title={snippetModal.title}
            />

            {/* --- CONTEXT HISTORY MODAL --- */}
            <Dialog
                open={historyModal.isOpen}
                onClose={() => setHistoryModal({ ...historyModal, isOpen: false })}
                PaperProps={{
                    sx: {
                        backgroundColor: '#2c3e50',
                        color: 'white',
                        minWidth: '500px',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                    }
                }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                    {t('contentInput.context.historyModalTitle')}
                </DialogTitle>
                <DialogContent sx={{ mt: 2, padding: '0 24px' }}>
                    <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
                        {t('contentInput.context.historyModalDescription')}
                    </DialogContentText>
                    {historyModal.history.length === 0 ? (
                        <Typography variant="body2" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3, color: 'rgba(255, 255, 255, 0.5)' }}>
                            {t('contentInput.context.noHistory')}
                        </Typography>
                    ) : (
                        <List>
                            {historyModal.history.map((item, index) => {
                                // Format Date
                                const dateStr = item.analysis_timestamp
                                    ? new Date(item.analysis_timestamp).toLocaleString()
                                    : t('contentInput.context.historyLabels.na');

                                return (
                                    <ListItem key={index} disablePadding sx={{ borderBottom: '1px solid rgba(255,255,255,0.25)', mb: 1 }}>
                                        <ListItemButton
                                            onClick={() => handleApplyHistory(item)}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                gap: 0.5,
                                                py: 2,
                                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
                                            }}
                                        >
                                            {/* 1) Analyzed On */}
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 'bold', mb: 0.5 }}>
                                                {t('contentInput.context.historyLabels.analyzedOn')}: {dateStr}
                                            </Typography>

                                            {/* 2) Source URL */}
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                                <Box component="span" sx={{ color: 'rgba(255,255,255,0.6)', mr: 1 }}>
                                                    {t('contentInput.context.historyLabels.sourceUrl')}:
                                                </Box>
                                                <span style={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.85rem',
                                                    wordBreak: 'break-all',
                                                    whiteSpace: 'normal'
                                                }}>
                                                    {item.source_url || t('contentInput.context.historyLabels.na')}
                                                </span>
                                            </Typography>

                                            {/* 3) Platform */}
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                                <Box component="span" sx={{ color: 'rgba(255,255,255,0.6)', mr: 1 }}>
                                                    {t('contentInput.context.historyLabels.platform')}:
                                                </Box>
                                                {item.platform 
                                                    ? getPlatformDisplayName(item.platform, t) 
                                                    : t('contentInput.context.historyLabels.na')}
                                            </Typography>

                                            {/* 4) Additional Notes */}
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                                <Box component="span" sx={{ color: 'rgba(255,255,255,0.6)', mr: 1 }}>
                                                    {t('contentInput.context.historyLabels.notes')}:
                                                </Box>
                                                <span style={{
                                                    fontStyle: item.user_notes ? 'italic' : 'normal',
                                                    fontSize: '0.85rem',
                                                    wordBreak: 'break-all',
                                                    whiteSpace: 'normal'
                                                }}>
                                                    {item.user_notes || t('contentInput.context.historyLabels.na')}
                                                </span>
                                            </Typography>

                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
                    <Button onClick={() => setHistoryModal({ ...historyModal, isOpen: false })} sx={{ color: 'rgba(255, 255, 255, 0.6)', '&:hover': { color: 'white' } }}>
                        {t('bulkAnalysisPage.dialogs.common.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- USER NOTES MODAL --- */}
            <Dialog
                open={noteModal.isOpen}
                onClose={() => setNoteModal({ ...noteModal, isOpen: false })}
                PaperProps={{
                    sx: {
                        backgroundColor: '#ffffff !important', // White background
                        color: 'rgba(0, 0, 0, 0.87) !important', // Dark text
                        width: '100%',
                        maxWidth: '600px',
                        border: 'none', // Remove dark border
                        borderRadius: '12px',
                        boxShadow: '0 24px 38px 3px rgba(0,0,0,0.14), 0 9px 46px 8px rgba(0,0,0,0.12), 0 11px 15px -7px rgba(0,0,0,0.2)'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    justifyContent: 'center',
                    color: 'rgba(0, 0, 0, 0.87)' // Dark Title
                }}>
                    <AddModeratorIcon sx={{ color: 'var(--primary-color)' }} /> {/* Use primary color for icon */}
                    {t('contentInput.context.userNotesLabel')}
                </DialogTitle>
                
                <DialogContent sx={{ mt: 2, padding: '24px' }}>
                    
                    {/* Context Info Header - Light Grey Box */}
                    <Box sx={{ 
                        mb: 3, 
                        p: 2, 
                        backgroundColor: '#f5f5f5', // Light Grey
                        borderRadius: '8px', 
                        border: '1px solid #e0e0e0' 
                    }}>
                        <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)', display: 'block', fontWeight: 'bold', mb: 0.5 }}>
                            {t("bulkAnalysisPage.table.context.editingContextFor")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.87)', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                            {noteModal.item?.sourcePath.split('/').pop() || t("bulkAnalysisPage.table.context.unknownItem")}
                        </Typography>
                    </Box>

                    {/* Text Area with Light Theme Styling */}
                    <TextField
                        inputRef={noteInputRef} // Attach Ref here
                        autoFocus // Keep for good measure, though ref handles it
                        multiline
                        rows={8}
                        fullWidth
                        variant="outlined"
                        value={noteModal.value}
                        onChange={(e) => {
                            setNoteModal({ ...noteModal, value: e.target.value.slice(0, 1000) });
                        }}
                        placeholder={getNotesPlaceholderForItem(noteModal.item)}
                        InputProps={{
                            startAdornment: !noteModal.value && (
                                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                    <Tooltip
                                        title={getContextWhyTooltipForItem(noteModal.item)}
                                        arrow
                                        placement="right"
                                        PopperProps={{ className: 'global-dark-tooltip' }} // Keep tooltip dark for consistency? Or remove class for default light tooltip
                                    >
                                        <InfoOutlinedIcon
                                            fontSize="small"
                                            sx={{ color: 'rgba(0, 0, 0, 0.54)', cursor: 'help' }}
                                        />
                                    </Tooltip>
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            // 1. Target the Root Container
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: '#ffffff',
                                fontSize: '0.95rem',
                                paddingLeft: !noteModal.value ? '12px' : undefined,
                                // Borders
                                '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.23)' }, 
                                '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.87)' }, 
                                '&.Mui-focused fieldset': { borderColor: 'var(--primary-color)' },
                            },
                            // 2. Target the INPUT text specifically to force it DARK against global styles
                            '& .MuiInputBase-input': {
                                color: 'rgba(0, 0, 0, 0.87) !important', // Force Text Black
                                caretColor: '#000000 !important',         // Force Cursor Black
                            },
                            // 3. Target the Placeholder
                            '& .MuiInputBase-input::placeholder': {
                                color: 'rgba(0, 0, 0, 0.42)', // Standard placeholder gray
                                opacity: 1
                            }
                        }}
                    />
                    
                    {/* Character Counter - Localized */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Typography variant="caption" sx={{ color: noteModal.value.length >= 1000 ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)' }}>
                            {t('bulkAnalysisPage.table.context.charCount', { 
                                current: noteModal.value.length, 
                                max: 1000 
                            })}
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)', p: 2 }}>
                    <Button 
                        onClick={() => setNoteModal({ ...noteModal, isOpen: false })} 
                        variant="outlined" 
                        // INCREASED SPECIFICITY: Chaining classes to beat global rule
                        sx={{ 
                            "&.MuiButton-root.MuiButton-outlined": {
                                color: 'rgba(0, 0, 0, 0.6) !important', 
                                borderColor: 'rgba(0, 0, 0, 0.23) !important',
                            },
                            "&.MuiButton-root.MuiButton-outlined:hover": { 
                                color: 'black !important',
                                borderColor: 'black !important',
                                backgroundColor: 'rgba(0, 0, 0, 0.04) !important' 
                            } 
                        }}
                    >
                        {t('bulkAnalysisPage.dialogs.common.cancel')}
                    </Button>
                    <Button 
                        onClick={handleSaveNote} 
                        variant="contained" 
                        color="secondary" // Changed to secondary to match app theme
                        disabled={noteModal.value === (noteModal.item?.contextUserNotes || '')} 
                        sx={{
                            "&.MuiButton-root.Mui-disabled": {
                                backgroundColor: 'rgba(0, 0, 0, 0.12) !important',
                                color: 'rgba(0, 0, 0, 0.26) !important'
                            }
                        }}
                    >
                        {t("bulkAnalysisPage.table.context.saveNotesButton")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- CONSENT MODAL --- */}
            {ReactDOM.createPortal(
                <div className={`confirmation-dialog-overlay ${isConsentModalOpen ? 'open' : ''}`} role="dialog" aria-modal="true">
                    {isConsentModalOpen && (
                        <div 
                            onClick={(e) => e.stopPropagation()} 
                            style={{ 
                                backgroundColor: 'var(--primary-color)', // Dark red background
                                color: 'rgba(255,255,255,0.8)', 
                                border: '1px solid #ef5350',
                                maxWidth: '600px', // Match ContentInput width
                                padding: '24px',
                                borderRadius: '4px',
                                textAlign: 'left' // Force left alignment
                            }}
                        >
                            {/* Title */}
                            <h2 
                                className="confirmation-dialog-title" 
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '12px', 
                                    color: '#ef5350', 
                                    justifyContent: 'flex-start', // Align left
                                    marginBottom: '20px',
                                    fontSize: '1.25rem',
                                    fontWeight: 500,
                                    padding: 0 // Remove default padding from class
                                }}
                            >
                                <WarningAmberIcon /> {t('contentInput.context.publicWarningModal.title')}
                            </h2>

                            {/* Close Button */}
                            <IconButton 
                                aria-label="close" 
                                onClick={() => setIsConsentModalOpen(false)} 
                                className="dialog-close-button" 
                                sx={{ 
                                    position: 'absolute',
                                    right: 12,
                                    top: 12,
                                    color: 'rgba(255, 255, 255, 0.5) !important',
                                    '&:hover': { color: 'white !important', backgroundColor: 'rgba(255,255,255,0.1) !important' }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                            
                            <div className="confirmation-dialog-body" style={{ padding: 0 }}>
                                {/* Dynamic Description */}
                                <p className="confirmation-dialog-text" style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '16px', textAlign: 'left' }}>
                                    {consentWarningType === 'both' && t('contentInput.context.publicWarningModal.description_both')}
                                    {consentWarningType === 'note' && t('contentInput.context.publicWarningModal.description_note')}
                                    {consentWarningType === 'url' && t('contentInput.context.publicWarningModal.description_url')}
                                </p>

                                {/* Warning Highlight */}
                                <p className="confirmation-dialog-text" style={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 'bold', marginBottom: '16px', textAlign: 'left' }}>
                                    {t('contentInput.context.publicWarningModal.warning')}
                                </p>

                                {/* Question */}
                                <p className="confirmation-dialog-text" style={{ color: 'rgba(255,255,255,0.8)', textAlign: 'left' }}>
                                    {t('contentInput.context.publicWarningModal.question')}
                                </p>

                                {/* Checkbox */}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={dontShowConsentAgain}
                                            onChange={(e) => setDontShowConsentAgain(e.target.checked)}
                                            sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#ef5350' } }}
                                        />
                                    }
                                    label={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{t('contentInput.context.publicWarningModal.dontShowAgain')}</Typography>}
                                    sx={{ mt: 2, display: 'flex', width: '100%', marginLeft: 0 }} 
                                />
                            </div>

                            {/* Actions */}
                            <div className="confirmation-dialog-actions" style={{ flexDirection: 'column', gap: '10px', alignItems: 'stretch', padding: '20px 0 0 0' }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={handleConsentAgree}
                                    fullWidth
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    {t('contentInput.context.publicWarningModal.agreeButton')}
                                </Button>
                                
                                <Button
                                    variant="outlined"
                                    onClick={() => handleConsentDeleteAndProceed(consentWarningType)}
                                    fullWidth
                                    sx={{
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        '&:hover': {
                                            borderColor: 'var(--secondary-color)',
                                            backgroundColor: 'rgba(79, 195, 247, 0.1)',
                                            color: 'var(--secondary-color) !important',
                                        },
                                        // Remove default margin from parent CSS class if present
                                        marginLeft: '0 !important' 
                                    }}
                                >
                                    {consentWarningType === 'both' && t('contentInput.context.publicWarningModal.deleteBothButton')}
                                    {consentWarningType === 'note' && t('contentInput.context.publicWarningModal.deleteNoteButton')}
                                    {consentWarningType === 'url' && t('contentInput.context.publicWarningModal.deleteUrlButton')}
                                </Button>

                                <Button
                                    onClick={() => setIsConsentModalOpen(false)}
                                    sx={{ color: 'rgba(255,255,255,0.5)', marginLeft: '0 !important' }}
                                    fullWidth
                                >
                                    {t('contentInput.context.publicWarningModal.cancelButton')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>,
                document.body
            )}

            {ReactDOM.createPortal(
                <div
                    className={`confirmation-dialog-overlay ${isManualUnsavedDialogOpen ? "open" : ""}`}
                    onClick={() => setIsManualUnsavedDialogOpen(false)}
                >
                    {isManualUnsavedDialogOpen && (
                        <div
                            className="confirmation-dialog-content"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <IconButton
                                aria-label="close"
                                onClick={() =>
                                    setIsManualUnsavedDialogOpen(false)
                                }
                                className="dialog-close-button"
                            >
                                <CloseIcon />
                            </IconButton>
                            <h2 className="confirmation-dialog-title">
                                {t(
                                    "bulkAnalysisPage.dialogs.unsavedChanges.title",
                                )}
                            </h2>
                            <div className="confirmation-dialog-body">
                                <p className="confirmation-dialog-text">
                                    {t(
                                        "bulkAnalysisPage.dialogs.unsavedChanges.description",
                                    )}
                                </p>
                            </div>
                            <div className="confirmation-dialog-actions">
                                <Button
                                    onClick={() =>
                                        setIsManualUnsavedDialogOpen(false)
                                    }
                                    variant="outlined"
                                >
                                    {t(
                                        "bulkAnalysisPage.dialogs.unsavedChanges.cancelButton",
                                    )}
                                </Button>
                                <Button
                                    onClick={handleManualLeavePage}
                                    color="error"
                                    variant="contained"
                                >
                                    {t(
                                        "bulkAnalysisPage.dialogs.unsavedChanges.confirmButton",
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>,
                document.body,
            )}

            {ReactDOM.createPortal(
                <div
                    className={`confirmation-dialog-overlay ${isOverwriteConfirmOpen ? "open" : ""}`}
                    onClick={handleCancelOverwrite}
                >
                    {isOverwriteConfirmOpen && (
                        <div
                            className="confirmation-dialog-content"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <IconButton
                                aria-label="close"
                                onClick={handleCancelOverwrite}
                                className="dialog-close-button"
                            >
                                <CloseIcon />
                            </IconButton>
                            <h2 className="confirmation-dialog-title">
                                {t(
                                    "bulkAnalysisPage.dialogs.overwriteConfirm.title",
                                )}
                            </h2>
                            <div className="confirmation-dialog-body">
                                <p className="confirmation-dialog-text">
                                    {t(
                                        "bulkAnalysisPage.dialogs.overwriteConfirm.description",
                                        { count: batchItems.length },
                                    )}
                                </p>
                            </div>
                            <div className="confirmation-dialog-actions">
                                <Button
                                    onClick={handleCancelOverwrite}
                                    variant="outlined"
                                >
                                    {t(
                                        "bulkAnalysisPage.dialogs.common.cancel",
                                    )}
                                </Button>
                                <Button
                                    onClick={handleConfirmOverwrite}
                                    color="error"
                                    variant="contained"
                                >
                                    {t(
                                        "bulkAnalysisPage.dialogs.overwriteConfirm.confirmButton",
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>,
                document.body,
            )}

            {ReactDOM.createPortal(
                <div
                    className={`confirmation-dialog-overlay ${isUploadConfirmOpen ? "open" : ""}`}
                    onClick={handleCancelUpload}
                >
                    {isUploadConfirmOpen && (
                        <div
                            className="confirmation-dialog-content"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <IconButton
                                aria-label="close"
                                onClick={handleCancelUpload}
                                className="dialog-close-button"
                            >
                                <CloseIcon />
                            </IconButton>
                            <h2 className="confirmation-dialog-title">
                                {t(
                                    "bulkAnalysisPage.dialogs.uploadConfirm.title",
                                )}
                            </h2>
                            <div className="confirmation-dialog-body">
                                <p className="confirmation-dialog-text">
                                    {t(
                                        "bulkAnalysisPage.dialogs.uploadConfirm.description",
                                    )}
                                </p>
                            </div>
                            <div className="confirmation-dialog-actions">
                                {/* Cancel Button (no change) */}
                                <Button
                                    onClick={handleCancelUpload}
                                    variant="outlined"
                                >
                                    {t(
                                        "bulkAnalysisPage.dialogs.common.cancel",
                                    )}
                                </Button>

                                {/* Append Button: Change variant to "contained" to make it solid blue */}
                                <Button
                                    onClick={() =>
                                        handleConfirmUpload("append")
                                    }
                                    variant="contained"
                                    color="secondary"
                                >
                                    {t(
                                        "bulkAnalysisPage.dialogs.uploadConfirm.appendButton",
                                    )}
                                </Button>

                                {/* Overwrite Button: Change color to "error" to make it red */}
                                <Button
                                    onClick={() =>
                                        handleConfirmUpload("overwrite")
                                    }
                                    color="error"
                                    variant="contained"
                                >
                                    {t(
                                        "bulkAnalysisPage.dialogs.uploadConfirm.overwriteButton",
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>,
                document.body,
            )}

            {ReactDOM.createPortal(
                <div
                    className={`confirmation-dialog-overlay ${isDeleteConfirmOpen ? "open" : ""}`}
                    onClick={handleCloseDeleteConfirm}
                >
                    {isDeleteConfirmOpen && (
                        <div
                            className="confirmation-dialog-content"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <IconButton
                                aria-label="close"
                                onClick={handleCloseDeleteConfirm}
                                className="dialog-close-button"
                            >
                                <CloseIcon />
                            </IconButton>
                            <h2 className="confirmation-dialog-title">
                                {t("bulkAnalysisPage.dialogs.deleteItem.title")}
                            </h2>
                            <div className="confirmation-dialog-body">
                                <p className="confirmation-dialog-text">
                                    {t(
                                        "bulkAnalysisPage.dialogs.deleteItem.description",
                                    )}
                                </p>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={dontAskAgain}
                                            onChange={(e) =>
                                                setDontAskAgain(
                                                    e.target.checked,
                                                )
                                            }
                                            name="dontAskAgain"
                                            color="primary"
                                        />
                                    }
                                    label={t(
                                        "bulkAnalysisPage.dialogs.deleteItem.dontAskAgain",
                                    )}
                                    sx={{
                                        mt: 2,
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                />
                            </div>
                            <div className="confirmation-dialog-actions">
                                <Button
                                    onClick={handleCloseDeleteConfirm}
                                    variant="outlined"
                                >
                                    {t(
                                        "bulkAnalysisPage.dialogs.common.cancel",
                                    )}
                                </Button>
                                <Button
                                    onClick={handleConfirmDelete}
                                    color="error"
                                    variant="contained"
                                >
                                    {t(
                                        "bulkAnalysisPage.dialogs.common.delete",
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>,
                document.body,
            )}

            {ReactDOM.createPortal(
                <div
                    className={`confirmation-dialog-overlay ${isRetentionDialogOpen ? "open" : ""}`}
                    onClick={() => setIsRetentionDialogOpen(false)}
                >
                    {isRetentionDialogOpen && (
                        <div
                            className="confirmation-dialog-content"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <IconButton
                                aria-label="close"
                                onClick={() => setIsRetentionDialogOpen(false)}
                                className="dialog-close-button"
                            >
                                <CloseIcon />
                            </IconButton>
                            <h2 className="confirmation-dialog-title">
                                {t("bulkAnalysisPage.dialogs.retention.title")}
                            </h2>
                            <div className="confirmation-dialog-body">
                                <p
                                    className="confirmation-dialog-text"
                                    style={{
                                        textAlign: "left",
                                        lineHeight: 1.8,
                                    }}
                                >
                                    <Trans i18nKey="bulkAnalysisPage.dialogs.retention.description" />
                                </p>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={dontAskRetentionAgain}
                                            onChange={(e) =>
                                                setDontAskRetentionAgain(
                                                    e.target.checked,
                                                )
                                            }
                                            name="dontAskRetentionAgain"
                                            color="primary"
                                        />
                                    }
                                    label={t(
                                        "bulkAnalysisPage.dialogs.retention.dontAskAgain",
                                    )}
                                    sx={{
                                        mt: 2,
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                />
                            </div>
                            <div className="confirmation-dialog-actions">
                                <Button
                                    onClick={() =>
                                        setIsRetentionDialogOpen(false)
                                    }
                                    variant="outlined"
                                >
                                    {t(
                                        "bulkAnalysisPage.dialogs.common.cancel",
                                    )}
                                </Button>
                                <Button
                                    onClick={() => {
                                        if (retentionDialogCallback) {
                                            retentionDialogCallback(); // Execute the stored file open action
                                        }
                                        setIsRetentionDialogOpen(false); // Close the dialog
                                    }}
                                    color="primary"
                                    variant="contained"
                                >
                                    {t(
                                        "bulkAnalysisPage.dialogs.retention.confirmButton",
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>,
                document.body,
            )}
        </Box>
    );
};

export default InteractiveBatchTable;
